/* ═══════════════════════════════════════════════════════════════════════════
   Chase N Chew — storefront behaviour
   Mirrors the app's interactions: 3.5s hero autoplay (paused while scrolling),
   snap rails, gallery thumbs, qty stepper, wishlist heart, copy-code tickets.
   Vanilla JS, no dependencies. Every widget is opt-in via a data- attribute.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* Every section emits its own <script src="cnc-app.js">, and duplicate
     script tags with the same URL each execute. Without a guard, boot() runs
     once per section on the page: the hero ends up with several autoplay
     intervals fighting over scrollLeft, which reads as a stuttering carousel
     and can lock up the renderer. `once` stamps an element so each widget is
     only ever wired up a single time, no matter how often boot() runs. */
  /* The stamp is namespaced, and that namespace is load-bearing.

     It used to write el.dataset[key] directly, so once(html, "cncRailAdd") set
     data-cnc-rail-add="1" on <html> — the very attribute the rail's delegated
     handler matches on. e.target.closest("[data-cnc-rail-add]") then matched
     <html> for EVERY click anywhere on the page, and the handler's
     btn.textContent = "Adding…" replaced the entire document with that text,
     then with "Try again". once(html, "cncTotals") collided with
     data-cnc-totals the same way and left <html> looking like the totals
     container.

     Any guard key that happens to match a marker attribute turns a delegated
     listener into a page-wide one, so the two namespaces must not overlap.
     data-cnc-once-* is reserved for this and used for nothing else. */
  function once(el, key) {
    if (!el) return false;
    var k = "cncOnce" + key.charAt(0).toUpperCase() + key.slice(1);
    if (el.dataset[k] === "1") return false;
    el.dataset[k] = "1";
    return true;
  }

  /* ── The discount percentage, computed in exactly one place ──────────────
     THE RULE: round UP. ₹499 off ₹600 is 16.83%, and every surface prints
     "17% OFF". Ceiling, not floor and not round-half — decided 2026-08-22
     after the PDP shipped both numbers at once: Liquid rounded the badge on
     the photo to 17 while this file floored the price line to 16, six pixels
     apart on the same screen. Which rule wins matters less than there being
     only one.

     Mirrored in Liquid as `mrp | minus: price | times: 100.0 | divided_by: mrp
     | ceil` — same arithmetic, same answer, on the card, the PDP and the cart
     line. Change one and you must change the other, or the PDP splits again.

     Subtraction first, and that is not a style choice. The old form,
     (1 - price / mrp) * 100, carries binary float dust: ₹700 off ₹1000 goes
     through 0.7 and comes out 30.000000000000004, and a ceiling prints
     "31% OFF" on a flat 30% product. ((mrp - price) * 100) / mrp divides two
     exact integers (paise, far under 2^53), so a whole-number discount stays
     whole. */
  function discPct(price, mrp) {
    price = Number(price) || 0;
    mrp   = Number(mrp) || 0;
    if (!(mrp > price)) return 0;
    return Math.ceil(((mrp - price) * 100) / mrp);
  }

  /* ── Hero carousel ──────────────────────────────────────────────────────
     The app advances every 3.5s but never while the user is scrolling the
     page or once the hero is off screen — same rules here. */
  function initHero(root) {
    if (!once(root, "cncHero")) return;
    var track = root.querySelector("[data-hero-track]");
    var dots  = Array.prototype.slice.call(root.querySelectorAll("[data-hero-dot]"));
    if (!track) return;
    /* A swipe must not count as a tap.

       Now that the whole slide is a link, a touch drag to the next slide ends
       with a click event on the anchor under the finger — so browsing the
       carousel on a phone would navigate away instead. Registered before the
       single-slide bail below, and in the CAPTURE phase so it runs before the
       browser follows the href. 10px is the usual slop for "held still". */
    var downX = 0, downY = 0, dragged = false;
    track.addEventListener("pointerdown", function (e) {
      downX = e.clientX; downY = e.clientY; dragged = false;
    }, { passive: true });
    track.addEventListener("pointermove", function (e) {
      if (Math.abs(e.clientX - downX) > 10 || Math.abs(e.clientY - downY) > 10) dragged = true;
    }, { passive: true });
    track.addEventListener("click", function (e) {
      if (dragged && e.target.closest(".cnc-hero-hit")) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    var slides = track.children.length;
    if (slides < 2) { if (dots.length) dots.forEach(function (d) { d.hidden = true; }); return; }

    var index = 0, timer = null, visible = true, busy = false, busyTimer;

    function paint() {
      dots.forEach(function (d, i) { d.setAttribute("aria-current", String(i === index)); });
    }
    function goTo(i, smooth) {
      index = (i + slides) % slides;
      track.scrollTo({ left: index * track.clientWidth, behavior: smooth && !reduceMotion ? "smooth" : "auto" });
      paint();
    }
    function start() {
      if (timer || reduceMotion) return;
      timer = setInterval(function () {
        if (!visible || busy) return;
        goTo(index + 1, true);
      }, 3500);
    }
    function stop() { clearInterval(timer); timer = null; }

    track.addEventListener("scroll", function () {
      busy = true;
      clearTimeout(busyTimer);
      busyTimer = setTimeout(function () {
        busy = false;
        var i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== index) { index = i; paint(); }
      }, 140);
    }, { passive: true });

    dots.forEach(function (d, i) { d.addEventListener("click", function () { goTo(i, true); }); });

    // Prev / next arrows. goTo() already wraps at both ends; restarting the
    // timer means a tap never gets followed by an instant auto-advance.
    var hPrev = root.querySelector("[data-hero-prev]");
    var hNext = root.querySelector("[data-hero-next]");
    function nudge(dir) { stop(); goTo(index + dir, true); if (!root.matches(":hover")) start(); }
    if (hPrev) hPrev.addEventListener("click", function () { nudge(-1); });
    if (hNext) hNext.addEventListener("click", function () { nudge(1); });

    root.addEventListener("mouseenter", stop);
    root.addEventListener("mouseleave", start);

    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }, { threshold: 0.25 }).observe(root);
    }

    paint();
    start();
  }

  /* ── Product gallery — thumbs scroll the peek carousel ─────────────────── */
  function initGallery(root) {
    if (!once(root, "cncGallery")) return;
    var track  = root.querySelector("[data-gallery-track]");
    var thumbs = Array.prototype.slice.call(root.querySelectorAll("[data-gallery-thumb]"));
    if (!track) return;

    function select(i) {
      thumbs.forEach(function (t, n) { t.classList.toggle("is-active", n === i); });
      var fig = track.children[i];
      if (!fig) return;
      if (window.innerWidth >= 900) {
        fig.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
      } else {
        track.scrollTo({ left: fig.offsetLeft - track.offsetLeft, behavior: reduceMotion ? "auto" : "smooth" });
      }
    }
    thumbs.forEach(function (t, i) { t.addEventListener("click", function () { select(i); }); });
    if (thumbs[0]) thumbs[0].classList.add("is-active");
  }

  /* ── Quantity stepper ──────────────────────────────────────────────────── */
  function initQty(root) {
    if (!once(root, "cncQty")) return;
    var input = root.querySelector("input");
    if (!input) return;
    root.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-qty-step]");
      if (!btn) return;
      var next = (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyStep, 10);
      input.value = Math.max(1, next);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  /* ── Sold-out buy row → back-in-stock ───────────────────────────────────
     Which FACE the buy bar shows: the stepper + Add to Bag, or the waitlist.

     This lives here, next to apply(), rather than in cnc-pdp.js, because this
     is the one function that learns a variant changed — it is already
     rewriting the price, the MRP, the % off and the Add/Sold Out label from
     the same `match`. A second listener in the other file would be a second
     source of truth for "is this buyable", and the two would drift the first
     time someone edits one of them.

     The DOM work stops here; the network does not belong in cnc-app.js, which
     loads on every CNC page. cnc-pdp.js listens for the event at the bottom
     and owns the panel, the probe and the submit.

     Note the [hidden] toggles are paired with explicit `display:none` rules in
     cnc-pdp.css: a CNC rule that sets `display` outranks the UA sheet's
     [hidden], so a face that is hidden in the markup still paints without
     them. */
  function setBuyFace(form, variant) {
    var notify = form.querySelector('[data-buy-face="notify"]');
    if (!notify) return;                    // merchant turned the waitlist off
    var add  = form.querySelector('[data-buy-face="add"]');
    var want = !(variant && variant.available);

    notify.hidden = !want;
    if (add) add.hidden = want;
    if (!want) return;

    if (variant) {
      notify.setAttribute("data-bis-variant", String(variant.id));
      // "Default Title" is not a name to show a customer — see bis_multi in
      // sections/cnc-product.liquid.
      if (notify.getAttribute("data-bis-multi") === "1") {
        var what = notify.querySelector("[data-bis-what]");
        if (what && variant.title) what.textContent = variant.title;
      }
    }
    document.dispatchEvent(new CustomEvent("cnc:buyface", {
      detail: { root: notify, variantId: variant && variant.id },
    }));
  }

  /* ── Variant picker — swaps the hidden id + price without a page load ──── */
  function initVariants(form) {
    if (!once(form, "cncVariants")) return;
    var data = form.querySelector("[data-variant-json]");
    if (!data) return;
    var variants = JSON.parse(data.textContent);
    var idField  = form.querySelector("[name='id']");
    var priceEl  = form.querySelector("[data-price]");
    var mrpEl    = form.querySelector("[data-mrp]");
    var discEl   = form.querySelector("[data-disc]");
    var addBtn   = form.querySelector("[data-add]");

    function fmt(cents) {
      var n = (cents / 100).toFixed(Number.isInteger(cents / 100) ? 0 : 2);
      // Indian grouping — 1,23,456 (matches utils/format.js formatINR)
      var parts = String(n).split(".");
      var x = parts[0], last3 = x.slice(-3), rest = x.slice(0, -3);
      if (rest) last3 = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
      return "₹" + last3 + (parts[1] ? "." + parts[1] : "");
    }

    function apply() {
      var chosen = Array.prototype.slice
        .call(form.querySelectorAll("[data-option-index]"))
        .filter(function (el) { return el.getAttribute("aria-checked") === "true"; })
        .sort(function (a, b) { return a.dataset.optionIndex - b.dataset.optionIndex; })
        .map(function (el) { return el.dataset.optionValue; });

      var match = variants.find(function (v) {
        return v.options.every(function (o, i) { return !chosen[i] || o === chosen[i]; });
      });
      if (!match) return;

      if (idField) idField.value = match.id;
      if (priceEl) priceEl.textContent = fmt(match.price);
      if (mrpEl) {
        var showMrp = match.compare_at_price && match.compare_at_price > match.price;
        mrpEl.hidden = !showMrp;
        if (showMrp) mrpEl.textContent = fmt(match.compare_at_price);
        if (discEl) {
          discEl.hidden = !showMrp;
          // Through discPct, like every other discount in this theme. This
          // line runs on load as well as on a swatch click (see apply() at the
          // end of this function), so any rule of its own immediately
          // contradicts the number Liquid printed on the photo.
          if (showMrp) discEl.textContent = discPct(match.price, match.compare_at_price) + "% OFF";
        }
      }
      if (addBtn) {
        addBtn.disabled = !match.available;
        addBtn.querySelector("[data-add-label]").textContent = match.available ? "Add to bag" : "Sold out";
      }
      setBuyFace(form, match);
    }

    form.addEventListener("click", function (e) {
      var opt = e.target.closest("[data-option-index]");
      if (!opt) return;
      form.querySelectorAll('[data-option-index="' + opt.dataset.optionIndex + '"]')
        .forEach(function (el) { el.setAttribute("aria-checked", String(el === opt)); });
      apply();
    });
    apply();
  }

  /* ── Wishlist heart ─────────────────────────────────────────────────────
     Writes to the HOST THEME's wishlist store, not a private one.

     This previously used its own "cnc:wishlist" key. The hearts toggled and
     looked fine, but the theme's header counter and wishlist page read
     `glozin__wishlist-items`, so nothing a customer saved from one of these
     sections ever showed up anywhere else in the store — it looked like the
     button was broken. Both are a plain array of product-id strings, so they
     share one key cleanly.

     If you ever move to a different theme, this is the one constant to change. */
  var WISH_KEY = "glozin__wishlist-items";
  var LEGACY_WISH_KEY = "cnc:wishlist";

  /* Sidecar to WISH_KEY: { "<id>": { handle, title, url, img, price, compare } }.

     It exists because WISH_KEY has to remain a bare array of id strings — the
     host theme's header counter and wishlist page both parse it — while an id
     alone can't be rendered. Shopify's storefront AJAX API resolves products by
     handle, never by numeric id, so there is no request that turns
     `["7234567890"]` back into something with a title and a price. The details
     are therefore captured at the moment of hearting, when the card already has
     them in the DOM, and read back with no network call at all.

     Consequence worth knowing: a wishlist saved before this shipped has ids but
     no meta, so those items stay invisible in the rail until re-hearted. That's
     deliberate — the alternative was sweeping /collections/all/products.json to
     backfill, which pulls the whole catalogue to rescue a rail. */
  var WISH_META_KEY = "cnc:wishlist-meta";
  var VIEWED_KEY = "cnc:recently-viewed";
  var VIEWED_MAX = 12;

  function readJson(key, fallback) {
    try { return JSON.parse(localStorage.getItem(key)) || fallback; }
    catch (e) { return fallback; }
  }
  function writeJson(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* quota */ }
  }

  function wishMetaFrom(btn) {
    var d = btn.dataset;
    if (!d.wishTitle) return null;
    return {
      handle: d.wishHandle || "",
      title: d.wishTitle || "",
      url: d.wishUrl || (d.wishHandle ? "/products/" + d.wishHandle : ""),
      img: d.wishImg || "",
      // Liquid prints money fields in paise; an unset compare_at prints "".
      price: parseInt(d.wishPrice, 10) || 0,
      compare: parseInt(d.wishCompare, 10) || 0,
      variant: parseInt(d.wishVariant, 10) || 0,
      nvariants: parseInt(d.wishNvariants, 10) || 1
    };
  }

  /* Backfill for wishlists saved before the meta capture existed — and for
     anything hearted on a page that isn't one of ours, since the host theme's
     own wishlist button writes the id and nothing else.

     Shopify resolves products by handle, never by numeric id, so the only way
     back from a bare id is to walk the catalogue and match. That's a heavy
     thing to do for a rail, hence: only when ids are actually missing, capped
     at 1000 products, and cached in sessionStorage so it happens once per tab
     rather than once per drawer open. If the endpoint is disabled the rail just
     falls back to recently-viewed, which is why nothing here rejects. */
  var CATALOGUE_PAGE = 250;
  var CATALOGUE_MAX_PAGES = 4;

  function backfillWishMeta() {
    var ids = readWish();
    var meta = readJson(WISH_META_KEY, {});
    var missing = ids.filter(function (id) { return !meta[id]; });
    if (!missing.length) return Promise.resolve(meta);

    /* Remember which ids we've already swept for, not merely THAT we swept.
       The host theme's own "add to wishlist before remove?" prompt writes a
       bare product id straight into glozin__wishlist-items, so new unresolved
       ids appear without us doing anything. A single boolean meant the first
       sweep locked the tab and those items never got a title or a price — they
       simply never showed up in the rail. */
    var tried = [];
    try { tried = JSON.parse(sessionStorage.getItem("cnc:wish-swept") || "[]"); }
    catch (e) { tried = []; }
    var fresh = missing.filter(function (id) { return tried.indexOf(String(id)) === -1; });
    if (!fresh.length) return Promise.resolve(meta);

    var want = {};
    missing.forEach(function (id) { want[String(id)] = 1; });

    function page(n, acc) {
      if (n > CATALOGUE_MAX_PAGES) return Promise.resolve(acc);
      return fetch("/collections/all/products.json?limit=" + CATALOGUE_PAGE + "&page=" + n, {
        headers: { Accept: "application/json" }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var list = (data && data.products) || [];
          list.forEach(function (p) {
            if (!want[String(p.id)]) return;
            var v = (p.variants || [])[0] || {};
            acc[String(p.id)] = {
              handle: p.handle,
              title: p.title,
              url: "/products/" + p.handle,
              img: ((p.images || [])[0] || {}).src || "",
              // products.json prints decimal rupee strings, not paise.
              price: Math.round(parseFloat(v.price || 0) * 100),
              compare: Math.round(parseFloat(v.compare_at_price || 0) * 100),
              variant: v.id || 0,
              nvariants: (p.variants || []).length || 1
            };
            delete want[String(p.id)];
          });
          if (!list.length || !Object.keys(want).length) return acc;
          return page(n + 1, acc);
        })
        .catch(function () { return acc; });
    }

    return page(1, meta).then(function (filled) {
      writeJson(WISH_META_KEY, filled);
      try {
        sessionStorage.setItem("cnc:wish-swept",
          JSON.stringify(tried.concat(missing.map(String))));
      } catch (e) { /* private mode */ }
      return filled;
    });
  }

  /* Most-recent-first, deduped by id, capped. Runs on any page carrying a
     [data-cnc-viewed] payload — the CNC product template. */
  function recordViewed(root) {
    if (!once(root, "cncViewed")) return;
    var item;
    try { item = JSON.parse(root.textContent); } catch (e) { return; }
    if (!item || !item.id) return;
    pushViewed(item);
  }

  function pushViewed(item) {
    var list = readJson(VIEWED_KEY, []).filter(function (p) {
      return p && String(p.id) !== String(item.id);
    });
    list.unshift(item);
    writeJson(VIEWED_KEY, list.slice(0, VIEWED_MAX));
  }

  /* ── Recording a view WITHOUT a payload ──────────────────────────────────
     The payload above only exists on the CNC product template. Any product
     page the host theme renders — and on most stores that is still some of
     them, because only `templates/product.json` was replaced and a product
     with its own template suffix keeps the theme's — recorded nothing at all.
     The symptom is a Recently viewed rail that stays empty no matter how much
     the customer browses, which reads as a broken section rather than as a
     template that was never swapped.

     This file is enqueued by the header, so it is on EVERY page of the store.
     That makes the URL a reliable second source: if the path names a product
     and no payload is present, resolve the handle and record it.

     One fetch, only on product URLs, only when the template didn't already do
     the job, and skipped entirely when this product is already the most recent
     entry — so a reload or a variant switch costs nothing. */
  function recordViewedFromUrl() {
    if (!once(document.documentElement, "cncViewedUrl")) return;
    if (document.querySelector("[data-cnc-viewed]")) return;

    // Matches /products/x and /collections/y/products/x alike.
    var m = /\/products\/([^/?#]+)/.exec(location.pathname || "");
    if (!m) return;
    var handle = m[1];

    var seen = readJson(VIEWED_KEY, []);
    if (seen.length && String(seen[0] && seen[0].handle) === handle) return;

    fetchProduct(handle).then(function (p) {
      if (!p || !p.id) return;
      var vs = p.variants || [];
      var v = vs.filter(function (x) { return x.available; })[0] || vs[0] || {};
      pushViewed({
        id: p.id,
        handle: p.handle,
        title: p.title || "",
        url: "/products/" + p.handle,
        /* products.js hands back the ORIGINAL, with no width parameter — which
           is what the card renderer wants anyway, since it only rewrites a
           width that is already there and leaves an original alone. */
        img: (p.images || [])[0] || "",
        price: v.price || 0,
        compare: v.compare_at_price || 0,
        variant: v.id || 0,
        nvariants: vs.length || 1
      });
    });
  }

  function readWish() {
    try { return JSON.parse(localStorage.getItem(WISH_KEY)) || []; } catch (e) { return []; }
  }

  function writeWish(list) {
    var json = JSON.stringify(list);
    localStorage.setItem(WISH_KEY, json);

    // Repaint every heart on the page for this id, so the same product in two
    // different rails stays consistent without a reload.
    document.querySelectorAll("[data-wish]").forEach(function (b) {
      b.setAttribute("aria-pressed", String(list.indexOf(b.dataset.wish) > -1));
    });

    // Nudge the theme's own header counter. It reads the key on load, so it
    // needs telling; a synthetic StorageEvent covers themes listening for
    // cross-tab changes, and the direct text update covers the rest.
    document.querySelectorAll(".wishlist-count").forEach(function (el) {
      el.textContent = list.length;
    });
    try {
      window.dispatchEvent(new StorageEvent("storage", {
        key: WISH_KEY, newValue: json, storageArea: localStorage
      }));
    } catch (e) { /* older browsers — the direct update above still ran */ }
    document.dispatchEvent(new CustomEvent("cnc:wishlist-updated", { detail: { items: list } }));
  }

  // One-time migration so anything saved under the old key isn't silently lost.
  function migrateWishlist() {
    var legacy;
    try { legacy = JSON.parse(localStorage.getItem(LEGACY_WISH_KEY)); } catch (e) { return; }
    if (!Array.isArray(legacy) || !legacy.length) return;
    var merged = readWish();
    legacy.forEach(function (id) { if (merged.indexOf(id) === -1) merged.push(id); });
    localStorage.setItem(WISH_KEY, JSON.stringify(merged));
    localStorage.removeItem(LEGACY_WISH_KEY);
  }

  function initWishlist() {
    migrateWishlist();
    var wished = readWish();
    document.querySelectorAll("[data-wish]").forEach(function (btn) {
      var id = btn.dataset.wish;
      // Re-sync the pressed state on every boot, but only bind the click once.
      btn.setAttribute("aria-pressed", String(wished.indexOf(id) > -1));
      if (!once(btn, "cncWish")) return;
      btn.addEventListener("click", function (e) {
        e.preventDefault(); e.stopPropagation();
        var list = readWish();
        var at = list.indexOf(id);
        var meta = readJson(WISH_META_KEY, {});
        if (at > -1) {
          list.splice(at, 1);
          delete meta[id];
        } else {
          list.push(id);
          // Re-read on every add rather than caching: the same product can be
          // hearted from a rail and a PDP, and the PDP carries the better image.
          var info = wishMetaFrom(btn);
          if (info) meta[id] = info;
        }
        writeJson(WISH_META_KEY, meta);
        writeWish(list);
        btn.animate(
          [{ transform: "scale(1)" }, { transform: "scale(1.5)" }, { transform: "scale(1)" }],
          { duration: reduceMotion ? 0 : 320, easing: "ease-out" }
        );
      });
    });
  }

  /* ── Carousel ───────────────────────────────────────────────────────────
     Shared by the product rails, Instagram and the coupon bar.

     Scrolls by a whole page rather than one item, which keeps the rhythm
     predictable. `loop` wraps around at either end instead of dead-ending —
     the old rails simply stopped, leaving the last few products unreachable
     whenever grid mode was off. Autoplay pauses on hover and focus, and stops
     for good the moment someone interacts, so it never fights the customer. */
  function initCarousel(root) {
    if (!once(root, "cncCarousel")) return;
    var track = root.querySelector("[data-carousel-track]");
    if (!track) return;

    var prev  = root.querySelector("[data-carousel-prev]");
    var next  = root.querySelector("[data-carousel-next]");
    var dotsW = root.querySelector("[data-carousel-dots]");
    var loop  = root.dataset.loop === "true";
    var every = parseInt(root.dataset.autoplay, 10) || 0;   // seconds, 0 = off
    var timer = null, userEngaged = false;

    function maxScroll() { return track.scrollWidth - track.clientWidth; }
    function overflowing() { return maxScroll() > 2; }
    // The visible page, minus the track's side padding (the mobile rails bleed
    // to the screen edge and pad back in, so clientWidth over-counts).
    function page() {
      var cs = window.getComputedStyle(track);
      var w = track.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      return w > 0 ? w : track.clientWidth;
    }

    function go(dir) {
      var at = track.scrollLeft;
      var max = maxScroll();
      var target = at + dir * page();
      if (loop) {
        if (dir > 0 && at >= max - 2) target = 0;
        else if (dir < 0 && at <= 2) target = max;
      }
      track.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: reduceMotion ? "auto" : "smooth" });
    }

    function paint() {
      var on = overflowing();
      if (prev) {
        prev.hidden = !on;
        prev.disabled = !on || (!loop && track.scrollLeft <= 2);
      }
      if (next) {
        next.hidden = !on;
        next.disabled = !on || (!loop && track.scrollLeft >= maxScroll() - 2);
      }
      if (dotsW) {
        var pages = on ? Math.round(maxScroll() / page()) + 1 : 0;
        if (dotsW.children.length !== pages) {
          dotsW.innerHTML = "";
          for (var i = 0; i < pages; i++) {
            var d = document.createElement("button");
            d.type = "button";
            d.className = "cnc-cdot";
            d.setAttribute("aria-label", "Go to page " + (i + 1));
            (function (n) {
              d.addEventListener("click", function () {
                stop();
                var to = n >= dotsW.children.length - 1 ? maxScroll() : n * page();
                track.scrollTo({ left: to, behavior: reduceMotion ? "auto" : "smooth" });
              });
            })(i);
            dotsW.appendChild(d);
          }
        }
        var cur = track.scrollLeft >= maxScroll() - 2
          ? dotsW.children.length - 1
          : Math.round(track.scrollLeft / page());
        Array.prototype.forEach.call(dotsW.children, function (d, i) {
          d.setAttribute("aria-current", String(i === cur));
        });
      }
    }

    function start() {
      if (timer || !every || reduceMotion || userEngaged) return;
      timer = setInterval(function () { if (overflowing()) go(1); }, every * 1000);
    }
    function stop() { clearInterval(timer); timer = null; }
    function engage() { userEngaged = true; stop(); }

    if (prev) prev.addEventListener("click", function () { engage(); go(-1); });
    if (next) next.addEventListener("click", function () { engage(); go(1); });

    track.addEventListener("scroll", function () {
      window.requestAnimationFrame(paint);
    }, { passive: true });
    track.addEventListener("pointerdown", engage, { passive: true });

    root.addEventListener("mouseenter", stop);
    root.addEventListener("mouseleave", start);
    root.addEventListener("focusin", stop);

    window.addEventListener("resize", function () { window.requestAnimationFrame(paint); });

    // Images arrive after first paint and change scrollWidth, so the arrows
    // have to be re-evaluated once the track has settled.
    if ("ResizeObserver" in window) new ResizeObserver(paint).observe(track);

    paint();
    start();
  }

  /* ── Live coupons ───────────────────────────────────────────────────────
     Same endpoint the app uses (src/api/coupons.js), so the website and the
     app can never drift apart or show a code the other doesn't have. Liquid
     can't read Shopify discount rules, which is why this is a client fetch.

     On any failure the server-rendered blocks stay exactly as they are — a
     coupon bar that quietly shows stale codes is worse than one that doesn't
     change, so failures are logged rather than swallowed. */
  var COUPON_TIMEOUT_MS = 12000;

  function couponBenefit(c) {
    if (c.pct) return c.pct + "% OFF";
    if (c.flat) return "₹" + c.flat + " OFF";
    if (c.bundlePrice) return "₹" + c.bundlePrice;
    return c.label || "Offer";
  }
  function couponCondition(c) {
    if (c.minOrder) return "On orders above ₹" + c.minOrder;
    if (c.minQty) return "On " + c.minQty + "+ items in the cart";
    if (c.bundleQty) return "Buy any " + c.bundleQty + " from " + (c.collection || "the collection");
    return "No minimum order";
  }

  function initLiveCoupons(root) {
    if (!once(root, "cncCoupons")) return;
    var endpoint = root.dataset.couponsEndpoint;
    var track = root.querySelector("[data-carousel-track]");
    if (!endpoint || !track || !window.fetch) return;

    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, COUPON_TIMEOUT_MS);

    fetch(endpoint, { signal: controller.signal })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!Array.isArray(data.coupons) || !data.coupons.length) {
          throw new Error("no coupons array in response");
        }
        // Auto-applied bundles first, then the biggest saving — the order a
        // customer benefits from reading.
        var list = data.coupons.slice().sort(function (a, b) {
          if ((a.type === "auto") !== (b.type === "auto")) return a.type === "auto" ? -1 : 1;
          return (b.pct || 0) - (a.pct || 0) || (b.flat || 0) - (a.flat || 0);
        });

        track.innerHTML = list.map(function (c) {
          var code = c.code ? String(c.code) : "";
          return '<div class="cnc-ticket">'
            + '<span class="cnc-ticket-ic">' + (root.dataset.tagIcon || "") + '</span>'
            + '<div style="min-width:0"><div class="cnc-ticket-benefit">' + esc(couponBenefit(c)) + '</div>'
            + '<div class="cnc-ticket-cond">' + esc(couponCondition(c)) + '</div></div>'
            + (code
                ? '<button type="button" class="cnc-ticket-code" data-copy="' + esc(code) + '"'
                  + ' aria-label="Copy code ' + esc(code) + '">' + esc(code) + '</button>'
                : '<span class="cnc-ticket-code" style="border-style:dashed">AUTO</span>')
            + '</div>';
        }).join("");

        initCopy();
        root.dispatchEvent(new CustomEvent("cnc:coupons-loaded"));
      })
      .catch(function (e) {
        var why = e && e.name === "AbortError"
          ? "timed out after " + (COUPON_TIMEOUT_MS / 1000) + "s"
          : (e && e.message) || "network error";
        console.warn("[cnc-coupons] live fetch failed (" + why + ") — keeping the coupons set in the theme editor");
      })
      .then(function () { clearTimeout(timer); });
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  /* ── Copy coupon code ──────────────────────────────────────────────────── */
  function initCopy() {
    document.querySelectorAll("[data-copy]").forEach(function (btn) {
      if (!once(btn, "cncCopy")) return;
      btn.addEventListener("click", function () {
        var code = btn.dataset.copy;
        var done = function () {
          var was = btn.textContent;
          btn.textContent = "Copied!";
          btn.classList.add("copied");
          setTimeout(function () { btn.textContent = was; btn.classList.remove("copied"); }, 2000);
        };
        if (navigator.clipboard) navigator.clipboard.writeText(code).then(done, done);
        else done();
      });
    });
  }

  /* ── Collection filters — pure URL params, works without JS too ────────── */
  function initFilters(root) {
    if (!once(root, "cncFilters")) return;
    root.addEventListener("change", function (e) {
      if (!e.target.matches("[data-filter]")) return;
      root.closest("form").submit();
    });
  }

  /* ── Cart drawer ────────────────────────────────────────────────────────
     Adding to cart opens the HOST THEME's minicart rather than navigating to
     /cart, and rather than this file growing a second cart UI of its own. A
     duplicate drawer would have to re-implement totals, discount codes,
     free-shipping progress and upsells, and would drift out of step with the
     real cart the moment any of them changed.

     The open state was read off the running theme by clicking its own cart
     icon and diffing the DOM, not guessed. It takes THREE markers, and the
     panel stays translated off-screen if any one is missing:
       <html>              open-drawer open-minicart
       .minicart           open
       .minicart__wrapper  open
     If a future theme changes this, the constants below are the only edit. */
  var DRAWER_HTML_CLASSES = ["open-drawer", "open-minicart"];
  var DRAWER_ROOT_SEL = ".minicart";
  var DRAWER_PANEL_SEL = ".minicart__wrapper";
  var CART_COUNT_SEL = ".cart-count";

  /* The custom element that OWNS the drawer. Deliberately not ".minicart":
     the theme puts that class on two nodes — a wrapper around the header cart
     icon and this one — and document.querySelector returns the header's first. */
  var DRAWER_HOST_SEL = "cart-notification.minicart";

  function openCartDrawer() {
    var panel = document.querySelector(DRAWER_PANEL_SEL);
    var root = document.querySelector(DRAWER_ROOT_SEL);
    if (!panel || !root) return false;
    document.documentElement.classList.add(DRAWER_HTML_CLASSES[0], DRAWER_HTML_CLASSES[1]);
    root.classList.add("open");
    panel.classList.add("open");
    return true;
  }

  function closeCartDrawer() {
    document.documentElement.classList.remove(DRAWER_HTML_CLASSES[0], DRAWER_HTML_CLASSES[1]);
    var root = document.querySelector(DRAWER_ROOT_SEL);
    var panel = document.querySelector(DRAWER_PANEL_SEL);
    if (root) root.classList.remove("open");
    if (panel) panel.classList.remove("open");

    /* The sub-drawer is a fixed overlay on <body>, so it does not close with
       the cart it belongs to. Its own X carries data-cnc-close, which used to
       shut the cart and leave the sheet sitting over a closed drawer, covering
       the page — and because initDrawerClose bails when `open-minicart` is
       gone, nothing could dismiss it. That's the "stuck" state. Closing the
       stack together is the only correct behaviour: the sheet has no meaning
       without the cart behind it. */
    closeCouponSheet();
  }

  /* The only supported way for another CNC file to touch the drawer.

     cnc-header.js needs to open the minicart from the bag icon, and the two
     files are loaded independently — the header is on every page, this one
     only where a CNC section renders. Re-implementing openCartDrawer there
     would mean two copies of DRAWER_PANEL_SEL and DRAWER_HTML_CLASSES, and
     the next theme upgrade would silently fix one and break the other.

     Returns false when there is no drawer on the page, which is the signal
     for the caller to let its href="/cart" through instead. */
  window.CNC = window.CNC || {};
  window.CNC.openCart = openCartDrawer;
  window.CNC.closeCart = closeCartDrawer;

  /* The discount rule, exported for the same reason as everything else here:
     cnc-wishlist.js paints price rows too, and a second copy of the arithmetic
     is a second rule waiting to drift. Safe to reach for — every section that
     loads cnc-wishlist.js or cnc-pdp.js loads cnc-app.js on the line above it,
     and `defer` keeps document order. */
  window.CNC.discPct = discPct;

  /* Is there a drawer on this page at all? Callers need to know BEFORE they
     call preventDefault on a click, so they can let an href="/cart" through
     instead of swallowing the click and doing nothing. Exported rather than
     re-tested by the caller so DRAWER_PANEL_SEL stays in one file. */
  window.CNC.hasCart = function () {
    return !!(document.querySelector(DRAWER_PANEL_SEL) && document.querySelector(DRAWER_ROOT_SEL));
  };

  /* ── Exports for sections that render their own cards ────────────────────
     The wishlist page and the recently-viewed rail paint cards from
     localStorage, which means they arrive after this file has already booted.
     Three things they cannot sensibly reimplement:

       openPicker  the one variant sheet, so a second surface doesn't grow a
                   second picker — the mistake this release just undid.
       addToCart   POST /cart/add.js AND re-render the drawer the theme's way.
                   The two-step dance (documented endpoints, section render)
                   is subtle enough that a copy of it would drift.
       rescan      re-run the widget binders over markup that did not exist at
                   boot. bootWidgets is idempotent — every binder is guarded by
                   `once` on the element it binds — so calling it again costs a
                   querySelectorAll and binds only what is genuinely new.

     rescan is deliberately NOT boot(): boot coalesces calls inside 50ms to
     absorb a burst of shopify:section:load events, and a caller that has just
     written new DOM needs it bound now, not maybe. */
  window.CNC.openPicker = function (opts) { return pkOpen(opts); };
  window.CNC.addToCart = function (variantId, qty) { return themeAdd(variantId, qty); };
  window.CNC.rescan = function () {
    try { bootWidgets(); } catch (err) { console.error("[cnc] rescan failed", err); }
  };

  /* ── Exports for the /cart page ──────────────────────────────────────────
     The cart page (cnc-cart.js) renders the same three mount points the
     drawer injects — [data-cnc-offers], [data-cnc-totals], [data-cnc-rail] —
     and needs two things this file already owns.

     paintBlocks   fill those hooks with the coupon row, the points line, the
                   breakdown and the rail. The page must NOT reimplement any
                   of them: the coupon row it renders carries
                   data-cnc-coupon-open, which the sheet is already listening
                   for on document, and a second copy of the row would be a
                   second definition of what "applied" looks like.

     onCartChange  a way to hear that the cart moved. The coupon sheet ends
                   every apply and every remove in refreshCartDrawer(), which
                   updates the drawer and the header count and knows nothing
                   about any page. Rather than teach it, it announces. */
  window.CNC.paintBlocks = function (scope) {
    var root = scope || document;
    return paintBlocks(
      root.querySelector("[data-cnc-offers]"),
      root.querySelector("[data-cnc-rail]"),
      root.querySelector("[data-cnc-totals]"),
      root
    );
  };

  /* Re-fetch the page and re-sync the drawer + header count. Exported because
     the /cart page edits quantities without going through the theme's own
     stepper, which leaves the drawer holding the quantities it last rendered.
     Line KEYS survive a quantity change, so reconcileLineKeys cannot see the
     drift — only a real refresh can. Callers debounce it; it is a page fetch. */
  window.CNC.refreshCart = refreshCartDrawer;

  /* Exported so the cart page cannot re-learn the /cart Accept-header trap the
     hard way. See fetchSections() for what that trap costs. */
  window.CNC.fetchSections = fetchSections;

  /* ── Exports for the product page's "Best offers" card ───────────────────
     cnc-pdp.js ranks the live coupons against the product on screen and puts
     the winner on the cart after Add to Bag. It must not grow its own copy of
     any of these: postDiscounts() is the ONE place allowed to post `discount`
     (it replaces the whole set — see the note above it), fetchCart() is the
     rate-limit-safe reader, and fetchCoupons() is the shared, cached list.

     applyCoupon resolves { ok, cart, reason } exactly like applyDiscount, and
     on success re-syncs the drawer and the /cart page the same way the coupon
     sheet does, so the drawer's announceCoupon() fires the toast + confetti. */
  window.CNC.coupons = function () { return fetchCoupons(); };
  window.CNC.fetchCart = function (force) { return fetchCart(force); };
  window.CNC.appliedCode = function (cart) { return appliedCode(cart); };
  window.CNC.applyCoupon = function (code) {
    return applyDiscount(code).then(function (res) {
      if (res && res.ok) window.__cncPendingCoupon = null;
      if (res && res.ok) {
        emitCartChange();
        refreshCartDrawer().catch(function (e) {
          console.warn("[cnc-cart] background drawer sync failed", e);
        });
      }
      return res;
    });
  };

  /* ONE REGISTRY, ON WINDOW, SHARED BY EVERY COPY OF THIS FILE.

     This is the whole bug, and it is worth spelling out because the same trap
     is waiting for the next piece of cross-file state anyone adds here.

     Every CNC section emits <script src="cnc-app.js">, so a page carrying
     three of them EXECUTES THIS IIFE THREE TIMES. The once() guards make the
     DOM binding idempotent, but they do not make the closures identical:
     each run built its own `cartListeners = []` and overwrote
     window.CNC.onCartChange with a function closing over its own array.

     Measured on the live cart page. Script order was
     app(1), header, app(2), cart, app(3) — so cnc-cart.js registered its
     listener into copy 2's array, while the coupon sheet's handlers were
     bound by copy 1 (first to run, so first past `once`). Copy 1's
     emitCartChange() therefore announced to an empty array. Removing a coupon
     posted to /cart/update.js, refreshed the drawer, and left the page
     serenely displaying the coupon it had just removed — no ?sections=
     request was ever made.

     SHARED, a few hundred lines up, already solves exactly this for the cart
     cache and in exactly this way. Anything that must be the same object
     across copies goes on window, not in this closure. */
  var cartListeners = (window.__cncCartListeners = window.__cncCartListeners || []);
  window.CNC.onCartChange = function (fn) {
    if (typeof fn === "function" && cartListeners.indexOf(fn) === -1) cartListeners.push(fn);
  };

  /* One listener throwing must not stop the others, and must not stop the
     drawer refresh that called this — the same rule as everywhere else in
     this file: decoration may never fail a purchase. */
  function emitCartChange() {
    for (var i = 0; i < cartListeners.length; i++) {
      try { cartListeners[i](); }
      catch (err) { console.error("[cnc-cart] cart listener failed", err); }
    }
  }

  /* Safety net. Re-rendering the panel's contents can drop click handlers the
     theme bound to individual nodes, and a drawer that won't close is a trap.
     This is delegated on document, so it survives any amount of re-rendering. */
  function initDrawerClose() {
    if (!once(document.documentElement, "cncDrawerClose")) return;
    document.addEventListener("click", function (e) {
      /* THE COUPON SHEET OWNS THE OUTSIDE CLICK WHENEVER IT IS OPEN.

         This branch is above the open-minicart guard on purpose, and it
         fixes two separate things.

         On the CART PAGE the sheet opens from the page's own coupon row, so
         no drawer is open, so this handler used to return on the first line
         and nothing was listening for a click outside the sheet at all — it
         could only be dismissed with Escape or the Back arrow. Measured on
         the live theme: sheet open, click at the left edge of the page,
         still open.

         With the drawer open it is a behaviour fix rather than a dead end.
         Falling through closed the WHOLE stack, when the sheet is something
         you navigated into from the cart — so dismissing it should return
         you to the cart, exactly like the Back arrow does. Hence the early
         return: while the sheet is up, no click can reach the drawer's own
         outside-click logic.

         The opener is excluded because that click is what opened the sheet;
         without it the sheet closes on the same event that mounted it. */
      var sheet = document.querySelector(SHEET_SEL);
      if (sheet && sheet.classList.contains("is-open")) {
        if (!e.target.closest(SHEET_SEL) && !e.target.closest("[data-cnc-coupon-open]")) {
          closeCouponSheet();
        }
        return;
      }

      if (!document.documentElement.classList.contains("open-minicart")) return;
      var panel = document.querySelector(DRAWER_PANEL_SEL);
      if (e.target.closest("[data-cnc-close], .minicart__close, .close-minicart, .bls__drawer-overlay")) {
        closeCartDrawer();
      } else if (
        panel &&
        !e.target.closest(DRAWER_PANEL_SEL) &&
        !e.target.closest(".cnc-card-addform") &&
        // The coupon sub-drawer lives outside the panel (see ensureCouponSheet),
        // so without this a tap on a coupon counted as "clicked outside" and
        // shut the whole cart the instant you tried to use it.
        !e.target.closest(SHEET_SEL) &&
        /* Anything that OPENS the drawer is not an outside click.
           The header's bag icon opens the drawer during its own click, and
           that same click then bubbles to here — where the drawer is already
           marked open and the bag is, technically, outside the panel. So this
           handler closed it on the very event that opened it, and the drawer
           appeared not to work at all. Adds to cart escaped this because they
           open the drawer after a fetch, in a later task. */
        !e.target.closest("[data-cnc-hd-cart], [data-cnc-open-cart]")
      ) {
        closeCartDrawer();       // click outside the panel
      }
    });
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      closeCouponSheet();
      closeCartDrawer();         // Escape drops the whole stack, back only the sheet
    });
  }

  /* The minicart is rendered by a Liquid snippet, not a section, so Shopify's
     Section Rendering API can't return it on its own. Re-fetching the current
     page and swapping the panel keeps every total and progress bar correct
     without this file knowing how any of them are calculated. */
  function refreshCartDrawer() {
    return fetch(location.pathname + location.search, {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var wasOpen = document.documentElement.classList.contains(DRAWER_HTML_CLASSES[1]);

        /* WE DO NOT TOUCH THE DRAWER'S OWN MARKUP. EVER.

           This used to rewrite .minicart__wrapper.innerHTML, and that single
           line broke the theme's quantity stepper and remove button:
           <cart-notification> caches child references in its constructor, the
           constructor runs once, and rewriting the contents left it holding
           pointers to detached nodes (measured — cartCountDown.isConnected went
           false). Dropping an item to zero fired /cart/change and then changed
           nothing on screen, and the "move to wishlist?" prompt never appeared.

           Replacing the whole host was tried and measured too: also broken,
           because the constructor registers a body-level click listener that is
           never torn down, so the old instance keeps intercepting.

           Adds now go through the theme's own <product-form>, which renders the
           drawer itself. All this function still owns is the header count and
           our injected blocks — observeDrawer() repaints those whenever the
           theme re-renders. */

        /* innerHTML, not textContent. The header badge is not a bare number —
           it holds the digit plus a visually-hidden "4 items" label as separate
           child nodes. Assigning textContent flattened both into one text node,
           so the badge rendered "4 4 items" crammed into a 16px circle and the
           cart icon looked corrupted after every update. Copying the markup
           keeps the structure, and the screen-reader label with it. */
        var freshCounts = doc.querySelectorAll(CART_COUNT_SEL);
        document.querySelectorAll(CART_COUNT_SEL).forEach(function (el, i) {
          if (freshCounts[i]) el.innerHTML = freshCounts[i].innerHTML;
        });

        // Repaint our own blocks. Wrapped, because decoration must never be
        // able to fail a purchase: an exception raised while drawing a coupon
        // row once surfaced to the customer as "Try again" on a product that
        // was already in their cart. Log it, keep the cart correct, carry on.
        try {
          enhanceDrawer();
        } catch (err) {
          console.error("[cnc-cart] drawer enhancement failed — cart is fine", err);
        }

        /* Every coupon apply and every coupon remove funnels through here, so
           this is the one place that knows the cart has moved for a reason
           the caller did not initiate. The /cart page listens; nothing else
           does yet, and when nothing is listening this is a no-op. */
        emitCartChange();
      });
  }

  /* The only place we still post to the cart ourselves. Adds from cards, the
     product page and the rail all go through the theme's <product-form> so it
     can render its own drawer; this exists for Buy Now, which navigates away
     and so has no drawer to keep in sync. */
  function postAdd(body) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: body
    }).then(function (r) {
      return cartJson(r, "Could not add to cart").then(function (data) {
        invalidateCart();
        return data;
      });
    });
  }

  function routesCartUrl() {
    var link = document.querySelector('a[href$="/cart"]');
    return link ? link.getAttribute("href") : "/cart";
  }

  /* ── Watching the theme render ──────────────────────────────────────────
     The theme owns the drawer's markup and re-renders it after every add,
     quantity change and removal. We own three blocks inside it, and they're
     wiped by each of those renders.

     A MutationObserver is the right instrument precisely because it depends on
     nothing: no event name, no theme method, no ordering assumption. Whenever
     the panel's contents change, for whatever reason, our blocks go back in.

     The observer is disconnected while we work, or injecting them would trip it
     and loop. */
  var drawerObserver = null;

  function observeDrawer() {
    if (!window.MutationObserver) return;
    var panel = document.querySelector(DRAWER_PANEL_SEL);
    if (!panel || !once(panel, "cncObserved")) return;

    drawerObserver = new MutationObserver(function () {
      // Cheap guard: only react when the theme has actually replaced content,
      // which always removes our blocks.
      if (panel.querySelector("[data-cnc-offers]")) return;
      try {
        enhanceDrawer();
      } catch (err) {
        console.error("[cnc-cart] re-enhance after theme render failed", err);
      }
    });
    drawerObserver.observe(panel, { childList: true, subtree: true });
  }

  /* ── The host theme's quick view → the Pick & Pack sheet ────────────────
     The drawer's "You may also like" row (and any host product card) opens
     Glozin's own quick view on "Select Options": a second variant picker with
     a red "Buy it now", red prices, a floored discount badge that disagreed
     with the card it came from (−17% under an "18% OFF" card), a Brown
     swatch painted red, "Add to Cart" where every other button says "Add to
     bag", and the whole product description stacked above the options.

     One picker, store-wide, is already the rule (see openPicker above), so
     the host trigger is taken over in the CAPTURE phase — before the theme's
     <select-option> handler — and routed to the CNC sheet. The handle is read
     off the trigger's data-url (/products/<handle>?section_id=product-
     quickview). If it can't be read, the click falls through to the theme:
     a picker we can't open is worse than the one we were replacing. */
  function hostQuickHandle(trigger) {
    var url = trigger.getAttribute("data-url") || trigger.getAttribute("href") || "";
    var m = url.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : "";
  }
  function initHostQuickView() {
    if (!once(document.documentElement, "cncHostQuick")) return;
    document.addEventListener("click", function (e) {
      var t = e.target.closest && e.target.closest(
        "select-option[data-url], .product-item__select-options[data-url], [data-item='overlay-quickbuy'][data-url]"
      );
      if (!t || t.closest(".cnc")) return;
      var handle = hostQuickHandle(t);
      if (!handle) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      pkOpen({ handle: handle, url: "/products/" + handle, mode: "add", opener: t });
    }, true);
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Enter" && e.key !== " ") return;
      var t = e.target.closest && e.target.closest("select-option[data-url]");
      if (!t || t.closest(".cnc")) return;
      var handle = hostQuickHandle(t);
      if (!handle) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      pkOpen({ handle: handle, url: "/products/" + handle, mode: "add", opener: t });
    }, true);
  }

  /* The upsell row arrives AFTER the drawer paints (the theme fetches it from
     /recommendations), so enhanceDrawer() never sees it. Its buttons carry the
     host locale's "Add to Cart" / "Select Options"; everywhere else in the
     store says "Add to bag" / "Choose options". Relabelled in place — the
     node is the theme's, only its text changes, so <product-form> keeps
     working. Marked done so the observer's own mutation doesn't loop. */
  function tuneUpsell(root) {
    (root || document).querySelectorAll(
      ".cart-recommend .product-item__button-submit .btn-action:not([data-cnc-lbl])," +
      ".cart-recommend .product-item__select-options > span:first-child:not([data-cnc-lbl])"
    ).forEach(function (el) {
      el.setAttribute("data-cnc-lbl", "");
      var multi = !!el.closest(".product-item__select-options");
      el.textContent = multi ? "Choose options" : "Add to bag";
      var btn = el.closest("button, select-option");
      if (btn && btn.hasAttribute("aria-label")) btn.setAttribute("aria-label", el.textContent);
    });
  }
  /* The /cart page's line card prints "18% off" in green beside the struck
     MRP; the host drawer row prints only the two prices. Added here from the
     two figures already on the row, through discPct() — the one rounding rule
     (ceil) every card, the PDP and the cart page use. Paise throughout, so a
     ₹499/₹600 line reads 17% here exactly as it does on its card. */
  function paisa(txt) {
    var n = parseFloat(String(txt || "").replace(/[^0-9.]/g, ""));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }
  function tuneDrawerItems(root) {
    (root || document).querySelectorAll(".minicart .minicart__item-prices:not([data-cnc-off])").forEach(function (row) {
      row.setAttribute("data-cnc-off", "");
      var now = row.querySelector(".minicart__item-price");
      var was = row.querySelector(".minicart__item-original-price");
      if (!now || !was) return;
      var pct = discPct(paisa(now.textContent), paisa(was.textContent));
      if (!pct) return;
      var tag = document.createElement("span");
      tag.className = "cnc-mc-off";
      tag.textContent = pct + "% off";
      was.insertAdjacentElement("afterend", tag);
    });
  }
  var UPSELL_TODO = ".cart-recommend .btn-action:not([data-cnc-lbl]), .cart-recommend .product-item__select-options > span:first-child:not([data-cnc-lbl]), .minicart__item-prices:not([data-cnc-off])";
  function observeUpsell() {
    if (!window.MutationObserver) return;
    var panel = document.querySelector(DRAWER_PANEL_SEL);
    if (!panel || !once(panel, "cncUpsellObs")) return;
    tuneUpsell(panel);
    tuneDrawerItems(panel);
    new MutationObserver(function () {
      if (panel.querySelector(UPSELL_TODO)) {
        tuneUpsell(panel);
        tuneDrawerItems(panel);
      }
    }).observe(panel, { childList: true, subtree: true });
  }

  /* ── Empty badges stay empty ────────────────────────────────────────────
     The host theme's count bubbles (.h-count — the phone bottom bar's
     Wishlist and Cart, and the header's own) print "0" in a red dot when
     nothing is saved. A notification dot that says "nothing" is noise, and on
     Wishlist it read as a count of something missing. Hidden at zero; shown
     again the moment anything writes a number into it (the host's own sync,
     writeWish() here, or refreshCartDrawer's innerHTML copy). The number is
     the badge's FIRST text node — the cart bubble also carries a
     visually-hidden "2 items" label, so textContent alone reads "2 2 items". */
  function badgeCount(el) {
    for (var n = el.firstChild; n; n = n.nextSibling) {
      if (n.nodeType === 3 && n.nodeValue.trim()) return parseInt(n.nodeValue, 10) || 0;
    }
    return parseInt(el.textContent, 10) || 0;
  }
  function syncZeroBadges() {
    document.querySelectorAll(".h-count").forEach(function (el) {
      el.classList.toggle("cnc-count-zero", badgeCount(el) === 0);
    });
  }
  function initZeroBadges() {
    syncZeroBadges();
    if (!window.MutationObserver || !once(document.documentElement, "cncZeroBadges")) return;
    var queued = false;
    new MutationObserver(function (list) {
      if (queued) return;
      for (var i = 0; i < list.length; i++) {
        var t = list[i].target;
        var el = t.nodeType === 3 ? t.parentNode : t;
        if (el && el.closest && el.closest(".h-count")) {
          queued = true;
          requestAnimationFrame(function () { queued = false; syncZeroBadges(); });
          return;
        }
      }
    }).observe(document.body, { childList: true, characterData: true, subtree: true });
    window.addEventListener("storage", syncZeroBadges);
    document.addEventListener("cnc:wishlist-updated", function () { setTimeout(syncZeroBadges, 0); });
  }

  /* ── Buy Now ────────────────────────────────────────────────────────────
     Everything else on the product page is now the theme's job, but Buy Now
     isn't: <product-form> intercepts every submit in the form and turns it into
     an AJAX add, which would silently strip the "and go to checkout" half of
     the button and just open the drawer instead.

     Capture phase on document, so this runs before product-form's own listener,
     and stopImmediatePropagation so it never gets one. We add, then navigate —
     no drawer render involved, because we're leaving the page. */
  function initBuyNow() {
    if (!once(document.documentElement, "buyNow")) return;
    document.addEventListener("submit", function (e) {
      var btn = e.submitter;
      if (!btn || !btn.hasAttribute("data-buynow")) return;
      var form = e.target;
      if (!form || !form.matches("[data-product-form]")) return;
      if (!window.fetch) return;                // no fetch: let it post natively

      e.preventDefault();
      e.stopImmediatePropagation();

      var body = new FormData(form);
      body.delete("return_to");
      postAdd(body)
        .then(function () {
          /* The product page's best-offer auto-apply. Buy Now leaves the page
             before any drawer could render, so the code has to be on the cart
             before we navigate. Capped at 2.5s: a slow discount call must never
             hold a customer back from checkout — worst case they pay full
             price and can still enter the code there. */
          var hook = window.CNC && window.CNC.beforeCheckout;
          if (typeof hook !== "function") return;
          return Promise.race([
            Promise.resolve().then(hook).catch(function () {}),
            new Promise(function (r) { setTimeout(r, 2500); })
          ]);
        })
        .then(function () { window.location.href = "/checkout"; })
        .catch(function (err) {
          console.warn("[cnc-cart] buy now failed — " + (err && err.message));
          form.submit();                        // fall back to a native post
        });
    }, true);
  }

  /* ═══ CART DRAWER ENHANCEMENTS ═════════════════════════════════════════════
     The minicart is the host theme's, rendered by `snippets/minicart.liquid`.
     Four things the app's CartScreen has and it doesn't get added here, from
     the outside, rather than by forking that snippet:

       1. no Terms & Conditions gate on checkout
       2. View Cart and Checkout on one row
       3. a coupons row that opens a sub-drawer
       4. a read-only CNC Points panel

     Doing it from JS rather than editing the snippet is deliberate. That file
     belongs to Glozin and a theme update overwrites it; nothing here is lost
     when that happens. The trade is a frame where the original markup is
     visible — which costs nothing in practice, because the drawer is closed at
     page load and enhanceDrawer() has run long before it opens.

     Everything is idempotent and re-runs after each render: refreshCartDrawer
     replaces the panel's innerHTML wholesale, so any state kept on a node
     rather than derived from the cart would be gone by the second add.       */

  var SHEET_SEL = ".cnc-coupon-sheet";
  var COUPONS_ENDPOINT = "https://cnc-api-five.vercel.app/api/coupons";

  /* ── Shared state ────────────────────────────────────────────────────────
     THIS MUST LIVE ON window, NOT IN THE CLOSURE.

     Every cnc- section emits its own <script src="cnc-app.js">, and browsers
     execute each duplicate tag. That's four or five separate runs of this IIFE
     on a page, each with its own closure — so a `var productCache = {}` in here
     is four independent empty caches, and boot() runs four times.

     The `once()` guards already stop widgets binding twice, because they stamp
     a shared DOM node. Caches had no such anchor, so every network call was
     multiplied by the number of sections on the page: a six-item cart issued
     24 requests to /products/*.js and 16 to /cart.js on a single drawer open.
     Shopify answered 429, /cart.js came back as an HTML error page, r.json()
     threw, and the drawer failed with "Try again" on items that were in fact
     already in the cart.

     Hanging the caches off window gives them the same single owner the DOM
     guards rely on. */
  var SHARED = (window.__cncShared = window.__cncShared || {
    products: {},     // handle -> Promise<product>
    coupons: null,    // Promise<coupon[]>
    points: null,     // Promise<points|null>
    cart: null,       // last good /cart.js payload
    cartAt: 0,        // when we fetched it
    cartPromise: null,// in-flight /cart.js, so parallel callers share one request
    painting: false,  // guards against overlapping drawer repaints
    ptsSig: "",       // last cart/redemption state reconciled — see reconcilePoints
    ptsClash: ""      // a coupon that refused to share the cart with a points code
  });

  var CART_TTL_MS = 1200;

  /* Shopify serves /cart.js and /products/<handle>.js as `text/javascript`,
     NOT `application/json`. A previous version gated parsing on the header
     containing "json" and so rejected every single valid response — the totals,
     the MRP breakdown and the wishlist rail all render inside the block that
     bails when the cart is null, so all three silently vanished while the
     network tab showed a clean 200.

     Sniffing the body is both more permissive and more accurate: it accepts any
     content-type Shopify chooses, and still rejects the HTML error page served
     under rate limiting, which is the case the guard existed for. */
  function readJsonBody(res) {
    return res.text().then(function (txt) {
      var s = txt.trim();
      if (s.charAt(0) !== "{" && s.charAt(0) !== "[") {
        throw new Error("expected JSON, got " + (s.slice(0, 24) || "empty"));
      }
      return JSON.parse(s);
    });
  }

  /* Every Shopify cart .js endpoint, read the one way that survives a bad day.

     readJsonBody already existed for /cart.js because Shopify answers its .js
     endpoints with an HTML ERROR PAGE rather than JSON when it throttles a
     session or its bot protection fires. The WRITE endpoints never got the
     same treatment, and a raw r.json() on that page throws

         Unexpected token '<', "<!DOCTYPE "... is not valid JSON

     which the coupon sheet then printed at the customer, verbatim, in red,
     under the code they had just tapped. Measured against the live store the
     same request returns clean JSON — with a real code, a bogus code, an empty
     cart and the sections request attached — so the HTML is the abnormal
     answer, which is exactly the case a parser must be ready for and a
     customer must never be shown.

     Two failure shapes, deliberately distinguished by `userMessage`:

       Shopify replied, and said no      its own words, fit to show as-is
       Shopify did not reply in JSON     nothing here is fit to show; the
                                         caller supplies a human sentence and
                                         the technical detail goes to console */
  function cartJson(res, rejectMsg) {
    return readJsonBody(res).then(
      function (data) {
        if (!res.ok) {
          var refused = new Error(data.description || data.message || rejectMsg);
          refused.userMessage = refused.message;
          throw refused;
        }
        return data;
      },
      function (parseErr) {
        var broken = new Error("HTTP " + res.status + " — " + parseErr.message);
        broken.userMessage = "";
        throw broken;
      }
    );
  }

  /* THE SECTION RENDERING API, FETCHED THE ONE WAY THAT WORKS ON /cart.

     Read this before writing another ?sections= request by hand.

     ?sections= is documented as working on any URL, and it does — unless the
     request ALSO asks for JSON. `/cart` is a dual-purpose route: send it
     `Accept: application/json` and Shopify answers with the CART OBJECT and
     ignores ?sections= entirely.

     Measured on the live store, same URL, three ways:

       Accept: application/json  →  200, keys: token, note, attributes,
                                    original_total_price, items …  (the cart)
       no Accept header          →  200, keys: template--…__cart
                                    → 16,927 chars of section HTML   ✓
       Accept: text/html         →  200, same as above               ✓

     Nothing throws. The caller gets a 200 and a valid JSON body; it just
     contains no section markup, so renderCartSection() and swapSection() find
     nothing, return false, and every surface keeps whatever it was already
     showing. That was the whole "the coupon applies on the server but the page
     never updates, and no confetti fires" bug — three layers of correct code
     sitting downstream of one header.

     So: NO Accept header. The endpoint replies application/json regardless,
     and readJsonBody still catches the HTML error page served under rate
     limiting, which is the case that header was reaching for. Every
     ?sections= read in this codebase goes through here. */
  function fetchSections(url, ids) {
    var list = Array.isArray(ids) ? ids.join(",") : ids;
    var sep = url.indexOf("?") === -1 ? "?" : "&";
    return fetch(url + sep + "sections=" + encodeURIComponent(list))
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return readJsonBody(r);
      });
  }

  /* ── CNC Points ───────────────────────────────────────────────────────────
     Both of these are App Proxy paths on the SHOP's own origin, not the Vercel
     URL, and that is the whole security model rather than a routing detail:
     Shopify appends `logged_in_customer_id` from its own session cookie and
     signs the query string, so the customer's identity arrives from Shopify
     instead of from this page. Nothing here sends an email or an id — there is
     nothing a browser could send that the server would believe.

     They are the SAME serverless function behind ?resource=, because Vercel's
     Hobby plan caps this deployment at 12 functions and cnc-api is at exactly
     12. See the header of cnc-api/api/addresses.js.

     A theme ships independently of a Vercel deploy, so both calls must fail
     soft: no balance means the card simply doesn't render, and the cart works
     exactly as it did before any of this existed. */
  var POINTS_ENDPOINT = "/apps/cnc/addresses?resource=rewards";
  var REDEEM_ENDPOINT = "/apps/cnc/addresses?resource=points";

  /* Mirrors src/utils/rewards.js. Kept as a literal because the theme can't
     import from the app — if the tier maths ever changes there, change it
     here in the same commit. */
  var POINTS_PER_RUPEE = 10;
  var MIN_REDEEM_POINTS = 100;
  var EARN_CAP_ORDER_VALUE = 10000;
  var TIERS = [
    { id: "bronze",   label: "Bronze",   min: 0,  earnPer10: 2, redeemPct: 10 },
    { id: "silver",   label: "Silver",   min: 10, earnPer10: 3, redeemPct: 15 },
    { id: "gold",     label: "Gold",     min: 20, earnPer10: 5, redeemPct: 20 },
    { id: "platinum", label: "Platinum", min: 50, earnPer10: 6, redeemPct: 25 }
  ];
  function tierForOrders(n) {
    var cur = TIERS[0];
    for (var i = 0; i < TIERS.length; i++) if ((n || 0) >= TIERS[i].min) cur = TIERS[i];
    return cur;
  }
  function pointsToRupees(pts) { return (pts || 0) / POINTS_PER_RUPEE; }

  /* Mirrors pointsForOrder() in src/utils/rewards.js — earned on the first
     ₹10,000 of an order only. */
  function pointsForOrder(orderRupees, orderCount) {
    var tier = tierForOrders(orderCount);
    return Math.floor(Math.min(orderRupees || 0, EARN_CAP_ORDER_VALUE) / 10) * tier.earnPer10;
  }

  /* Mirrors maxRedeemRupees / maxRedeemPoints in src/utils/rewards.js and
     clampRedemption in cnc-api/api/_lib/rewards.js. Three copies of one rule
     is two too many, and it is still the right trade: the app cannot import
     from the theme, the theme cannot import from the server, and the server
     cannot let the browser decide what a customer may spend. Change one,
     change all three in the same commit — the server's answer is the one that
     binds, so a stale copy here shows a button that then can't be honoured. */
  function maxRedeemRupees(orderTotal, orderCount) {
    return Math.floor(((orderTotal || 0) * tierForOrders(orderCount).redeemPct) / 100);
  }
  function maxRedeemPoints(balance, orderTotal, orderCount) {
    var cap = Math.round(maxRedeemRupees(orderTotal, orderCount) * POINTS_PER_RUPEE);
    return Math.max(0, Math.min(balance || 0, cap));
  }

  var ICON = {
    tag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82Z"/><circle cx="7" cy="7" r="1.2" fill="currentColor"/></svg>',
    chevR: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>',
    chevL: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
    star: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="m12 2 2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2Z"/></svg>',
    arrowL: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5m0 0 7-7m-7 7 7 7"/></svg>',
    chevD: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>',
    receipt: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16v16l-2.7-1.6L14.6 20l-2.6-1.6L9.4 20l-2.7-1.6L4 20V4Z"/><path d="M8 9h8M8 13h5"/></svg>',
    heart: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-7.5-4.7-9.3-9A5.3 5.3 0 0 1 12 6.7 5.3 5.3 0 0 1 21.3 12c-1.8 4.3-9.3 9-9.3 9Z"/></svg>',
    clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
    /* Same geometry as `shopping-bag` and `sliders` in snippets/cnc-icon.liquid.
       Duplicated rather than shared for the reason `lock` below is: this map is
       what the drawer paints with, and the drawer renders on pages that carry
       no Liquid snippet. The paths must stay identical to the snippet's, or the
       bag on a shelf card and the bag on the cart page are different bags. */
    bag: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6ZM3 6h18M16 10a4 4 0 0 1-8 0"/></svg>',
    sliders: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/></svg>',
    /* The app's redeem card is headed by a trophy in amber-deep, and the
       points card here is the same object on a different screen — a different
       glyph would read as a different feature. */
    trophy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M8 4h8v5a4 4 0 0 1-8 0V4Z"/><path d="M16 5h2.5a1.5 1.5 0 0 1 0 5H16M8 5H5.5a1.5 1.5 0 0 0 0 5H8"/><path d="M12 13v3m-3 4h6l-.6-2.4a1 1 0 0 0-1-.6h-2.8a1 1 0 0 0-1 .6L9 20Z"/></svg>',
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>',
    /* Same geometry as `lock` in snippets/cnc-icon.liquid. Duplicated rather
       than shared because this map is what the drawer paints with and the
       drawer renders on pages that carry no Liquid snippet — but the two
       paths must stay identical, or the padlock on the cart page and the
       padlock in the bag are visibly different padlocks. */
    lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>'
  };

  function money(rs) {
    var n = Math.round((rs || 0) * 100) / 100;
    var s = (Number.isInteger(n) ? String(n) : n.toFixed(2)).split(".");
    var x = s[0], last3 = x.slice(-3), rest = x.slice(0, -3);
    if (rest) last3 = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
    return "₹" + last3 + (s[1] ? "." + s[1] : "");
  }

  /* ── Coupon confirmation ─────────────────────────────────────────────────

     Applying a coupon used to be silent: a row changed colour and a number
     inside a collapsed breakdown got smaller. Someone who had just hunted for
     a code got no acknowledgement it had landed, let alone what it was worth,
     so they re-opened the sheet to check — which is most of why the flow felt
     slow.

     This lives in cnc-app.js rather than on the cart page because a coupon can
     be applied from the minicart drawer on ANY page in the store. Built on the
     page first, it only ever congratulated the one surface that already showed
     the totals; the drawer, which is where most codes are actually entered,
     got nothing.

     The numbers come from the cart object paintBlocks already holds, not from
     scraping the DOM, and they are formatted with money() — the one formatter
     allowed to produce a figure anyone acts on. */

  var CONFETTI = ["#01579B", "#0288D1", "#F2A900", "#FDD835", "#66BB6A", "#B3E5FC"];

  /* Resolved from the DOM, never cached in a closure: this file executes once
     per cnc section, and a per-copy variable would give each copy its own
     toast to stack on top of the others. See the note on cartListeners. */
  function toastNode() {
    var el = document.querySelector(".cnc-toast");
    if (el) return el;
    el = document.createElement("div");
    el.className = "cnc cnc-toast";
    /* status, not alert: this confirms something the customer just did, so it
       should be announced politely rather than interrupt a screen reader. */
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    document.body.appendChild(el);
    return el;
  }

  function toast(kind, html) {
    var el = toastNode();
    el.className = "cnc cnc-toast" + (kind === "undo" ? " cnc-toast--undo" : "");
    el.innerHTML = '<span class="cnc-toast-ic">' + ICON.check + "</span><span>" + html + "</span>";
    // Two frames: one to mount, one to let the browser see the start state.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add("is-in"); });
    });
    clearTimeout(el.__cncToastTimer);
    el.__cncToastTimer = setTimeout(function () { el.classList.remove("is-in"); }, 3400);
  }

  /* A toast that can be taken back.

     Un-saving is one tap on a small target, and dropFromWishlist deletes the
     META as well as the id — so a mis-tap on a phone costs the customer a
     product they may no longer be able to name. The undo therefore has to
     carry the whole record back, not just re-add an id, which is why the
     caller hands over a closure rather than a value.

     The action lives on the ELEMENT (`__cncUndo`), not in a closure here: this
     file runs once per CNC section, there is exactly one toast node on the
     page, and a per-copy variable would give the last-loaded copy's handler
     nothing to undo. Same rule as cartListeners and SHARED. */
  function toastAction(icon, html, label, fn) {
    var el = toastNode();
    el.className = "cnc cnc-toast cnc-toast--undo";
    el.innerHTML = '<span class="cnc-toast-ic">' + icon + "</span><span>" + html + "</span>"
      + '<button type="button" class="cnc-toast-act" data-cnc-toast-act>' + esc(label) + "</button>";
    el.__cncUndo = fn;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add("is-in"); });
    });
    clearTimeout(el.__cncToastTimer);
    /* Longer than the plain toast's 3.4s. This one is not an announcement, it
       is an offer, and an offer has to outlast the moment it takes to read it. */
    el.__cncToastTimer = setTimeout(function () {
      el.classList.remove("is-in");
      el.__cncUndo = null;
    }, 6000);
  }

  function initToastAction() {
    if (!once(document.documentElement, "cncToastAct")) return;
    document.addEventListener("click", function (e) {
      var b = e.target.closest("[data-cnc-toast-act]");
      if (!b) return;
      e.preventDefault();
      var el = b.closest(".cnc-toast");
      var fn = el && el.__cncUndo;
      if (el) { el.classList.remove("is-in"); el.__cncUndo = null; }
      if (typeof fn === "function") fn();
    });
  }

  function confetti() {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    var layer = document.createElement("div");
    layer.className = "cnc-confetti";
    layer.setAttribute("aria-hidden", "true");
    var html = "";
    for (var i = 0; i < 26; i++) {
      var w = 6 + Math.random() * 5;
      html += '<i style="left:' + (Math.random() * 100).toFixed(2) + "%;"
        + "width:" + w.toFixed(1) + "px;height:" + (w * 1.5).toFixed(1) + "px;"
        + "background:" + CONFETTI[i % CONFETTI.length] + ";"
        + "--dx:" + ((Math.random() * 2 - 1) * 90).toFixed(0) + "px;"
        + "--spin:" + (360 + Math.random() * 720).toFixed(0) + "deg;"
        + "animation-delay:" + (Math.random() * 0.22).toFixed(2) + 's"></i>';
    }
    layer.innerHTML = html;
    document.body.appendChild(layer);
    setTimeout(function () { layer.remove(); }, 2100);
  }

  /* Called from paintBlocks, which runs for the drawer AND for the cart page.
     `SHARED.lastCode` is what keeps that from celebrating twice: whichever
     surface paints first records the new code, and the second sees no change.
     It is on SHARED for the same reason everything else is — this file runs
     once per section, and a per-copy variable would celebrate once per copy.

     `undefined` means "not observed yet". A cart that ALREADY had a coupon on
     it when the page loaded has not just had one applied, and confetti on
     every page load is how a delight becomes an irritation. */
  function announceCoupon(code, cart) {
    var before = SHARED.lastCode;
    SHARED.lastCode = code;
    if (before === undefined) return;

    if (code && code !== before) {
      var off = cart && cart.total_discount ? money(cart.total_discount / 100) : "";
      toast("ok", "<b>" + esc(code) + "</b> applied"
        + (off ? " — you saved <b>" + esc(off) + "</b>" : ""));
      confetti();

      /* Open every breakdown on screen. The whole point of the announcement is
         the number behind it, and in both surfaces that number lives one tap
         inside an accordion nobody has a reason to suspect. */
      document.querySelectorAll("[data-cnc-total-rows]").forEach(function (rows) {
        rows.hidden = false;
        var head = rows.parentNode && rows.parentNode.querySelector("[data-cnc-total-toggle]");
        if (head) head.setAttribute("aria-expanded", "true");
      });
    } else if (!code && before) {
      toast("undo", "Coupon removed");
    }
  }

  /* ── Data ──────────────────────────────────────────────────────────────── */

  /* One fetch per page load, shared by the row and the sub-drawer. The list is
     the same one the app reads, so the two can't drift. A failure resolves to
     an empty list rather than rejecting: a drawer with no coupon list still
     has to check out. */
  function fetchCoupons() {
    if (SHARED.coupons) return SHARED.coupons;
    SHARED.coupons = fetch(COUPONS_ENDPOINT)
      .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then(function (d) { return Array.isArray(d.coupons) ? d.coupons : []; })
      .catch(function (e) {
        console.warn("[cnc-cart] coupon list unavailable — " + (e && e.message));
        return [];
      });
    return SHARED.coupons;
  }

  /* /cart.js is the only place the applied discount code is legible. The
     drawer's Liquid prints the discount's *title*, which is the rule name and
     not the code a customer typed, so it can't be matched back to the list.

     Three layers of restraint, all of them earned the hard way:
       - a short TTL, because several blocks want the cart in the same repaint
       - one shared in-flight promise, so parallel callers don't each issue a
         request
       - a body sniff. Under rate limiting Shopify answers .js endpoints with an
         HTML error page, and r.json() on that throws a SyntaxError far from the
         real cause. Returning the last good cart keeps the drawer showing
         something true rather than collapsing. */
  function fetchCart(force) {
    var now = Date.now();
    if (!force && SHARED.cart && now - SHARED.cartAt < CART_TTL_MS) {
      return Promise.resolve(SHARED.cart);
    }
    if (SHARED.cartPromise) return SHARED.cartPromise;

    SHARED.cartPromise = fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return readJsonBody(r);
      })
      .then(function (c) {
        SHARED.cart = c;
        SHARED.cartAt = Date.now();
        return c;
      })
      .catch(function (e) {
        console.warn("[cnc-cart] /cart.js unavailable — " + (e && e.message)
          + (SHARED.cart ? "; using last known cart" : ""));
        return SHARED.cart;      // null on a cold failure, which callers handle
      })
      .then(function (c) { SHARED.cartPromise = null; return c; });

    return SHARED.cartPromise;
  }

  function invalidateCart() { SHARED.cart = null; SHARED.cartAt = 0; }

  /* A points redemption rides on the cart as a discount code like any other,
     so every place that used to assume "the applied code is the coupon" has to
     say which one it means. Minted server-side with this prefix — see
     POINTS_CODE_PREFIX in cnc-api/api/_lib/rewards.js. */
  var POINTS_CODE_RE = /^CNCPTS-/i;
  function isPointsCode(code) { return POINTS_CODE_RE.test(String(code || "")); }

  /** Every code the cart is carrying, applicable or not. */
  function cartCodes(cart) {
    return ((cart && cart.discount_codes) || []).map(function (d) { return String(d.code || ""); });
  }

  /** The COUPON the customer typed — never the points code. */
  function appliedCode(cart) {
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      if (codes[i].applicable && !isPointsCode(codes[i].code)) return codes[i].code;
    }
    return "";
  }

  /** The live points code on the cart, and whether Shopify is honouring it. */
  function appliedPoints(cart) {
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      if (isPointsCode(codes[i].code)) {
        return { code: codes[i].code, applicable: !!codes[i].applicable };
      }
    }
    return null;
  }

  /* Shopify accepts a discount on the cart, but silently: /cart/update.js
     answers 200 whether the code applied or not, with `applicable: false` the
     only tell. Reporting "couldn't apply" and stopping there is what makes
     coupon UIs infuriating, so the condition line from our own list is folded
     in — nine times in ten that (minimum order, item count) is the reason. */
  /* Titles of discounts Shopify has already put on the cart by itself. These
     are the automatic ones — bundle deals, buy-more-save-more — which the
     customer never typed and cannot see a way to remove. */
  function autoDiscountTitles(cart) {
    var seen = {};
    ((cart && cart.items) || []).forEach(function (i) {
      (i.line_level_discount_allocations || []).forEach(function (a) {
        var t = a.discount_application && a.discount_application.title;
        if (t) seen[t] = 1;
      });
    });
    ((cart && cart.cart_level_discount_applications) || []).forEach(function (d) {
      if (d.title) seen[d.title] = 1;
    });
    return Object.keys(seen);
  }

  /* Why Shopify said no.

     The previous version printed the coupon's own condition — "On orders above
     ₹999" — whenever a code was refused, on the assumption that an unmet
     minimum was the only way this happens. It isn't, and the result was the
     UI confidently telling a customer with ₹1,910 in their cart that they
     hadn't reached ₹999. A wrong reason is worse than no reason: it sends
     someone off to add items that won't help.

     `applicable: false` carries no explanation, so the checks run in order of
     what can actually be verified, and the last branch admits ignorance rather
     than inventing something. */
  function rejectionReason(code, cart) {
    return fetchCoupons().then(function (list) {
      var known = list.filter(function (c) {
        return String(c.code || "").toUpperCase() === String(code).toUpperCase();
      })[0];
      if (!known) return "“" + code + "” isn't a code this store recognises.";

      var subtotal = cart ? cart.items_subtotal_price / 100 : 0;
      var qty = cart ? cart.item_count : 0;

      // Only claim a minimum is unmet when it demonstrably is.
      if (known.minOrder && subtotal < known.minOrder) {
        return "Add " + money(known.minOrder - subtotal) + " more to use this code — "
          + "it needs a minimum order of " + money(known.minOrder) + ".";
      }
      if (known.minQty && qty > 0 && qty < known.minQty) {
        var n = known.minQty - qty;
        return "Add " + n + " more item" + (n !== 1 ? "s" : "") + " to use this code.";
      }

      /* The points redemption is a discount too, and a coupon that is not set
         to combine with product discounts will be refused while one is on the
         cart — with the same silent `applicable: false` as every other
         refusal. Naming it turns a dead end into a choice, because removing
         the points is something the customer can do from the card right above
         this message. */
      if (appliedPoints(cart)) {
        return code + " can't be combined with the CNC Points on this order. "
          + "Remove the points to use it, or keep the points and checkout.";
      }

      /* Most Shopify codes are configured not to stack with automatic
         discounts. When one is already on the cart and the stated conditions
         are met, this is overwhelmingly the reason. */
      var auto = autoDiscountTitles(cart);
      if (auto.length) {
        return code + " can't be combined with the " + auto[0]
          + " already applied to this cart.";
      }

      return code + " can't be applied to this cart — it may have expired or "
        + "reached its usage limit.";
    });
  }

  /* The section the theme itself re-renders the drawer with. Observed on the
     wire: every quantity change posts sections:["minicart-form"] and swaps the
     returned markup into #minicart-form. */
  var CART_SECTION = "minicart-form";

  /* Re-render the drawer THE THEME'S WAY.

     Line keys are a hash that includes a line's discount allocations, so
     applying or removing a coupon silently rewrites every key in the cart.
     Leaving stale markup on screen means the next quantity change, delete or
     variant edit posts a key the server no longer knows — Shopify answers 400
     "no valid id or line parameter", and to the customer the drawer simply
     stops responding.

     So we must re-render, but only through the door the theme already uses:
     ask for the section, replace the same node it replaces. This is not the
     freehand innerHTML surgery that broke the quantity stepper — that rewrote
     .minicart__wrapper, orphaning the references <cart-notification> caches. */
  function renderCartSection(sections) {
    var html = sections && sections[CART_SECTION];
    var live = document.getElementById(CART_SECTION);
    if (!html || !live) return false;
    var doc = new DOMParser().parseFromString(html, "text/html");
    var fresh = doc.getElementById(CART_SECTION);
    if (!fresh) return false;
    live.innerHTML = fresh.innerHTML;
    return true;
  }

  /* Self-healing guard against stale line keys.

     A line key is a hash that includes the line's discount allocations, so an
     automatic bundle discount re-allocating across the cart silently rewrites
     keys on lines nobody touched. Any drawer markup rendered before that point
     is now pointing at lines the server has never heard of, and the theme's own
     quantity stepper, delete and variant edit all post those dead keys:
     400 "no valid id or line parameter", and the drawer appears frozen.

     Guarding each individual path proved insufficient — the drift can come from
     a discount re-evaluating, which no single caller owns. So instead the
     drawer checks itself on every repaint: compare the keys on screen with the
     keys on the server, and if they disagree, pull a fresh section. Costs one
     comparison in the common case, where nothing has drifted. */
  function reconcileLineKeys(cart) {
    var dom = [];
    document.querySelectorAll(".minicart .quantity-input").forEach(function (i) {
      if (i.dataset.id) dom.push(i.dataset.id);
    });
    var server = ((cart && cart.items) || []).map(function (i) { return i.key; });
    if (!dom.length) return;
    var same = dom.length === server.length && dom.every(function (k, n) {
      return k === server[n];
    });
    if (same) return;

    console.warn("[cnc-cart] drawer line keys were stale — re-rendering");
    /* GET ?sections=, not a sections-only POST to /cart/update.js: measured,
       that returns no sections at all when nothing about the cart changes.
       The GET form of the Section Rendering API is read-only and always
       renders, which is what a repair path wants — it must never be able to
       alter the cart it was called to describe. */
    fetchSections(location.pathname, CART_SECTION)
      .then(function (sections) { renderCartSection(sections); })
      .catch(function (e) {
        console.warn("[cnc-cart] could not refresh stale drawer — " + (e && e.message));
      });
  }

  /* THE DISCOUNT PARAMETER REPLACES THE WHOLE SET. IT DOES NOT ADD.

     This is the single most breakable thing about carrying two codes at once.
     `{ discount: 'WELCOME15' }` does not put WELCOME15 alongside what is
     already there — it makes WELCOME15 the cart's entire discount list, and
     `{ discount: '' }` clears everything. So every call has to name every code
     the cart should end up with, and the moment one caller sends a bare code
     the customer's points redemption silently vanishes from the cart while
     their balance stays spent.

     Hence one setter. Nothing else in this file may post `discount` — the
     helpers below express the four things anyone actually wants to do, and all
     four are "keep the other one". */
  function postDiscounts(list) {
    var codes = (list || []).filter(Boolean);
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      // Ask for the drawer markup in the same round trip. Without it the DOM
      // keeps line keys the discount change has just invalidated.
      body: JSON.stringify({
        discount: codes.join(","),
        sections: [CART_SECTION],
        sections_url: location.pathname
      })
    })
      .then(function (r) { return cartJson(r, "Shopify rejected that code"); })
      .then(function (cart) {
        invalidateCart();
        renderCartSection(cart.sections);
        return cart;
      });
  }

  /** Read the cart's current codes, then post the set `mutate` asks for. */
  function setCartDiscounts(mutate) {
    return fetchCart().then(function (cart) {
      return postDiscounts(mutate(cartCodes(cart || {})));
    });
  }

  /* Apply (or clear) the COUPON, leaving any points redemption in place.
     `code` empty means "remove the coupon", which is why the points code is
     filtered back in rather than the whole list being cleared. */
  function applyDiscount(code) {
    return setCartDiscounts(function (codes) {
      var keep = codes.filter(isPointsCode);
      return code ? keep.concat([code]) : keep;
    }).then(function (cart) {
      if (!code) return { ok: true, cart: cart };
      var hit = (cart.discount_codes || []).filter(function (d) {
        return String(d.code).toUpperCase() === String(code).toUpperCase();
      })[0];
      if (hit && hit.applicable) return { ok: true, cart: cart };
      return rejectionReason(code, cart).then(function (why) {
        return { ok: false, cart: cart, reason: why };
      });
    });
  }

  /** Put the minted points code on the cart, leaving the coupon in place. */
  function applyPointsCode(code) {
    return setCartDiscounts(function (codes) {
      return codes.filter(function (c) { return !isPointsCode(c); }).concat([code]);
    });
  }

  /** Take the points code off, leaving the coupon in place. */
  function clearPointsCode() {
    return setCartDiscounts(function (codes) {
      return codes.filter(function (c) { return !isPointsCode(c); });
    });
  }

  /* ── Customer ──────────────────────────────────────────────────────────── */

  /* Shopify exposes the logged-in customer id on window.__st; the logout link
     is the fallback for themes that strip it. This only decides whether it is
     worth ASKING — the proxy decides who the customer actually is. */
  function signedIn() {
    if (window.__st && window.__st.cid) return true;
    return !!document.querySelector('a[href*="/account/logout"]');
  }

  /* The balance, the tier basis and any live redemption, in one read.

     Cached on SHARED because this file runs once per cnc- section and four
     copies asking in parallel is the bug documented on SHARED itself. Every
     write path calls invalidatePoints() rather than patching the cached value:
     the server owns these numbers — it clamps the request, it sweeps expired
     holds, it may refund points this page never knew were held — so guessing
     the new balance locally is how the two go out of sync. */
  function fetchPoints() {
    if (!POINTS_ENDPOINT || !signedIn()) return Promise.resolve(null);
    if (SHARED.points) return SHARED.points;
    SHARED.points = fetch(POINTS_ENDPOINT, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return d && d.ok ? d : null; })
      .catch(function () { return null; });
    return SHARED.points;
  }

  function invalidatePoints() { SHARED.points = null; }

  /* Ask the server to spend `pts` points on a cart worth `subtotal` rupees.
     `pts` is a ceiling and not an instruction — the server clamps it against
     the real balance and the tier cap, which is why the response's numbers are
     the ones painted and the request's are never reused. */
  function redeemPoints(pts, subtotal) {
    return fetch(REDEEM_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ points: pts, subtotal: subtotal })
    })
      .then(function (r) { return r.json().catch(function () { return null; }); })
      .then(function (d) {
        invalidatePoints();
        if (!d) throw new Error("Could not reach your rewards account.");
        if (!d.ok) throw new Error(d.error || "Those points can't be used on this cart.");
        return d;
      });
  }

  function releasePoints(code) {
    return fetch(REDEEM_ENDPOINT, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ code: code || "" })
    })
      .then(function (r) { return r.json().catch(function () { return null; }); })
      .then(function (d) { invalidatePoints(); return d; })
      .catch(function () { invalidatePoints(); return null; });
  }

  /* ── The drawer footer ─────────────────────────────────────────────────── */

  function unlockCheckout(footer) {
    footer.querySelectorAll("terms-conditions, .terms-conditions").forEach(function (n) {
      n.remove();
    });
    footer.querySelectorAll("[name='checkout'], .btn-checkout").forEach(function (b) {
      b.disabled = false;
      b.removeAttribute("disabled");
      b.classList.remove("disabled");
      b.style.pointerEvents = "";
    });
  }

  /* One action, not two.

     View Cart used to sit beside Checkout, which split the drawer's attention
     between the thing we want (pay) and a link back to a page that shows the
     same items the drawer is already showing. The drawer IS the cart — it
     lists the items, edits quantities, takes the coupon and prints the totals,
     so the only thing /cart adds is a page load on the way to the same button.

     `view` is the THEME's node, so it is hidden rather than removed: the
     theme's own scripts may still hold a reference to it, and a detached node
     is how you find out at the worst moment. Hidden, it stays queryable and
     stays out of the tab order.

     To put it back, drop the `view.hidden` line and restore the two-column
     grid in cnc-drawer.css — both sides of the change are one line each. */
  function twoUpActions(footer) {
    if (footer.querySelector(".cnc-drawer-actions")) return;
    var view = footer.querySelector("a.viewcart, a[href$='/cart']");
    var pay = footer.querySelector("[name='checkout'], .btn-checkout");
    if (!pay) return;

    var row = document.createElement("div");
    row.className = "cnc-drawer-actions";
    pay.parentNode.insertBefore(row, view || pay);

    /* View Cart is BACK, and the reason it left no longer holds.

       It was removed because "the drawer IS the cart" — it lists the items,
       edits quantities, takes the coupon and prints the totals, so /cart was
       a page load on the way to the same button. That was true of the HOST
       theme's cart page.

       It is not true of the one at /cart now. That page carries things the
       drawer has no room for: per-line MRP and savings, the delivery meter,
       the total-savings ribbon, the trust row and the recommendation rail.
       Sending someone there is no longer a detour to the same place.

       If the theme has no View Cart node — some layouts only render one when
       the cart is non-empty — one is built rather than skipped, so the pair
       never silently collapses back to a single button. That node is ours, so
       building it is safe; the checkout button is emphatically NOT, which is
       why it is MOVED. It carries form="minicart" and the theme's own submit
       binding, and a hand-rolled replacement would post nothing at all. */
    if (!view) {
      view = document.createElement("a");
      view.className = "viewcart";
      view.href = routesCartUrl();
      view.textContent = "View Cart";
    }
    view.hidden = false;
    view.removeAttribute("aria-hidden");
    view.removeAttribute("tabindex");
    view.classList.add("cnc-btn", "cnc-btn-ghost");
    row.appendChild(view);

    pay.classList.add("cnc-btn", "cnc-btn-primary");
    row.appendChild(pay);
  }

  /* ── THE AUTO-APPLY IN FLIGHT ───────────────────────────────────────────

     The product page promises "WELCOME15 is applied when you add to bag". It
     IS — but only after Add to Bag lands, /cart.js confirms it and the code is
     posted: one to three seconds in which the drawer, already open, offered
     "Apply coupon" at full price. A shopper reading that concludes the promise
     was false and taps Apply themselves, which is exactly what happened in the
     2026-09-21 walkthrough.

     So cnc-pdp.js marks the code as pending the moment it starts watching the
     add, and the row says what is happening instead of asking for an action
     that is already being taken. On window, not in this closure: this file
     runs once per CNC section (see cartListeners below). It expires on its own
     so a failed add or a refused code can never leave a spinner behind. */
  var PENDING_MS = 8000;
  function pendingCoupon() {
    var p = window.__cncPendingCoupon;
    if (!p || !p.code || Date.now() > p.until) return "";
    return p.code;
  }
  function repaintOffers() {
    document.querySelectorAll("[data-cnc-offers]").forEach(function (el) {
      var scope = el.closest("[data-cnc-cart]") || el.closest(DRAWER_PANEL_SEL) || document;
      try {
        paintBlocks(el, scope.querySelector("[data-cnc-rail]"), scope.querySelector("[data-cnc-totals]"), scope);
      } catch (err) { console.error("[cnc-cart] offers repaint failed", err); }
    });
  }
  window.CNC = window.CNC || {};
  window.CNC.markCouponPending = function (code) {
    if (!code) return;
    window.__cncPendingCoupon = { code: String(code).toUpperCase(), until: Date.now() + PENDING_MS };
    repaintOffers();
    clearTimeout(window.__cncPendingTimer);
    window.__cncPendingTimer = setTimeout(repaintOffers, PENDING_MS + 50);
  };
  window.CNC.clearCouponPending = function () {
    if (!window.__cncPendingCoupon) return;
    window.__cncPendingCoupon = null;
    clearTimeout(window.__cncPendingTimer);
    repaintOffers();
  };

  function couponRowHtml(code, coupon) {
    var pending = !code && pendingCoupon();
    if (pending) {
      return '<div class="cnc-coupon-applied cnc-coupon-pending" role="status" aria-live="polite">'
        + '<span class="cnc-coupon-ic"><span class="cnc-coupon-spin" aria-hidden="true"></span></span>'
        + '<span class="cnc-coupon-txt">'
        + '<span class="cnc-coupon-title">Applying ' + esc(pending) + "…</span>"
        + '<span class="cnc-coupon-sub">Your best offer goes on automatically</span>'
        + "</span></div>";
    }
    if (code) {
      return '<div class="cnc-coupon-applied">'
        + '<span class="cnc-coupon-ic">' + ICON.check + "</span>"
        + '<span class="cnc-coupon-txt">'
        + '<span class="cnc-coupon-title">' + esc(code) + " applied</span>"
        + '<span class="cnc-coupon-sub">'
        + esc(coupon ? couponBenefit(coupon) : "Discount applied at checkout")
        + "</span></span>"
        + '<button type="button" class="cnc-coupon-remove" data-cnc-coupon-remove>Remove</button>'
        + "</div>";
    }
    return '<button type="button" class="cnc-coupon-row" data-cnc-coupon-open'
      + ' aria-haspopup="dialog">'
      + '<span class="cnc-coupon-ic">' + ICON.tag + "</span>"
      + '<span class="cnc-coupon-txt">'
      + '<span class="cnc-coupon-title">Apply coupon</span>'
      + '<span class="cnc-coupon-sub">View all offers on this order</span>'
      + "</span>"
      + '<span class="cnc-chev">' + ICON.chevR + "</span>"
      + "</button>";
  }

  /* ── THE CNC POINTS CARD ───────────────────────────────────────────────────

     Two things in one panel, in the order they matter to the person reading:
     what this order can be paid with NOW, and what it will earn.

     THIS USED TO SAY "REDEEM UP TO 10% IN THE APP." That line was written when
     redemption genuinely was app-only, and it was the only honest thing to
     print — but it is an instruction to leave, placed at the highest-intent
     moment in the funnel, next to a balance the reader cannot spend. Now that
     the website mints its own discount, the balance is spendable here, and the
     card is the app's redeem card rather than a signpost to it.

     WHY THE MATHS IS DUPLICATED FROM THE SERVER. The button has to name a
     figure before anything is committed — "Use ₹128" is the whole affordance,
     and a button that says "Use points" and then reveals the amount afterwards
     is a worse version of the same interaction. So the cap is computed here
     too, from the balance and tier the server just sent. The server clamps
     again and its answer wins; this copy exists to LABEL the button, never to
     decide what is spent.

     The applied state is painted from the CART, not from what was clicked.
     Shopify is the only thing that knows whether the code is still being
     honoured — a cart edited below the code's minimum drops it, and a card
     that kept insisting the points were applied would be lying about a bill. */

  /* WHAT THE TIER CAP IS A PERCENTAGE **OF**, AND WHY IT IS NOT total_price.

     Once a redemption is applied, cart.total_price is already NET of it. Cap
     against that and the ceiling shrinks by the amount just spent — so the
     redemption is judged too big, gets re-minted smaller, which lowers the
     total again, which lowers the ceiling again. That is not a rounding
     wobble; it is a loop that walks a customer's discount down to nothing a
     few rupees at a time, minting a Shopify discount on every pass.

     So the cap is always taken on what the order costs BEFORE points — the
     same figure the app uses (grandTotal, computed before pointsOff). Adding
     the live redemption back is how that figure is recovered from a cart that
     has already had it taken off.

     Earning is the opposite and stays on total_price: points are credited on
     what was actually charged, which is what the order webhook will see. */
  function goodsBeforePoints(cart, data) {
    var paid = cart && typeof cart.total_price === "number" ? cart.total_price / 100 : 0;
    var live = appliedPoints(cart);
    var booked = data && data.redemption;
    return live && live.applicable && booked ? paid + pointsToRupees(booked.points) : paid;
  }

  function pointsCardHtml(data, cart, live) {
    var paid = cart && typeof cart.total_price === "number" ? cart.total_price / 100 : 0;
    var goods = goodsBeforePoints(cart, data);
    var orders = data && data.totalOrders;
    var tier = tierForOrders(orders);
    var earned = pointsForOrder(paid, orders);

    var earnLine = earned
      ? '<p class="cnc-earn"><span class="cnc-earn-ic">' + ICON.star + "</span>"
        + "<span>Earn <b>" + earned + " pts</b> (" + money(pointsToRupees(earned))
        + ") on this order</span>"
      : "";

    /* Signed out. One sentence about what points are for and one way in — no
       balance, no cap, no tier, because none of it is knowable and a made-up
       number here would be the first thing they check later. */
    if (!signedIn()) {
      if (!earned) return "";
      return '<div class="cnc-panel cnc-panel--earn">' + earnLine
        + '<a href="/account/login">Sign in</a></p></div>';
    }

    // No answer from the proxy: the theme deploys without cnc-api, so say nothing.
    if (!data || typeof data.points !== "number") {
      return earnLine ? '<div class="cnc-panel cnc-panel--earn">' + earnLine + "</p></div>" : "";
    }

    var balance = data.points;
    /* Applied means BOTH sides agree: a code Shopify is honouring and a hold
       the server knows about. Either alone is a drift reconcilePoints is on
       its way to fixing, and painting it as applied would put a discount on
       screen that the next repaint takes away. */
    var applied = live && live.applicable && data.redemption ? live : null;

    /* ROUNDED DOWN TO A WHOLE RUPEE, like the app. Points buy rupees at 10:1,
       so spending 137 of them would take ₹13.70 off — a figure that turns a
       clean total into a stray 30 paise and makes the receipt harder to read
       than the saving is worth. */
    var usable = Math.floor(maxRedeemPoints(balance, goods, orders) / 10) * 10;
    var canRedeem = usable >= MIN_REDEEM_POINTS;

    var head = '<span class="cnc-pts-ic">' + ICON.trophy + "</span>"
      + '<span class="cnc-pts-txt">'
      + '<span class="cnc-pts-title">Use CNC Points'
      + '<button type="button" class="cnc-pts-info" data-cnc-pts-info'
      + ' aria-expanded="false" aria-label="How CNC Points redemption works">'
      + ICON.info + "</button></span>";

    /* The info text is the app's, word for word, because it answers the only
       question the "?" is ever clicked for: why is the number smaller than my
       balance. Named amounts, not percentages alone — "up to 10%" makes the
       reader do arithmetic they already asked us to do. */
    var info = '<p class="cnc-pts-note" data-cnc-pts-note hidden>As a ' + tier.label
      + " member you can pay up to " + tier.redeemPct + "% of your order total ("
      + money(goods) + ") with points — that's " + money(maxRedeemRupees(goods, orders))
      + " on this order, capped by your points balance.</p>";

    var shell = function (body, on) {
      return '<div class="cnc-panel cnc-panel--pts' + (on ? " is-on" : "") + '">'
        + '<div class="cnc-pts-row">' + head + body + "</div>"
        + info
        + (earnLine ? earnLine + "</p>" : "")
        + "</div>";
    };

    /* The coupon on this cart already refused to share with a points code, and
       it is still the coupon on this cart (reconcilePoints recorded the pair).
       Offering "Use ₹128" again would set up the same refusal, take the points
       and hand them straight back — twice in a row is indistinguishable from
       broken. So name the code that is in the way instead: the Remove control
       on the coupon card directly above is the fix, and it is one click away.

       The flag clears itself the moment that coupon is no longer the one on
       the cart, which is exactly when the clash stops being true. */
    if (SHARED.ptsClash && SHARED.ptsClash !== appliedCode(cart)) SHARED.ptsClash = "";
    if (!applied && SHARED.ptsClash) {
      return shell(
        '<span class="cnc-pts-sub is-error">Can\'t be combined with '
        + esc(SHARED.ptsClash) + " — remove the coupon to pay with points</span></span>"
        + '<span class="cnc-pts-min">' + balance + " pts</span>",
        false
      );
    }

    var action, sub;

    if (applied) {
      var pts = (data.redemption && data.redemption.points) || 0;
      sub = '<span class="cnc-pts-sub is-on">' + pts + " pts applied · "
        + money(pointsToRupees(pts)) + " off this order</span>";
      action = '<button type="button" class="cnc-pts-btn is-remove" data-cnc-pts-remove>Remove</button>';
    } else if (canRedeem) {
      sub = '<span class="cnc-pts-sub">Balance ' + balance + " pts ("
        + money(pointsToRupees(balance)) + ")</span>";
      action = '<button type="button" class="cnc-pts-btn" data-cnc-pts-use="' + usable + '">'
        + "Use " + money(pointsToRupees(usable)) + "</button>";
    } else {
      /* Not enough to spend — say which wall they hit. A balance under the
         floor is a "keep going"; a balance over it that the tier cap has cut
         below the floor is a different sentence entirely, and telling someone
         with 900 points that they need 100 is the kind of wrong that costs
         a support email. */
      sub = balance < MIN_REDEEM_POINTS
        ? '<span class="cnc-pts-sub">Balance ' + balance + " pts · "
          + (MIN_REDEEM_POINTS - balance) + " more to redeem</span>"
        : '<span class="cnc-pts-sub">Balance ' + balance + " pts · cart too small to redeem</span>";
      action = '<span class="cnc-pts-min">Min ' + MIN_REDEEM_POINTS + " pts</span>";
    }

    return shell(sub + "</span>" + action, !!applied);
  }

  /* ── Keeping the cart and the ledger telling the same story ────────────────

     Two systems hold half of a redemption each: Firestore knows the points are
     spent, the Shopify cart knows the code that spends them. They can drift,
     and every way they drift is a bill that doesn't match a balance:

       · redeemed on a phone, cart opened on a laptop — the balance is down and
         the new session's cart carries no code, so the order would pay full
         price with the points already gone
       · the customer emptied the cart, or checked out, and the code survived
         in a cart the server no longer has a hold for
       · items were removed, or a coupon was applied, so the redemption is now
         larger than this cart is allowed to discount — Shopify quietly stops
         honouring the code and the totals go back up with no explanation

     So the cart is reconciled against the server on every paint. The rule is
     that the SERVER is right about how many points are spent and the cart is
     right about what is being charged; where they disagree, the cart is
     changed, and if the amount itself is now wrong the redemption is re-minted
     at whatever this cart can take.

     Re-entrancy is handled by signature rather than a lock. Each pass records
     the state it reconciled; an unchanged state is a no-op, so the repaint a
     fix triggers settles instead of looping. */
  function pointsSignature(cart, points) {
    var booked = points && points.redemption;
    return [
      cart ? cart.total_price : "-",
      cartCodes(cart || {}).join("|"),
      booked ? booked.code + ":" + booked.points : "-"
    ].join("~");
  }

  function reconcilePoints(cart, points) {
    if (!cart || !signedIn()) return Promise.resolve(false);

    var sig = pointsSignature(cart, points);
    if (SHARED.ptsSig === sig) return Promise.resolve(false);
    SHARED.ptsSig = sig;

    var live = appliedPoints(cart);
    var booked = points && points.redemption;

    // A code on the cart that no hold backs. Almost always a cart that
    // survived a checkout; either way it can only discount points nobody is
    // paying for, so it goes.
    if (live && !booked) {
      return clearPointsCode().then(function () { return true; });
    }
    if (!booked) return Promise.resolve(false);

    /* Pre-points, for the reason spelled out on goodsBeforePoints: capping
       against a total the redemption has already reduced is what turns this
       reconciliation into a descending loop. */
    var goods = goodsBeforePoints(cart, points);
    var orders = points && points.totalOrders;
    var usable = Math.floor(
      maxRedeemPoints(points.points + booked.points, goods, orders) / 10
    ) * 10;

    // Held but not on this cart — re-attach rather than refund. The points are
    // already spent; putting the code back is what makes them worth something.
    if (!live) {
      return applyPointsCode(booked.code).then(function () { return true; });
    }

    // On the cart and being honoured at the right size. Nothing to do.
    if (live.applicable && booked.points <= usable) return Promise.resolve(false);

    /* SOMETHING IS WRONG WITH THE CODE, AND WHY DECIDES WHETHER TO RE-MINT.

       Two very different failures arrive here as the same `applicable: false`,
       and treating them alike is how this turns into a loop that mints a
       discount every second:

         too big   — items came out of the cart, or a coupon reduced it, so the
                     redemption now exceeds what this order may discount. A
                     smaller one WILL apply, so re-mint at the new size.
         refused   — the code is the right size and Shopify still won't take
                     it, which in practice means the coupon on this cart is not
                     set to combine with product discounts. A new code is the
                     same code with a different name; minting one would be
                     refused identically, and reconciling again immediately.

       So the size test is the discriminator, and only the first re-mints. The
       second releases and stops, leaving the coupon in place — it is the
       discount the customer chose most recently — and remembers the pairing so
       the card can say what happened instead of silently losing the points. */
    var tooBig = booked.points > usable;
    if (!tooBig) SHARED.ptsClash = appliedCode(cart) || "";

    return releasePoints(booked.code)
      .then(function () { return clearPointsCode(); })
      .then(function () {
        if (!tooBig || usable < MIN_REDEEM_POINTS) return true;
        return redeemPoints(usable, goods)
          .then(function (d) { return applyPointsCode(d.redemption.code); })
          .then(function () { return true; })
          .catch(function () { return true; });   // released; the card repaints as unredeemed
      });
  }

  /* ── Totals ────────────────────────────────────────────────────────────── */

  /* Mirrors src/utils/pricing.js. SHIPPING_FEE and the tier thresholds are
     duplicated here for the same reason the tier table is — the theme can't
     import from the app. Change both in one commit. */
  var SHIPPING_FEE = 70;
  var TIER_FREE_SHIP = { bronze: 499, silver: 349, gold: 0, platinum: 0 };

  /* `total` is what the customer pays for the goods — cart.total_price, i.e.
     AFTER the coupon and any CNC Points — because that is the figure Shopify's
     checkout decides shipping on. Passing items_subtotal_price (before an
     order-level coupon) is what let the drawer say FREE on a ₹579 bag with
     WELCOME15 and checkout then charge ₹70 on ₹492.15. */
  function shippingFor(total, orderCount) {
    var threshold = TIER_FREE_SHIP[tierForOrders(orderCount).id];
    if (threshold === 0) return 0;              // Gold / Platinum: always free
    return total >= threshold ? 0 : SHIPPING_FEE;      // inclusive at the threshold
  }

  function freeShipAt(orderCount) {
    return TIER_FREE_SHIP[tierForOrders(orderCount).id];
  }

  /* Whose tier decides delivery. Shopify's own order count, printed by
     snippets/cnc-cart-drawer.liquid, comes first: it is the number the
     checkout's Silver/Gold free-shipping discounts are segmented on, and the
     one the Liquid pages (cnc-free-ship-at) use — so the drawer, the cart page
     and the checkout all read one figure. The App Proxy's totalOrders is the
     fallback for a page that didn't print it. */
  function shipOrders(points) {
    var s = window.CNC_SHIP;
    if (s && s.signedIn && typeof s.orders === "number") return s.orders;
    return points && points.totalOrders;
  }

  /* THE THEME'S FREE-SHIPPING BAR (snippets/minicart.liquid) — kept on the
     paid total.

     theme.js repaints <free-ship-progress-bar> after every add / change /
     remove by calling init(cart.items_subtotal_price): the total BEFORE an
     order coupon. That is how the drawer came to congratulate a customer on
     free shipping that checkout then charged for. theme.js is the host theme's
     minified bundle and is not edited; instead init() is wrapped once here so
     every repaint measures cart.total_price.

     The first paint (the constructor, fed from data-order) is already the
     paid total — the Liquid prints it — so it is painted straight away. Any
     other call re-reads /cart.js and paints from that. The amount the bar
     counts towards (data-fe-amount) is the customer's tier threshold, also
     printed by the Liquid; Gold and Platinum get an empty amount, which makes
     theme.js leave their full "free" bar alone. */
  function keepFreeShipBarOnPaidTotal() {
    if (!window.customElements || !customElements.whenDefined) return;
    customElements.whenDefined("free-ship-progress-bar").then(function () {
      var Bar = customElements.get("free-ship-progress-bar");
      var proto = Bar && Bar.prototype;
      if (!proto || typeof proto.init !== "function" || proto.__cncPaidTotal) return;
      proto.__cncPaidTotal = true;
      var themeInit = proto.init;

      /* Same words and the same rounding as the bill's delivery row. The
         theme's own line ("Spend ₹7 more to enjoy Free shipping!") rounded
         ₹6.85 up to ₹7 while the bill said ₹6.85 — two numbers for one gap. */
      proto.setProgressBarTitle = function (order, min) {
        var title = this.querySelector(".free-shipping-message");
        if (!title) return;
        title.classList.remove("opacity-0");
        var gap = Math.round((min - order) * 100) / 100;
        title.innerHTML = gap <= 0
          ? "You've unlocked <b>FREE</b> delivery"
          : "Add <b>" + money(gap) + "</b> more for <b>FREE</b> delivery";
      };
      proto.init = function (orders) {
        var bar = this;
        if (String(orders) === String(bar.dataset.order)) {
          return themeInit.call(bar, orders);
        }
        fetchCart(true).then(function (cart) {
          if (!cart || typeof cart.total_price !== "number") return;
          bar.dataset.order = String(cart.total_price);
          themeInit.call(bar, cart.total_price);
        });
      };
      // Repaint any bar that was upgraded before this ran, from a fresh cart.
      document.querySelectorAll("free-ship-progress-bar").forEach(function (bar) {
        fetchCart(true).then(function (cart) {
          if (!cart || typeof cart.total_price !== "number") return;
          bar.dataset.order = String(cart.total_price);
          themeInit.call(bar, cart.total_price);
        });
      });
    });
  }
  keepFreeShipBarOnPaidTotal();

  /* MRP is compare-at price, and /cart.js does not carry it — line items expose
     `original_price` (pre-discount) but never `compare_at_price`. So the only
     way to show "Total MRP" and "Discount on MRP" honestly is to fetch each
     distinct product and read the variant. Carts are small and this is cached
     for the page, so it's a handful of requests once. */
  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (SHARED.products[handle]) return SHARED.products[handle];
    SHARED.products[handle] = fetch("/products/" + handle + ".js", {
      headers: { Accept: "application/json" }
    })
      .then(function (r) { return r.ok ? readJsonBody(r) : null; })
      .catch(function () { return null; });
    return SHARED.products[handle];
  }

  /* Returns rupees. Falls back to the paid price when a product can't be
     fetched, so MRP is never *understated* — showing a discount that doesn't
     exist is worse than showing none. */
  function cartMrp(cart) {
    var items = (cart && cart.items) || [];
    if (!items.length) return Promise.resolve(0);
    return Promise.all(items.map(function (it) {
      if (!it.handle) return Promise.resolve(it.original_line_price / 100);
      return fetchProduct(it.handle).then(function (p) {
        var v = p && (p.variants || []).filter(function (x) {
          return x.id === it.variant_id;
        })[0];
        var unit = v && v.compare_at_price && v.compare_at_price > v.price
          ? v.compare_at_price
          : it.original_price;
        return (unit * it.quantity) / 100;
      });
    })).then(function (each) {
      return each.reduce(function (a, b) { return a + b; }, 0);
    });
  }

  function totalRow(label, value, cls) {
    return '<div class="cnc-total-row' + (cls ? " " + cls : "") + '">'
      + "<span>" + esc(label) + "</span><span>" + value + "</span></div>";
  }

  /* The breakdown deliberately omits a convenience fee — we don't charge one,
     and a ₹0 line for a fee that doesn't exist just plants the idea that it
     might. Delivery is the honest unknown instead: until someone signs in we
     can't know their tier, so quoting a number would be a guess we'd have to
     walk back at checkout. */
  function totalsHtml(cart, mrp, points) {
    var sub = cart.items_subtotal_price / 100;
    var paid = cart.total_price / 100;

    /* THE BASELINE IS THE LIST TOTAL, NOT THE SUBTOTAL.

       items_subtotal_price is already net of the coupon — Shopify allocates
       code and automatic discounts down onto the line items — so mrp - sub
       folded the coupon into "Discount on MRP", and the coupon row below then
       charged the same saving a second time. Two rows, one discount, and a
       breakdown that stopped adding up to the total it sits under.

       original_total_price is the lines at their selling price before any
       discount, so this is purely the MRP-vs-price gap. The fallback covers
       an older cart payload that omits the field. */
    var list = (cart.original_total_price != null
      ? cart.original_total_price
      : cart.items_subtotal_price + (cart.total_discount || 0)) / 100;
    var mrpOff = Math.max(0, mrp - list);

    /* SPLIT THE POINTS OUT OF THE DISCOUNT TOTAL.

       cart.total_discount is every code and every automatic discount fused
       into one figure, so once a points redemption is on the cart it lands
       inside "Coupon discount" — and a shopper reading that row sees a coupon
       that saved them more than the coupon says it does. The points value is
       known exactly (the server returned it) and is subtracted out here so
       both rows are individually true.

       Only when Shopify is actually honouring the code. An applied-but-
       inapplicable code discounts nothing, and crediting it here would show a
       saving the total does not contain. */
    var live = appliedPoints(cart);
    var ptsOff = live && live.applicable && points && points.redemption
      ? pointsToRupees(points.redemption.points)
      : 0;
    var couponOff = Math.max(0, (cart.total_discount || 0) / 100 - ptsOff);

    var known = signedIn();
    var orders = shipOrders(points);
    /* A guest is Bronze until they sign in, and Bronze carries the HIGHEST
       threshold on the ladder — so a guest bag already at or over it ships free
       whoever they turn out to be. "At checkout" there contradicted the
       "Free delivery unlocked" meter printed above it. Only a guest bag under
       the threshold is genuinely undecided (a Silver sign-in might still make
       it free), and only that case keeps the hedge. */
    var surelyFree = !known && paid >= TIER_FREE_SHIP.bronze;
    var shipKnown = known || surelyFree;
    var ship = known ? shippingFor(paid, orders) : (surelyFree ? 0 : null);
    var shipCell = !shipKnown
      ? '<span class="cnc-total-pending">At checkout</span>'
      : (ship === 0
          ? '<s class="cnc-total-was">' + money(SHIPPING_FEE) + '</s> <span class="cnc-total-free">FREE</span>'
          : money(ship));

    var grand = paid + (shipKnown ? ship : 0);

    /* "Estimated" was on this row in both states, and in neither state was it
       true in the way a shopper reads it. Every number here is already exact:
       the coupon is applied, the MRP discount is applied, the figure is what
       Shopify will charge. The ONLY unknown is delivery, and only while we
       don't know who this is — signed in, shippingFor() returns a real number
       and nothing about the total is estimated at all.

       So the hedge appears exactly where the uncertainty does. Signed out, the
       label names the missing piece rather than casting doubt over the whole
       figure — "estimated" invites a shopper to wonder whether their coupon
       really came off, which is the opposite of what this row is for. */
    var totalLabel = shipKnown ? "Total payable" : "Total excl. delivery";

    /* NAME THE CODE ON THE ROW IT DISCOUNTS.

       "Coupon discount −₹321" is a number with no provenance: a customer who
       has just typed a code cannot tell from it whether THEIR code is what
       came off, or whether an automatic bundle produced the same figure. The
       question that follows — "did my coupon work?" — is the one this whole
       row exists to answer, and it was answering it only by implication.

       appliedCode() reads /cart.js, which is the only place the code a
       customer actually typed is legible; the Liquid prints the discount's
       rule title instead, which is frequently something else entirely. */
    var code = appliedCode(cart);
    var couponLabel = code && couponOff
      ? "Coupon discount (" + code + ")"
      : "Coupon discount";

    return '<button type="button" class="cnc-total-head" data-cnc-total-toggle'
      + ' aria-expanded="false">'
      + '<span class="cnc-total-ic">' + ICON.receipt + "</span>"
      + "<span>" + totalLabel + "</span>"
      + '<span class="cnc-total-amt">' + money(grand) + "</span>"
      + '<span class="cnc-total-chev">' + ICON.chevD + "</span>"
      + "</button>"
      + '<div class="cnc-total-rows" data-cnc-total-rows hidden>'
      /* THE BILL READS TOP TO BOTTOM, THE WAY IT IS WORKED OUT.

         Delivery used to sit second, straight under Total MRP — so a shopper
         read "₹800 … Delivery ₹70" and asked why they were paying delivery on
         an ₹800 bag. Delivery is decided by what is left AFTER every discount,
         so it now comes after them, under an "Order value" line that shows
         that exact number. The row explains itself; the (i) explains the rule. */
      + totalRow("Total MRP", money(mrp))
      + totalRow("Discount on MRP", mrpOff ? "−" + money(mrpOff) : money(0), mrpOff ? "is-off" : "")
      /* Coupon and points rows only when there is something to show. */
      + (couponOff ? totalRow(couponLabel, "−" + money(couponOff), "is-off") : "")
      /* Only when there is something to show. A permanent "CNC Points ₹0" row
         advertises a feature at people who haven't used it, in the one place
         on the page that exists to answer "what am I paying" — and it is the
         row a customer scans for when they HAVE redeemed, so it should mean
         something every time it appears. The app's bill does the same. */
      + (ptsOff
          ? totalRow("CNC Points (" + points.redemption.points + " pts)", "−" + money(ptsOff), "is-off is-pts")
          : "")
      + totalRow("Order value", money(paid), "is-sub")
      + shipRowHtml(paid, known, ship, shipCell, orders, couponOff + ptsOff)
      + totalRow("Grand total", money(grand), "is-grand")
      + "</div>";
  }

  /* THE DELIVERY LINE — and why it costs what it costs.

     Three parts:
       1. the row — "Delivery fee (i)" and the amount (₹70, or ₹70 struck
          through and FREE);
       2. a one-line nudge right under it, only while a fee is being charged
          and free delivery is reachable: "Add ₹6.85 more for FREE delivery".
          That answers the question most people have without a tap;
       3. the working, behind the (i): order value, the threshold for their
          CNC Club tier, and the gap — the sum a customer would otherwise do
          in their head and get wrong.

     An inline disclosure, not a hover tooltip: most of this store's traffic
     is on phones, where hover doesn't exist, and a floating bubble inside a
     scrolling drawer is the component most likely to be clipped. It is
     rebuilt closed on every cart change, which is right — the numbers in it
     just changed. */
  function shipRowHtml(paid, known, ship, shipCell, orders, discOff) {
    var tier = tierForOrders(known ? orders : 0);
    var at = freeShipAt(known ? orders : 0);             // rupees; 0 = always free
    var gap = at > 0 ? Math.max(0, Math.round((at - paid) * 100) / 100) : 0;
    var perk = at === 0;

    var row = '<div class="cnc-total-row cnc-ship-row' + (gap > 0 ? " has-hint" : "") + '">'
      + '<span class="cnc-ship-lab">Delivery fee'
      + '<button type="button" class="cnc-ship-info" data-cnc-ship-info aria-expanded="false"'
      + ' aria-label="How is the delivery fee worked out?">' + ICON.info + "</button></span>"
      + "<span>" + shipCell + "</span></div>";

    /* DISCOUNTS CAN COST DELIVERY. A coupon or CNC Points come off before
       shipping is judged, so a ₹519 bag with ₹30 off lands at ₹489 and picks
       up ₹70 of delivery. When the bag would have shipped free without its
       discounts, the hint says what the rule is — one generic line that fits
       a coupon, points or both — instead of leaving the shopper to wonder why
       a bag over ₹499 is paying for delivery. */
    var discountsCaused = gap > 0 && discOff > 0 && paid + discOff >= at;

    var hint = "";
    if (discountsCaused) {
      hint = '<p class="cnc-ship-hint">Free delivery is on order value after discounts. Add <b>'
        + money(gap) + "</b> more to get <b>FREE</b> delivery.</p>";
    } else if (gap > 0) {
      hint = '<p class="cnc-ship-hint">Add <b>' + money(gap) + "</b> more for <b>FREE</b> delivery</p>";
    }

    var lines;
    if (perk) {
      lines = '<p>Delivery is always free for CNC ' + esc(tier.label) + " members. Enjoy!</p>";
    } else {
      lines = "<p>Free delivery is decided on your <b>order value</b> — what you pay for your items after MRP discounts, coupons and points.</p>"
        + '<div class="cnc-ship-calc">'
        + "<div><span>Your order value</span><span>" + money(paid) + "</span></div>"
        + "<div><span>Free delivery from" + (known ? " (CNC " + esc(tier.label) + ")" : "") + "</span><span>" + money(at) + "</span></div>"
        + (gap > 0
            ? '<div class="is-gap"><span>Add to get free delivery</span><span>' + money(gap) + "</span></div>"
            : '<div class="is-ok"><span>You qualify</span><span>FREE</span></div>')
        + "</div>"
        + (gap > 0 ? "<p>Below " + money(at) + ", delivery is " + money(SHIPPING_FEE) + ".</p>" : "")
        + "<p class=\"cnc-ship-tiers\">CNC Club: Bronze free from ₹499 · Silver from ₹349 · Gold &amp; Platinum always free"
        + (known ? "" : " — sign in to get your rate") + ".</p>";
    }

    return row + hint
      + '<div class="cnc-ship-note" data-cnc-ship-note hidden>' + lines + "</div>";
  }

  /* Rebuilt from scratch on every call. Patching in place would mean tracking
     which of the two coupon states is currently mounted, and that bookkeeping
     is exactly what went stale between renders. */
  /* ── The saved-for-later shelf (wishlist / recently-viewed) ─────────────── */

  /* Wishlist first; recently-viewed only as the fallback, so the section always
     has something rather than collapsing. Anything already in the cart is
     filtered out of both — recommending the item someone just added, under a
     heading inviting them to add it, reads as broken. */

  /* Last resort for wishlist entries saved before the handle was captured, or
     written by the host theme's own heart button. /products/<handle> is stable
     across locales because the theme's own product URLs are built from it. */
  function handleFromUrl(url) {
    var m = /\/products\/([^/?#]+)/.exec(String(url || ""));
    return m ? m[1] : "";
  }

  /* EVERY STRING AND EVERY SWITCH BELOW IS READ OFF THE MOUNT.

     The shelf is painted by this file but configured by whoever mounted it.
     The /cart section prints its theme-editor settings onto [data-cnc-rail] as
     data-rail-* attributes; the drawer's mount is injected by this same file,
     has no editor behind it, and simply gets the defaults.

     Read at paint time and never cached, because the cart page re-renders its
     whole section on every quantity change — by the next paint the element the
     config came from is a different node. */
  var RAIL_DEFAULTS = {
    source: "auto",            // auto | wish | viewed | off
    layout: "carousel",        // carousel | grid  (grid is desktop-only; see CSS)
    max: 12,
    title: "Saved for later",
    sub: "Still on your wishlist — move them over before you check out.",
    vtitle: "Recently viewed",
    vsub: "Picked up where you left off.",
    cta: "Move to bag",
    ctaMulti: "Choose options",
    allUrl: "/pages/wishlist",
    allLabel: "View all",
    showAll: true,
    showUnsave: true,
    showCount: true
  };

  var RAIL_ATTRS = {
    source: "source", layout: "layout", title: "title", sub: "sub",
    vtitle: "vtitle", vsub: "vsub", cta: "cta", ctaMulti: "cta-multi",
    allUrl: "all-url", allLabel: "all-label"
  };

  function railCfg(mount) {
    var cfg = {}, k;
    for (k in RAIL_DEFAULTS) {
      if (RAIL_DEFAULTS.hasOwnProperty(k)) cfg[k] = RAIL_DEFAULTS[k];
    }
    if (!mount || !mount.getAttribute) return cfg;

    for (k in RAIL_ATTRS) {
      if (!RAIL_ATTRS.hasOwnProperty(k)) continue;
      var v = mount.getAttribute("data-rail-" + RAIL_ATTRS[k]);
      /* An attribute printed as empty is a deliberate blank — "no subtitle",
         "no View all label" — so it has to beat the default. Only a MISSING
         attribute falls through, which is exactly the drawer's case. */
      if (v !== null) cfg[k] = v;
    }

    var n = parseInt(mount.getAttribute("data-rail-max"), 10);
    if (n > 0) cfg.max = n;

    function flag(name, dflt) {
      var v = mount.getAttribute("data-rail-" + name);
      return v === null ? dflt : (v !== "0" && v !== "false");
    }
    cfg.showAll = flag("show-all", cfg.showAll);
    cfg.showUnsave = flag("show-unsave", cfg.showUnsave);
    cfg.showCount = flag("show-count", cfg.showCount);
    return cfg;
  }

  function railItems(cart, cfg) {
    var inCart = {};
    ((cart && cart.items) || []).forEach(function (i) { inCart[String(i.product_id)] = 1; });

    var meta = readJson(WISH_META_KEY, {});
    var saved = readWish();
    /* Entries the meta can't render yet (saved by the host theme's own heart,
       or before the sidecar existed) are not "hidden", they're not knowable —
       backfillWishMeta() resolves them and repaints. They must not be counted
       as in-the-bag or the note would blame the cart for a race. */
    var known = saved.filter(function (id) { return meta[id]; });
    var wish = known
      .filter(function (id) { return !inCart[String(id)]; })
      .map(function (id) {
        var m = meta[id];
        return {
          id: id, title: m.title, url: m.url, img: m.img,
          price: m.price, compare: m.compare,
          variant: m.variant, nvariants: m.nvariants,
          /* The handle has to travel with the card. Without it the Choose
             button carries an empty data-cnc-rail-opts, fetchProduct("")
             resolves null, and the picker opens onto "Couldn't load options".
             Recently-viewed items pass through untouched and so always had it —
             only the wishlist path dropped it here. */
          handle: m.handle || handleFromUrl(m.url)
        };
      });

    /* THE COUNT HAS TO BE ABLE TO EXPLAIN ITSELF.

       Anything already in the bag is filtered out above — recommending the item
       someone just added, under a heading inviting them to add it, reads as
       broken. But the heading counts the WISHLIST, so a customer with two saved
       items and one of them in the bag sees "Saved for later · 2" over a single
       card, and the only available reading is that an item went missing.

       Neither number is wrong; what was missing is the sentence between them.
       So the filter now reports how many it took, and the header says so. */
    var wishData = { kind: "wish", items: wish, saved: saved.length, inBag: known.length - wish.length };

    /* "wish" is a promise the merchant made in the editor: this block is the
       wishlist and nothing else. Falling back to browsing history under a
       heading that says Saved for later would quietly break it. */
    if (cfg.source === "wish") return wishData;
    if (cfg.source !== "viewed" && wish.length) return wishData;

    var seen = readJson(VIEWED_KEY, []).filter(function (p) {
      return !inCart[String(p.id)];
    });
    /* No note on this branch. "Already in your bag" is a fact about a list the
       customer curated; browsing history is not a list they are keeping, so
       there is nothing for an omission to contradict. */
    return { kind: "viewed", items: seen, saved: seen.length, inBag: 0 };
  }

  /* The wishlist meta captured its image at width=200 — sized for the 120px
     thumbnail this block used to be. A shelf card now draws it at up to 260 CSS
     px, which on a 2× screen is a 520px request served from a 200px file.
     Rewritten at render time rather than re-captured, because the meta sits on
     the customer's own device and cannot be migrated. Mirrors cdnImg() in
     cnc-wishlist.js: if the URL shapes change, BOTH copies change. */
  function railImg(url, w) {
    var u = String(url || "");
    if (!u) return "";
    if (/[?&]width=\d+/.test(u)) return u.replace(/([?&]width=)\d+/, "$1" + w);
    var suffix = /_\d+x\d*(_crop_[a-z]+)?\.(jpg|jpeg|png|webp|gif)/i;
    if (suffix.test(u)) return u.replace(suffix, "_" + w + "x$1.$2");
    return u;
  }

  /* THE CARD IS THREE SIBLINGS, NEVER A NEST.

     media / body-link / button, flat. An <a> wrapping a <button> is invalid
     HTML and Safari stops delivering clicks to the inner control — the same
     trap the applied-coupon Remove button hit earlier.

     The picture and the title point at the same place, so the picture is
     aria-hidden and out of the tab order: otherwise every card in the row is
     two stops, the first of them unlabelled.

     Products with more than one variant get "Choose options" rather than an
     add: picking a size on the customer's behalf is a guess, and a wrong
     variant silently added to a cart is worse than one extra tap. */
  function railCardHtml(p, cfg, kind) {
    var off = p.compare && p.compare > p.price;
    var pct = discPct(p.price, p.compare);
    var single = (p.nvariants || 1) === 1 && p.variant;
    var url = esc(p.url || "#");
    var title = esc(p.title || "");

    var shot = "";
    if (p.img) {
      var mid = railImg(p.img, 400);
      var set = mid === p.img ? "" : ' srcset="' + esc(railImg(p.img, 200)) + " 200w, "
        + esc(mid) + " 400w, " + esc(railImg(p.img, 600)) + ' 600w"'
        + ' sizes="(min-width: 1000px) 260px, 45vw"';
      shot = '<img src="' + esc(mid) + '"' + set
        + ' alt="" loading="lazy" decoding="async" width="400" height="400">';
    }

    return '<article class="cnc-sh-card" data-sh-id="' + esc(String(p.id)) + '">'
      + '<div class="cnc-sh-media">'
      + '<a class="cnc-sh-shot" href="' + url + '" tabindex="-1" aria-hidden="true">'
      + shot + "</a>"
      /* One chip, top-left, and only when there is a saving. A badge on every
         card is a row of coloured blocks that stops meaning anything. */
      + (pct > 0 ? '<span class="cnc-sh-off">' + pct + "% off</span>" : "")
      /* Un-save is a filled heart, not an ×: the customer put it there with a
         heart, and the control that undoes an action should look like the one
         that did it. Only on the wishlist — there is nothing to un-save about
         a page you happened to look at. */
      + (cfg.showUnsave && kind === "wish"
          ? '<button type="button" class="cnc-sh-un" data-cnc-rail-un="' + esc(String(p.id)) + '"'
            + ' aria-label="Remove ' + title + ' from your wishlist">'
            + ICON.heart + "</button>"
          : "")
      + "</div>"
      + '<a class="cnc-sh-body" href="' + url + '">'
      + '<span class="cnc-sh-title">' + title + "</span>"
      /* Price, MRP and the saving on ONE line. On its own row the saving made
         discounted cards a line taller than the rest, so the buttons stopped
         lining up across the row — and a row whose controls don't align stops
         reading as a set of comparable things. */
      + '<span class="cnc-sh-price"><b>' + money((p.price || 0) / 100) + "</b>"
      + (off ? "<s>" + money(p.compare / 100) + "</s>" : "")
      + "</span></a>"
      /* data-sh-label is what the in-flight states write to. Setting
         textContent on the button itself would eat the icon and leave "Adding…"
         sitting where a labelled control used to be. */
      + (single
          ? '<button type="button" class="cnc-sh-cta" data-cnc-rail-add="' + p.variant
            + '" data-cnc-rail-id="' + esc(String(p.id)) + '">'
            + '<span class="cnc-sh-cta-ic" aria-hidden="true">' + ICON.bag + "</span>"
            + '<span data-sh-label>' + esc(cfg.cta) + "</span></button>"
          : '<button type="button" class="cnc-sh-cta cnc-sh-cta--opts" data-cnc-rail-opts="'
            + esc(p.handle || "") + '" data-cnc-rail-id="' + esc(String(p.id))
            + '" data-cnc-rail-url="' + esc(p.url || "") + '">'
            + '<span class="cnc-sh-cta-ic" aria-hidden="true">' + ICON.sliders + "</span>"
            + '<span data-sh-label>' + esc(cfg.ctaMulti) + "</span></button>")
      + "</article>";
  }

  function railHtml(cart, mount) {
    var cfg = railCfg(mount);
    if (cfg.source === "off") return "";

    var data = railItems(cart, cfg);
    if (!data.items.length) return "";

    var isWish = data.kind === "wish";
    var items = data.items.slice(0, cfg.max);
    var title = isWish ? cfg.title : cfg.vtitle;
    var sub = isWish ? cfg.sub : cfg.vsub;

    /* The count stays the SAVED total — it has to agree with the header's
       wishlist badge and with /pages/wishlist, and a drawer that quietly
       under-reports is a third number to reconcile, not a fix.

       But when cards are missing from under it, it says so: "1 of 2". A bare
       "2" over one card is a contradiction the customer can only resolve by
       assuming something broke. Hidden entirely at a total of 1, where a
       counter is only ever restating the row.

       Both causes fold into the one form. In the bag is the common one; the
       max-items cap is the other, and "12 of 20" is exactly as true. */
    var total = isWish ? data.saved : items.length;
    var countLabel = items.length < total ? items.length + " of " + total : String(total);

    /* The reason, in front of the merchant's line rather than after it.

       This clause is the variable and the subtitle is the constant, and the
       drawer clamps that line to one — so putting the constant first means the
       only sentence that explains the screen is the one that gets truncated
       away. Same rule as the product card: never set the constant louder than
       the variable. */
    var bagged = isWish && data.inBag > 0
      ? '<span class="cnc-sh-bagged">' + data.inBag + " already in your bag</span>"
      : "";

    return '<section class="cnc-sec cnc-rail-sec cnc-sh cnc-sh--'
      + (cfg.layout === "grid" ? "grid" : "rail") + '"'
      + ' data-sh-kind="' + (isWish ? "wish" : "viewed") + '">'

      + '<header class="cnc-sh-head">'
      + '<span class="cnc-sh-ic" aria-hidden="true">' + (isWish ? ICON.heart : ICON.clock) + "</span>"
      + '<div class="cnc-sh-lead">'
      + '<h2 class="cnc-sh-h">' + esc(title)
      + (cfg.showCount && total > 1
          ? '<span class="cnc-sh-count">' + countLabel + "</span>"
          : "")
      + "</h2>"
      /* The note stands on its own when the merchant has cleared the subtitle —
         it is the more important of the two lines, so it must not depend on
         the optional one being present. */
      + (sub || bagged
          ? '<p class="cnc-sh-sub">' + bagged + (bagged && sub ? " · " : "") + esc(sub) + "</p>"
          : "")
      + "</div>"
      /* Only on the wishlist branch: /pages/wishlist is not where browsing
         history lives, and a link that lands somewhere unrelated to the
         heading above it is worse than no link. */
      + (isWish && cfg.showAll && cfg.allUrl && cfg.allLabel
          ? '<a class="cnc-sh-all" href="' + esc(cfg.allUrl) + '">' + esc(cfg.allLabel)
            + '<span class="cnc-sh-all-ic" aria-hidden="true">' + ICON.chevR + "</span></a>"
          : "")
      + "</header>"

      + '<div class="cnc-rail-wrap">'
      + '<div class="cnc-rail cnc-sh-track" data-cnc-rail-track>'
      + items.map(function (p) { return railCardHtml(p, cfg, data.kind); }).join("")
      + "</div>"
      /* Touch users swipe and never need these, but on desktop a horizontal
         track with no affordance simply reads as a cut-off row — there's no
         hover-scrollbar on trackpads to hint otherwise. Hidden until the track
         actually overflows, so they never sit there doing nothing. */
      + '<button type="button" class="cnc-rail-nav cnc-rail-nav--prev"'
      + ' data-cnc-rail-nav="-1" aria-label="Scroll to previous products" hidden>'
      + ICON.chevL + "</button>"
      + '<button type="button" class="cnc-rail-nav cnc-rail-nav--next"'
      + ' data-cnc-rail-nav="1" aria-label="Scroll to more products" hidden>'
      + ICON.chevR + "</button>"
      + "</div></section>";
  }

  /* What the rail is currently a picture of: the wishlist, plus which products
     are in the cart (since anything in the cart is filtered out of it). If
     either moves, the rail on screen is out of date. */
  function railSignature() {
    var inCart = ((SHARED.cart && SHARED.cart.items) || [])
      .map(function (i) { return i.product_id; }).join(",");
    return readWish().join(",") + "|" + inCart;
  }

  /* Repaint the rail if — and only if — the thing it depicts has changed.

     The rail was painted once and then left: it still listed a product removed
     from the wishlist and omitted two that had been added. Hooking the handful
     of actions I could think of was the wrong shape, because the wishlist also
     changes from a heart on a product card, from another tab, and from the host
     theme's own buttons, which announce nothing at all.

     Comparing a signature instead means it doesn't matter who wrote or whether
     they told us. Same self-healing shape as the line-key guard. */
  /* querySelectorAll, not querySelector: open the minicart while standing on
     /cart and there are TWO mounts on the page, configured differently — the
     page's from the section's settings, the drawer's from the defaults. The
     single-element version repainted whichever happened to come first in the
     document and left the other showing a wishlist it had already been
     removed from. */
  function syncRail(force) {
    var mounts = [].slice.call(document.querySelectorAll("[data-cnc-rail]"))
      .filter(function (m) {
        return m.isConnected
          && (force || m.getAttribute("data-cnc-sig") !== railSignature());
      });
    if (!mounts.length) return;
    fetchCart().then(function (cart) {
      mounts.forEach(function (rail) {
        if (!rail.isConnected) return;
        rail.innerHTML = railHtml(cart, rail);
        paintRailNav(rail);
        rail.setAttribute("data-cnc-sig", railSignature());
      });
    });
  }

  function initRailSync() {
    if (!once(document.documentElement, "railSync")) return;

    // Our own writes announce themselves.
    document.addEventListener("cnc:wishlist-updated", function () { syncRail(true); });

    // Another tab.
    window.addEventListener("storage", function (e) {
      if (e.key === WISH_KEY || e.key === WISH_META_KEY) syncRail(true);
    });

    /* Everything else. The host theme's wishlist controls write straight to
       localStorage and emit nothing, so the only reliable signal is that
       someone touched one — then re-read and compare. */
    document.addEventListener("click", function (e) {
      if (!e.target.closest("[data-wish], .product-wishlist-js,"
        + " .btn-minicart__add-wishlist-js, .btn-minicart__remove-js,"
        + " .action-add-wishlist")) return;
      setTimeout(function () { syncRail(true); }, 700);
    });
  }

  /* Arrows reflect reality: hidden with nothing to scroll, disabled at each
     end. Called after every rail render and on scroll. */
  function paintRailNav(scope) {
    var track = scope.querySelector("[data-cnc-rail-track]");
    if (!track) return;
    var prev = scope.querySelector(".cnc-rail-nav--prev");
    var next = scope.querySelector(".cnc-rail-nav--next");
    var max = track.scrollWidth - track.clientWidth;
    var on = max > 4;
    if (prev) { prev.hidden = !on; prev.disabled = track.scrollLeft <= 2; }
    if (next) { next.hidden = !on; next.disabled = track.scrollLeft >= max - 2; }
  }

  /* The theme's "add to wishlist before remove?" writes straight to
     localStorage and re-renders the cart, and the order of those two is not
     ours to control. If our repaint lands first, the rail is built from a
     wishlist that doesn't yet contain the item — and nothing renders again, so
     it stays missing until the customer happens to touch the cart.

     Repainting shortly after the tap costs one extra pass and removes the race
     entirely. Bound on document because the prompt is re-rendered constantly. */
  function initThemeWishlistSync() {
    if (!once(document.documentElement, "wishSync")) return;
    document.addEventListener("click", function (e) {
      if (!e.target.closest(".btn-minicart__add-wishlist-js, .btn-minicart__remove-js")) return;
      setTimeout(function () {
        invalidateCart();
        try {
          enhanceDrawer();
          syncRail(true);
        } catch (err) {
          console.error("[cnc-cart] repaint after wishlist prompt failed", err);
        }
      }, 700);
    });
  }

  function initRailNav() {
    if (!once(document.documentElement, "railNav")) return;
    document.addEventListener("click", function (e) {
      var b = e.target.closest("button[data-cnc-rail-nav]");
      if (!b) return;
      e.preventDefault();
      var wrap = b.closest(".cnc-rail-wrap");
      var track = wrap && wrap.querySelector("[data-cnc-rail-track]");
      if (!track) return;
      // Just under a full page, so the card you were looking at stays partly
      // visible and the movement is legible rather than teleporting.
      var step = Math.max(140, track.clientWidth * 0.8);
      track.scrollBy({
        left: Number(b.getAttribute("data-cnc-rail-nav")) * step,
        behavior: reduceMotion ? "auto" : "smooth"
      });
    });
    document.addEventListener("scroll", function (e) {
      var t = e.target;
      if (t && t.nodeType === 1 && t.hasAttribute && t.hasAttribute("data-cnc-rail-track")) {
        paintRailNav(t.parentNode);
      }
    }, true);   // capture: scroll doesn't bubble

    /* Server-rendered rails (the PDP's "You may also like") were only ever
       painted on scroll, so their arrows started `hidden` and never appeared.
       Paint them once now, again on resize, and whenever the track changes
       size (images and fonts arrive after first paint). */
    function paintAll() {
      Array.prototype.forEach.call(document.querySelectorAll(".cnc-rail-wrap"), function (w) {
        paintRailNav(w);
      });
    }
    paintAll();
    window.addEventListener("resize", function () { window.requestAnimationFrame(paintAll); });
    window.addEventListener("load", paintAll);
    if ("ResizeObserver" in window) {
      var ro = new ResizeObserver(function () { paintAll(); });
      Array.prototype.forEach.call(document.querySelectorAll(".cnc-rail-wrap [data-cnc-rail-track]"), function (t) {
        ro.observe(t);
      });
    }
  }

  /* Adding from the rail moves the item: into the cart, out of the wishlist.
     Leaving it hearted would mean the next drawer open shows it in the rail
     again — and it's already in the cart, so railItems() filters it out anyway.
     That would read as the button having deleted the product. */
  function initRailAdd() {
    if (!once(document.documentElement, "cncRailAdd")) return;
    document.addEventListener("click", function (e) {
      /* Every selector in here is tag-qualified on purpose. A bare attribute
         selector matches any ancestor that happens to carry the attribute,
         including <html> — which is exactly how this handler once fired for
         the whole page.

         Un-save comes first because the heart sits INSIDE the card, over an
         anchor: reach the add branches first and the click has already been
         handed to the link. */
      var un = e.target.closest("button[data-cnc-rail-un]");
      if (un) {
        e.preventDefault();
        var uid = un.getAttribute("data-cnc-rail-un");
        var snapshot = readJson(WISH_META_KEY, {})[uid];
        var at = readWish().indexOf(String(uid));
        var name = (snapshot && snapshot.title) || "Item";
        dropFromWishlist(uid);
        toastAction(ICON.heart, esc(name) + " removed", "Undo", function () {
          restoreToWishlist(uid, snapshot, at);
        });
        return;
      }

      var opts = e.target.closest("button[data-cnc-rail-opts]");
      if (opts) {
        e.preventDefault();
        openVariantPicker(opts.getAttribute("data-cnc-rail-opts"),
                          opts.getAttribute("data-cnc-rail-id"),
                          opts.getAttribute("data-cnc-rail-url"));
        return;
      }
      var btn = e.target.closest("button[data-cnc-rail-add]");
      if (!btn) return;
      e.preventDefault();
      var variant = btn.getAttribute("data-cnc-rail-add");
      var id = btn.getAttribute("data-cnc-rail-id");
      btn.disabled = true;
      /* The label span, never the button: textContent on the button eats the
         icon, so "Adding…" would land where a labelled control used to be and
         the card would flicker a different width. */
      var lab = btn.querySelector("[data-sh-label]") || btn;
      lab.textContent = "Adding…";

      /* Drop it from the wishlist first, then let the theme add it.
         The order matters: the theme's render wipes and rebuilds the rail, and
         if the item were still hearted at that moment it would come straight
         back as a card for something now sitting in the cart. */
      dropFromWishlist(id);
      /* A button that says "Adding…" forever is worse than one that says it
         failed: the customer can't tell whether to wait or tap again. */
      themeAdd(variant).catch(function () {
        btn.disabled = false;
        lab.textContent = "Try again";
      });
    });
  }

  /* ── "Pick & Pack" — the storefront's one variant sheet ──────────────────
     Opened from anywhere a variant has to be chosen without a page load: the
     cart drawer's rail, the wishlist page's Move to bag, and whatever comes
     next. There used to be a second picker per surface; two pickers means a
     customer learns the control twice and we fix every bug twice.

     Shape: a bottom sheet on touch, a centred pad on desktop — one DOM, two
     placements, because on a phone the thumb is at the bottom of the screen
     and on a desktop the eye is in the middle.

     What it does that a plain option list does not:

       · Combination awareness. Values are enabled per GROUP against the
         current selection, so once you pick Blue the sizes that Blue is
         actually made in are the only ones left standing. Picking a value
         that strands another group repairs that group rather than leaving a
         selection that resolves to no variant at all — the failure mode that
         makes most option pickers add the wrong thing or nothing.
       · The stage image crossfades to the chosen variant's own photo, so a
         colour choice is confirmed by sight, not by a word.
       · The price morphs between values instead of cutting, which is what
         makes a £-difference between two sizes legible at a glance.

     Like the coupon sub-drawer, this lives on <body> and therefore sits
     OUTSIDE `.minicart__wrapper` — so it MUST swallow pointer events, or the
     host theme's close-on-outside-click reads every tap in it as a tap
     outside the cart and shuts the drawer underneath it. Same trap, same
     remedy.                                                                 */

  var PK_SEL = ".cnc-pk";

  /* One live sheet, one live selection. `sel` is per OPTION GROUP, parallel to
     product.options — not a variant id — because a variant is what a complete
     selection resolves to, and mid-choice there may not be one yet. */
  var PK = {
    product: null, sel: [], qty: 1, mode: "move",
    id: "", url: "", price: 0, opener: null, pre: ""
  };

  function pkOptCount(p) {
    var n = (p.options || []).length;
    if (n) return n;
    var v = (p.variants || [])[0];
    return (v && v.options && v.options.length) || 1;
  }

  /* Shopify's product.js has printed `options` as bare strings in one era and
     as {name, values} objects in another. Both still turn up in the wild. */
  function pkOptName(p, i) {
    var o = (p.options || [])[i];
    if (!o) return "Option";
    return (typeof o === "string" ? o : o.name) || "Option";
  }

  /* Values are derived from the VARIANTS rather than read off options[].values.
     They agree on a healthy product, and when they don't it's because a
     variant was deleted — in which case options[].values still lists a value
     that resolves to nothing, and offering it is a dead end. */
  function pkOptValues(p, i) {
    var out = [], seen = {};
    (p.variants || []).forEach(function (v) {
      var val = (v.options || [])[i];
      if (val == null || seen[val]) return;
      seen[val] = 1; out.push(String(val));
    });
    return out;
  }

  /* Every variant carrying `val` in group gi, ignoring what else is picked. */
  function pkAll(p, gi, val) {
    return (p.variants || []).filter(function (v) {
      return String((v.options || [])[gi]) === String(val);
    });
  }

  /* The same, narrowed to variants that also agree with every OTHER group's
     current selection. Group gi itself is excluded from the test — that's the
     group being offered, so constraining it against its own current value
     would report every alternative as impossible.

     The two together are what let a chip say three different things:
     unreachable right now, gone entirely, or ready. */
  function pkMatches(p, gi, val) {
    return pkAll(p, gi, val).filter(function (v) {
      var o = v.options || [];
      for (var i = 0; i < PK.sel.length; i++) {
        if (i === gi || !PK.sel[i]) continue;
        if (String(o[i]) !== String(PK.sel[i])) return false;
      }
      return true;
    });
  }
  function pkStocked(list) {
    return list.some(function (v) { return v.available; });
  }

  function pkVariant() {
    var p = PK.product;
    if (!p) return null;
    var hit = (p.variants || []).filter(function (v) {
      var o = v.options || [];
      for (var i = 0; i < PK.sel.length; i++) {
        if (String(o[i]) !== String(PK.sel[i])) return false;
      }
      return true;
    });
    return hit[0] || null;
  }

  /* A selection is only useful if it resolves to something buyable. After any
     pick, walk the OTHER groups and move any that now point at an impossible
     value onto their first in-stock alternative. Without this, picking a
     colour that a size doesn't come in silently leaves the sheet resolving to
     no variant and the CTA dead, with nothing on screen saying why. */
  function pkRepair(p, changed) {
    for (var gi = 0; gi < PK.sel.length; gi++) {
      if (gi === changed) continue;
      var cur = PK.sel[gi];
      var live = cur && pkMatches(p, gi, cur).some(function (v) { return v.available; });
      if (live) continue;
      var vals = pkOptValues(p, gi);
      var next = null, fallback = null;
      for (var k = 0; k < vals.length; k++) {
        var m = pkMatches(p, gi, vals[k]);
        if (!m.length) continue;
        if (!fallback) fallback = vals[k];
        if (m.some(function (v) { return v.available; })) { next = vals[k]; break; }
      }
      PK.sel[gi] = next || fallback || cur || vals[0] || "";
    }
  }

  function pkEnsure() {
    var el = document.querySelector(PK_SEL);
    if (el) return el;
    el = document.createElement("div");
    el.className = "cnc cnc-pk";
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-modal", "true");
    el.setAttribute("aria-label", "Choose options");
    el.innerHTML =
      '<div class="cnc-pk-scrim" data-pk-close></div>'
      + '<div class="cnc-pk-sheet" tabindex="-1">'
      + '<button type="button" class="cnc-pk-grab" data-pk-close aria-label="Close"></button>'
      + '<button type="button" class="cnc-pk-x" data-pk-close aria-label="Close">'
      + ICON.close + "</button>"

      + '<div class="cnc-pk-top">'
      + '<div class="cnc-pk-stage">'
      + '<img class="cnc-pk-shot" data-pk-shot-a alt="" width="240" height="240">'
      + '<img class="cnc-pk-shot cnc-pk-shot--b" data-pk-shot-b alt="" aria-hidden="true"'
      + ' width="240" height="240">'
      + '<span class="cnc-pk-off" data-pk-off hidden></span>'
      + "</div>"
      + '<div class="cnc-pk-id">'
      + '<p class="cnc-pk-brand">Chase N Chew</p>'
      + '<h4 class="cnc-pk-title" data-pk-title></h4>'
      + '<p class="cnc-pk-cost"><span class="cnc-pk-now" data-pk-price></span>'
      + '<s class="cnc-pk-was" data-pk-mrp hidden></s></p>'
      + '<p class="cnc-pk-stock" data-pk-stock hidden></p>'
      + '<div class="cnc-pk-qty" role="group" aria-label="Quantity">'
      + '<button type="button" data-pk-qty="-1" aria-label="Decrease quantity">−</button>'
      + '<span data-pk-qtyval aria-live="polite">1</span>'
      + '<button type="button" data-pk-qty="1" aria-label="Increase quantity">+</button>'
      + "</div></div></div>"

      + '<div class="cnc-pk-opts" data-pk-opts></div>'

      + '<div class="cnc-pk-foot">'
      + '<p class="cnc-pk-note" data-pk-note hidden></p>'
      + '<button type="button" class="cnc-pk-cta" data-pk-add disabled>'
      + '<span data-pk-cta-label>Move to bag</span>'
      + '<span class="cnc-pk-sep" data-pk-sep hidden aria-hidden="true"></span>'
      + '<span class="cnc-pk-ctaprice" data-pk-cta-price></span>'
      + "</button></div></div>";
    document.body.appendChild(el);

    ["pointerdown", "mousedown", "touchstart", "click"].forEach(function (type) {
      el.addEventListener(type, function (e) { e.stopPropagation(); });
    });

    el.addEventListener("click", function (e) {
      if (e.target.closest("[data-pk-close]")) { pkClose(); return; }

      var step = e.target.closest("[data-pk-qty]");
      if (step) {
        e.preventDefault();
        PK.qty = Math.max(1, PK.qty + (parseInt(step.getAttribute("data-pk-qty"), 10) || 0));
        el.querySelector("[data-pk-qtyval]").textContent = PK.qty;
        pkPaintCta();
        return;
      }

      var chip = e.target.closest("[data-pk-val]");
      if (chip && !chip.disabled) {
        e.preventDefault();
        var gi = parseInt(chip.getAttribute("data-pk-group"), 10) || 0;
        PK.sel[gi] = chip.getAttribute("data-pk-val");
        pkRepair(PK.product, gi);
        pkPaint();
        return;
      }

      var cta = e.target.closest("[data-pk-add]");
      if (cta && !cta.disabled) { e.preventDefault(); pkCommit(cta); }
    });

    el.addEventListener("keydown", function (e) {
      if (e.key === "Escape") { e.stopPropagation(); pkClose(); }
    });

    pkBindSwipe(el);
    return el;
  }

  /* Swipe-to-dismiss, bound to the grab handle and the head only — never the
     whole sheet. A drag listener over the option chips turns every tap that
     moves two pixels into a gesture, and picking a swatch stops working on
     exactly the devices this is for. */
  function pkBindSwipe(el) {
    var sheet = el.querySelector(".cnc-pk-sheet");
    var handles = [el.querySelector(".cnc-pk-grab"), el.querySelector(".cnc-pk-top")];
    var y0 = null;

    function down(e) {
      if (e.pointerType === "mouse") return;
      y0 = e.clientY;
      sheet.style.transition = "none";
    }
    function move(e) {
      if (y0 === null) return;
      var dy = Math.max(0, e.clientY - y0);
      sheet.style.transform = "translateY(" + dy + "px)";
    }
    function up(e) {
      if (y0 === null) return;
      var dy = Math.max(0, e.clientY - y0);
      y0 = null;
      sheet.style.transition = "";
      sheet.style.transform = "";
      if (dy > 90) pkClose();
    }

    handles.forEach(function (h) {
      if (!h) return;
      h.addEventListener("pointerdown", down);
    });
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
  }

  function pkClose() {
    var el = document.querySelector(PK_SEL);
    if (!el) return;
    el.classList.remove("is-open");
    document.documentElement.classList.remove("cnc-pk-open");
    if (PK.opener && PK.opener.isConnected) { try { PK.opener.focus(); } catch (e) { /* gone */ } }
    PK.opener = null;
  }

  /* Rupees in, rupees out — but counted rather than cut. A price that jumps
     between two sizes reads as a different page; a price that travels reads
     as the same product costing something else. */
  function pkMorph(el, fromPaise, toPaise) {
    if (!el) return;
    if (reduceMotion || fromPaise === toPaise || !window.requestAnimationFrame) {
      el.textContent = money(toPaise / 100);
      return;
    }
    var t0 = null, dur = 260;
    requestAnimationFrame(function step(t) {
      if (t0 === null) t0 = t;
      var k = Math.min(1, (t - t0) / dur);
      var e = 1 - Math.pow(1 - k, 3);
      el.textContent = money(Math.round(fromPaise + (toPaise - fromPaise) * e) / 100);
      if (k < 1) requestAnimationFrame(step);
    });
  }

  function pkShot(el, src) {
    if (!src) return;
    var a = el.querySelector("[data-pk-shot-a]");
    var b = el.querySelector("[data-pk-shot-b]");
    if (!a.getAttribute("src")) { a.setAttribute("src", src); return; }
    if (a.getAttribute("src") === src || b.getAttribute("src") === src) {
      /* Already the one on top — nothing to cross to. */
      if (a.getAttribute("src") === src) b.classList.remove("is-in");
      else b.classList.add("is-in");
      return;
    }
    /* Decode-gated: the incoming shot only becomes visible once it has
       actually loaded, so a cold cache can't paint a blank frame mid-fade. */
    var next = new Image();
    next.onload = function () {
      b.setAttribute("src", src);
      b.classList.add("is-in");
      setTimeout(function () {
        a.setAttribute("src", src);
        b.classList.remove("is-in");
      }, reduceMotion ? 0 : 300);
    };
    next.src = src;
  }

  function pkPaintCta() {
    var el = document.querySelector(PK_SEL);
    if (!el) return;
    var v = pkVariant();
    var cta = el.querySelector("[data-pk-add]");
    var label = el.querySelector("[data-pk-cta-label]");
    var priceEl = el.querySelector("[data-pk-cta-price]");
    var sep = el.querySelector("[data-pk-sep]");

    var buyable = !!(v && v.available);
    cta.disabled = !buyable;
    cta.classList.toggle("is-ready", buyable);
    label.textContent = !v ? "Unavailable"
      : !v.available ? "Sold out"
      : PK.mode === "add" ? "Add to bag" : "Move to bag";
    priceEl.textContent = buyable ? money((v.price * PK.qty) / 100) : "";
    sep.hidden = !buyable;
  }

  function pkPaint() {
    var el = document.querySelector(PK_SEL);
    var p = PK.product;
    if (!el || !p) return;
    var v = pkVariant();

    /* Three states, and the middle one is the whole reason this reads better
       than a plain option list:

         ready      buyable with what else is currently picked
         needs a    buyable, but not alongside the current other choices.
         swap       Still selectable — taking it repairs the other groups —
                    because a Large that only comes in Red is not "sold out",
                    it is one tap away, and greying it out is a dead end the
                    customer has no way to read their way out of.
         gone       no available variant anywhere. Struck through and inert:
                    that the size once existed is information, that it can be
                    bought is not true.

       A value with no variants behind it at all never existed — hidden, not
       disabled, because a permanently dead chip is furniture.               */
    el.querySelectorAll("[data-pk-val]").forEach(function (chip) {
      var gi = parseInt(chip.getAttribute("data-pk-group"), 10) || 0;
      var val = chip.getAttribute("data-pk-val");
      var exists = pkAll(p, gi, val).length > 0;
      var anywhere = pkStocked(pkAll(p, gi, val));
      var here = pkStocked(pkMatches(p, gi, val));
      var on = String(PK.sel[gi]) === String(val);

      chip.hidden = !exists;
      chip.disabled = !anywhere;
      chip.classList.toggle("is-on", on);
      chip.classList.toggle("is-out", exists && !anywhere);
      chip.classList.toggle("is-off", anywhere && !here && !on);
      chip.setAttribute("aria-checked", String(on));
      chip.title = !exists ? val
        : !anywhere ? val + " — sold out"
        : (!here && !on) ? val + " — changes your other choice"
        : val;
    });

    el.querySelectorAll("[data-pk-gval]").forEach(function (out) {
      var gi = parseInt(out.getAttribute("data-pk-gval"), 10) || 0;
      out.textContent = PK.sel[gi] || "—";
    });

    if (v) {
      var priceEl = el.querySelector("[data-pk-price]");
      pkMorph(priceEl, PK.price || v.price, v.price);
      PK.price = v.price;

      var mrp = el.querySelector("[data-pk-mrp]");
      var off = el.querySelector("[data-pk-off]");
      var hasOff = v.compare_at_price && v.compare_at_price > v.price;
      mrp.hidden = !hasOff;
      if (hasOff) mrp.textContent = money(v.compare_at_price / 100);
      off.hidden = !hasOff;
      if (hasOff) {
        off.textContent = discPct(v.price, v.compare_at_price) + "% OFF";
      }

      var stock = el.querySelector("[data-pk-stock]");
      var low = v.available && typeof v.inventory_quantity === "number"
        && v.inventory_management && v.inventory_quantity > 0 && v.inventory_quantity <= 5;
      stock.hidden = !low;
      if (low) stock.textContent = "Only " + v.inventory_quantity + " left";

      pkShot(el, (v.featured_image && v.featured_image.src) || (p.images || [])[0] || "");
    }

    pkPaintCta();
  }

  function pkCommit(cta) {
    var v = pkVariant();
    if (!v) return;
    cta.disabled = true;
    cta.classList.add("is-busy");
    var label = cta.querySelector("[data-pk-cta-label]");
    label.textContent = PK.mode === "add" ? "Adding…" : "Moving…";

    /* Out of the wishlist FIRST, then into the cart. The theme's re-render
       redraws anything reading the wishlist, and an item still hearted at that
       moment comes straight back as a card for something now in the bag. */
    if (PK.mode !== "add") dropFromWishlist(PK.id);

    themeAdd(v.id, PK.qty)
      .then(function () {
        document.dispatchEvent(new CustomEvent("cnc:added", {
          detail: { variant: v.id, product: PK.id, qty: PK.qty, mode: PK.mode }
        }));
        pkClose();
      })
      .catch(function () {
        cta.disabled = false;
        cta.classList.remove("is-busy");
        label.textContent = "Try again";
      });
  }

  /* opts: { handle, id, url, mode: "move" | "add", opener, preselect }

     `preselect` is a value in the FIRST option group — the one a grid card's
     swatch row previews. Tapping a colour on a card and then being handed a
     sheet that has forgotten which colour you tapped is the kind of small
     amnesia that makes a shortcut feel like a detour. */
  function pkOpen(opts) {
    opts = opts || {};
    var el = pkEnsure();
    PK.mode = opts.mode === "add" ? "add" : "move";
    PK.pre = opts.preselect || "";
    PK.id = String(opts.id || "");
    PK.url = opts.url || "";
    PK.qty = 1;
    PK.price = 0;
    PK.product = null;
    PK.sel = [];
    PK.opener = opts.opener || null;

    el.querySelector("[data-pk-title]").textContent = "Loading…";
    el.querySelector("[data-pk-price]").textContent = "";
    el.querySelector("[data-pk-mrp]").hidden = true;
    el.querySelector("[data-pk-off]").hidden = true;
    el.querySelector("[data-pk-stock]").hidden = true;
    el.querySelector("[data-pk-qtyval]").textContent = "1";
    el.querySelector("[data-pk-note]").hidden = PK.mode === "add";
    el.querySelector("[data-pk-note]").textContent =
      "Moving this to your bag takes it off your wishlist.";
    el.querySelector("[data-pk-opts]").innerHTML =
      '<div class="cnc-pk-skel"><span></span><span></span><span></span></div>';
    el.querySelector("[data-pk-add]").disabled = true;

    el.classList.add("is-open");
    document.documentElement.classList.add("cnc-pk-open");
    try { el.querySelector(".cnc-pk-sheet").focus({ preventScroll: true }); } catch (e) { /* old */ }

    fetchProduct(opts.handle).then(function (p) {
      if (!el.classList.contains("is-open")) return;
      if (!p || !(p.variants || []).length) {
        el.querySelector("[data-pk-title]").textContent = "Couldn’t load the options";
        el.querySelector("[data-pk-opts]").innerHTML =
          '<p class="cnc-pk-dead">We couldn’t reach this product’s options just now.'
          + (PK.url ? ' You can still open its page.' : "") + "</p>"
          + (PK.url ? '<a class="cnc-pk-out" href="' + esc(PK.url) + '">Open product page</a>' : "");
        return;
      }
      pkRender(el, p);
    });
    return el;
  }

  function pkRender(el, p) {
    PK.product = p;
    el.querySelector("[data-pk-title]").textContent = p.title || "";

    var groups = pkOptCount(p);

    /* Open on something real: the first in-stock variant, so the sheet is
       usable the moment it lands rather than after a compulsory tap. */
    var seed = (p.variants || []).filter(function (v) { return v.available; })[0]
      || (p.variants || [])[0];
    PK.sel = [];
    for (var i = 0; i < groups; i++) PK.sel[i] = String((seed.options || [])[i] || "");

    /* Then honour what the customer already told us on the card. Checked
       against the real values first: a stale `preselect` naming a value the
       merchant has since deleted must not strand the sheet on a selection
       that resolves to nothing. */
    if (PK.pre && pkOptValues(p, 0).indexOf(String(PK.pre)) > -1) {
      PK.sel[0] = String(PK.pre);
      pkRepair(p, 0);
    }

    var html = "";
    for (var gi = 0; gi < groups; gi++) {
      var name = pkOptName(p, gi);
      var vals = pkOptValues(p, gi);
      /* A lone "Title / Default Title" group is Shopify's placeholder for a
         product with no options at all. Printing it as a choice of one is
         noise, so it renders as nothing. */
      if (vals.length < 2 && /^title$/i.test(name)) continue;
      var isColour = /colou?r|shade|finish/i.test(name);

      html += '<div class="cnc-pk-group">'
        + '<p class="cnc-pk-glabel">' + esc(name)
        + '<b data-pk-gval="' + gi + '"></b></p>'
        + '<div class="cnc-pk-chips" role="radiogroup" aria-label="' + esc(name) + '">';

      html += vals.map(function (val, n) {
        var sw = isColour ? (colourFor(val) || "#DDD8CE") : colourFor(val);
        var thumb = "";
        if (!sw) {
          /* Not a colour word. If the variant carries its own photo, a chip
             with that photo beats a chip with a word — the customer is
             choosing between things they can see. */
          var owner = (p.variants || []).filter(function (v) {
            return String((v.options || [])[gi]) === String(val) && v.featured_image;
          })[0];
          if (owner && groups === 1) thumb = owner.featured_image.src;
        }
        return '<button type="button" role="radio" aria-checked="false"'
          + ' class="cnc-pk-chip' + (sw ? " is-colour" : "")
          + (thumb ? " is-thumb" : "") + '"'
          + ' style="--pk-i:' + n + (sw ? ";--sw:" + esc(sw) : "") + '"'
          + ' data-pk-group="' + gi + '" data-pk-val="' + esc(val) + '"'
          + ' title="' + esc(val) + '">'
          + (thumb ? '<img src="' + esc(thumb) + '" alt="" width="56" height="56">' : "")
          + (sw ? '<span class="cnc-sr">' + esc(val) + "</span>"
                : "<span>" + esc(val) + "</span>")
          + "</button>";
      }).join("");

      html += "</div></div>";
    }

    el.querySelector("[data-pk-opts]").innerHTML = html
      || '<p class="cnc-pk-dead">This product has a single option.</p>';
    pkPaint();
  }

  /* The drawer rail's original entry point, kept by name so its call site
     never had to learn about any of the above. */
  function openVariantPicker(handle, productId, fallbackUrl) {
    return pkOpen({ handle: handle, id: productId, url: fallbackUrl, mode: "move" });
  }

  /* Named CSS colours cover what a pet-toy catalogue actually ships. Anything
     unrecognised falls back to a text chip rather than guessing a colour and
     showing the customer the wrong one. */
  /* Paired with sw_map in cnc-product-card.liquid, value for value. The card
     resolves its swatches in Liquid and this sheet resolves them from
     /products/x.js, so the two never share code — only this list. Let them
     drift and the same colour is a dot on the card and a word in the sheet. */
  var SWATCHES = {
    red: "#D32F2F", blue: "#1E5FBF", green: "#2E7D32", yellow: "#F9C400",
    orange: "#EF6C00", purple: "#7B1FA2", pink: "#EC407A", brown: "#6D4C41",
    black: "#212121", white: "#FFFFFF", grey: "#9E9E9E", gray: "#9E9E9E",
    beige: "#D7CCC8", cream: "#F5EFE0", teal: "#00897B", navy: "#1A237E",
    maroon: "#7B1E1E", olive: "#6B7A34", mint: "#9FE0C4", lavender: "#C5B3E6",
    charcoal: "#36393D", tan: "#D2A679", ivory: "#FFFFF0", aqua: "#35C4D4",
    coral: "#FF7F62", mustard: "#D9A404", rust: "#B7410E",
    turquoise: "#1AAFA0", lilac: "#C8A2C8", peach: "#FFCBA4"
  };
  function colourFor(title) {
    var key = String(title || "").trim().toLowerCase();
    return SWATCHES[key] || null;
  }

  /* Shared by the rail's Add and the picker: an item on its way into the cart
     leaves the wishlist first, so the theme's re-render can't resurrect it. */
  function dropFromWishlist(id) {
    var list = readWish();
    var at = list.indexOf(String(id));
    if (at > -1) list.splice(at, 1);
    var meta = readJson(WISH_META_KEY, {});
    delete meta[id];
    writeJson(WISH_META_KEY, meta);
    writeWish(list);
    invalidateCart();
  }

  /* The mirror of dropFromWishlist, for Undo.

     Position matters: a wishlist is ordered, and an item that reappears at the
     end of the row after an undo has visibly not been restored — it has been
     re-added. Splicing it back at its old index is what makes the undo read as
     "that didn't happen" rather than as a second action. */
  function restoreToWishlist(id, snapshot, at) {
    var list = readWish();
    if (list.indexOf(String(id)) === -1) {
      list.splice(at > -1 && at <= list.length ? at : list.length, 0, String(id));
    }
    if (snapshot) {
      var meta = readJson(WISH_META_KEY, {});
      meta[id] = snapshot;
      writeJson(WISH_META_KEY, meta);
    }
    writeWish(list);
    invalidateCart();
  }

  /* Add, then render — both explicitly.

     This used to submit a throwaway <product-form> so the theme would do both.
     Measured: the add worked (200) but NOTHING re-rendered, because the theme's
     real forms carry a `section-id` input that tells product-form what markup
     to ask for. A hand-built form has none, so no section came back, the drawer
     never updated, and the rail's button sat on "Adding…" forever.

     Rather than reverse-engineer which hidden fields make someone else's
     handler behave, this does the two steps itself: POST /cart/add.js, then
     GET ?sections= and render. Both are documented Shopify endpoints, and the
     render path is the same one the stale-key guard uses. */
  function themeAdd(variantId, quantity) {
    var qty = parseInt(quantity, 10) || 1;
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty }] })
    })
      .then(function (r) { return cartJson(r, "Could not add"); })
      .then(function () {
        invalidateCart();
        return fetchSections(location.pathname, CART_SECTION);
      })
      .then(function (sections) {
        renderCartSection(sections);
        try { enhanceDrawer(); } catch (e) { /* observer will retry */ }
        /* An add from the wishlist rail changes the cart on a page that may be
           SHOWING the cart. Without this the /cart page kept the item list it
           was rendered with and the new line only appeared on a manual reload —
           the drawer updated, the page behind it did not. */
        emitCartChange();
      })
      .catch(function (err) {
        console.warn("[cnc-cart] rail add failed — " + ((err && err.message) || "unknown"), err);
        throw err;
      });
  }

  /* Two injected blocks, not one, because they answer different questions and
     belong to different surfaces:

       .cnc-offers  — "can I pay less?"  → floats above the grey totals band,
                      as its own card, inside .cart-footer
       .cnc-saved   — "what will I pay?" → stays inside the band with Subtotal

     While both lived in the band, the coupon row read as another line of the
     subtotal rather than as a thing you could act on. */
  function paintDrawerBlocks(footer) {
    /* Offers and the rail belong in the SCROLL container, not in .cart-footer.
       .cart-footer sits outside `.overflow-y-scroll`, so anything added there is
       pinned above the totals: it would eat fixed height, squeeze the item list
       into a sliver, and on a three-item cart the customer would be scrolling a
       120px window while a coupon card sat immovable below it. Only the totals
       and the buttons are meant to be fixed. */
    /* Scoped to the drawer this footer belongs to. The theme renders TWO
       .minicart elements — a small wrapper around the header's cart icon, and
       the real cart-notification drawer on <body> — so a document-wide query
       could hand back the header's scroller and inject the offers card into a
       node the customer never sees. */
    var root = footer.closest(".minicart") || document;
    var scroll = root.querySelector(".overflow-y-scroll, .custom-scrollbar")
      || footer.parentNode || footer;

    var offers = scroll.querySelector("[data-cnc-offers]");
    if (!offers) {
      offers = document.createElement("div");
      offers.className = "cnc cnc-offers";
      offers.setAttribute("data-cnc-offers", "");
      scroll.appendChild(offers);
    }

    var rail = scroll.querySelector("[data-cnc-rail]");
    if (!rail) {
      rail = document.createElement("div");
      rail.className = "cnc";
      rail.setAttribute("data-cnc-rail", "");
      scroll.appendChild(rail);
    }

    /* The theme prints its own subtotal and discount list. Both are replaced by
       the expandable breakdown, so they're hidden rather than removed — the
       theme's own scripts still query them, and deleting the nodes threw. */
    var totals = footer.querySelector("[data-cnc-totals]");
    if (!totals) {
      footer.querySelectorAll("[data-cart-summary], .subtotal").forEach(function (n) {
        n.style.display = "none";
      });
      totals = document.createElement("div");
      totals.className = "cnc cnc-totals";
      totals.setAttribute("data-cnc-totals", "");
      footer.insertBefore(totals, footer.firstChild);
    }

    /* THE HANDOFF LINE — drawer only.

       The drawer's Checkout button does not go to /cart, it goes straight to
       Shopify's hosted checkout. So for anyone who buys from the bag without
       ever opening the cart page — which is most people — the drawer IS the
       last surface this storefront controls, and the bridge built into
       cnc-cart.liquid never gets seen.

       This is the one-line version of that card. It is appended to the footer
       rather than folded into totalsHtml() on purpose: the /cart page paints
       the SAME [data-cnc-totals] host through the same paintBlocks(), so a
       line added there would render twice on the page that already has the
       full card.

       Text and an icon, no payment marks. The drawer is 380px wide and the
       marks need a rule and a row to read as anything but clutter at that
       size; the question they answer can wait for the page or for checkout
       itself. */
    if (!footer.querySelector("[data-cnc-paysafe]")) {
      var safe = document.createElement("div");
      safe.className = "cnc cnc-paysafe";
      safe.setAttribute("data-cnc-paysafe", "");
      safe.innerHTML = ICON.lock
        + "<span>Secure payment on the next page — UPI, cards, wallets &amp; net banking</span>";
      footer.appendChild(safe);
    }

    if (!offers.innerHTML) {
      offers.innerHTML = '<div class="cnc-panel">' + couponRowHtml("", null) + "</div>";
    }

    return paintBlocks(offers, rail, totals, footer);
  }

  /* THE PAINT PIPELINE, over any scope carrying the three mount points.

     Extracted from paintDrawerBlocks so the /cart page can reuse it. That page
     renders the same [data-cnc-offers] / [data-cnc-totals] / [data-cnc-rail]
     hooks in a different frame, and a second copy of this orchestration is
     precisely how the drawer and the page would start disagreeing about what
     an applied coupon looks like.

     Two changes from the version that lived inline:

       · the re-entrancy guard is PER SCOPE. It was one flag on SHARED, which
         was correct while the drawer was the only caller — with two, a drawer
         repaint already in flight silently swallowed the page's, and the page
         kept its server-rendered totals for good.
       · every mount point is optional. The drawer always creates all three;
         a page may not want a rail.                                          */
  function paintBlocks(offers, rail, totals, scope) {
    var host = scope || document.documentElement;

    /* COALESCE, DON'T DROP.

       This guard used to `return` when a paint was already in flight, which is
       correct for the case it was written for — four boots racing at page load,
       all painting the same unchanged cart — and quietly wrong for the case
       that matters. Applying a coupon fires two changes in quick succession
       (submitCode announces, then the background drawer refresh announces
       again). The first paint was still awaiting /cart.js when the second
       arrived, so the second was thrown away.

       The visible result was a page that looked *half* updated: the totals were
       right, because those came from the freshly swapped server HTML, while the
       offers block still showed the "Apply coupon" placeholder — the server
       always renders that, since the applied state is painted here. A dropped
       repaint reads exactly like a coupon that didn't apply.

       So a request arriving mid-flight sets a flag instead, and the in-flight
       paint re-runs itself once when it lands. Last state always wins, and
       there is still never more than one pass in the air. */
    if (host.__cncPainting) {
      host.__cncPaintAgain = true;
      return host.__cncPainting;
    }

    /* boot() runs once per script instance, so without a guard the same
       repaint fired four times in parallel — each issuing its own cart and
       product requests before any of them had populated a cache. */
    host.__cncPaintAgain = false;
    host.__cncPainting = fetchCart().then(function (cart) {
      if (!cart) return null;
      /* backfillWishMeta() is deliberately NOT in here. It walks the catalogue —
         up to 2MB — and putting it in the same Promise.all meant the totals, the
         coupon row and the buttons all waited on a sweep that exists only to
         populate a secondary rail. On a slow connection the whole footer sat
         empty until it finished, which looks exactly like a broken drawer.
         It runs alongside instead and fills the rail in when it lands. */
      return Promise.all([cart, fetchCoupons(), fetchPoints(), cartMrp(cart)]);
    }).then(function (res) {
      if (!res) return;
      var cart = res[0], points = res[2], mrp = res[3];
      // Before anything else: is the markup on screen still addressable?
      reconcileLineKeys(cart);

      /* Deliberately NOT awaited. Reconciling a drifted redemption takes two
         or three round trips, and holding the whole paint for it would leave
         the totals and the coupon row blank on every load for the sake of a
         case that almost never fires. It announces a cart change when it does
         anything, which brings this pipeline back round with the corrected
         cart — the same way applying a coupon does. */
      reconcilePoints(cart, points).then(function (changed) {
        if (changed) { invalidatePoints(); emitCartChange(); }
      });

      var code = appliedCode(cart);
      var known = res[1].filter(function (c) {
        return String(c.code || "").toUpperCase() === code.toUpperCase();
      })[0];
      // The panel may have been replaced again while those were in flight.
      if (offers && offers.isConnected) {
        offers.innerHTML = '<div class="cnc-panel' + (code || pendingCoupon() ? " cnc-panel--applied" : "") + '">'
          + couponRowHtml(code, known) + "</div>"
          + pointsCardHtml(points, cart, appliedPoints(cart));
      }
      if (rail && rail.isConnected) {
        rail.innerHTML = railHtml(cart, rail);
        paintRailNav(rail);
        rail.setAttribute("data-cnc-sig", railSignature());
      }
      if (totals && totals.isConnected) {
        totals.innerHTML = totalsHtml(cart, mrp, points);
        restoreTotalsState(totals);
      }

      /* After the paint, not before: announceCoupon opens the breakdown it has
         just written, and the toast should land on a surface already showing
         the number it is talking about. */
      announceCoupon(code, cart);

      // Second pass, off the critical path: once any missing wishlist meta has
      // been resolved, repaint just the rail. No-ops when nothing was missing.
      return backfillWishMeta().then(function () {
        if (!rail || !rail.isConnected) return;
        rail.innerHTML = railHtml(cart, rail);
        paintRailNav(rail);
        rail.setAttribute("data-cnc-sig", railSignature());
      });
    }).catch(function (err) {
      console.error("[cnc-cart] could not paint cart blocks", err);
    }).then(function () {
      host.__cncPainting = null;
      // Something asked for a repaint while this one was in the air.
      if (host.__cncPaintAgain) return paintBlocks(offers, rail, totals, scope);
    });

    return host.__cncPainting;
  }

  /* Expand/collapse the breakdown. The choice persists for the session, because
     someone who opened it once is checking a number and will want it open on
     the next render too — and the totals block is rebuilt on every cart change,
     so without this it would snap shut each time the quantity changed. */
  function initTotalsToggle() {
    if (!once(document.documentElement, "cncTotals")) return;
    document.addEventListener("click", function (e) {
      /* Delivery (i) — opens the working under the delivery row. Scoped to
         the nearest rows block, so the phone pay bar's cloned copy of the
         breakdown toggles its own note and not the summary card's. */
      var info = e.target.closest("[data-cnc-ship-info]");
      if (info) {
        var scope = info.closest(".cnc-total-rows") || document;
        var note = scope.querySelector("[data-cnc-ship-note]");
        if (!note) return;
        note.hidden = !note.hidden;
        info.setAttribute("aria-expanded", String(!note.hidden));
        return;
      }
      var btn = e.target.closest("[data-cnc-total-toggle]");
      if (!btn) return;
      var rows = btn.parentNode.querySelector("[data-cnc-total-rows]");
      if (!rows) return;
      var open = rows.hidden;
      rows.hidden = !open;
      btn.setAttribute("aria-expanded", String(open));
      try { sessionStorage.setItem("cnc:totals-open", open ? "1" : "0"); } catch (err) { /* private mode */ }
    });
  }

  function restoreTotalsState(scope) {
    var open;
    try { open = sessionStorage.getItem("cnc:totals-open") === "1"; } catch (e) { return; }
    if (!open) return;
    var rows = scope.querySelector("[data-cnc-total-rows]");
    var btn = scope.querySelector("[data-cnc-total-toggle]");
    if (rows) rows.hidden = false;
    if (btn) btn.setAttribute("aria-expanded", "true");
  }

  function enhanceDrawer() {
    var footer = document.querySelector(".minicart .drawer-bottom, .minicart .cart__ctas");
    if (!footer) return;                 // empty cart — the footer isn't rendered
    // Our own insertions would otherwise re-trigger observeDrawer().
    if (drawerObserver) drawerObserver.disconnect();
    try {
      unlockCheckout(footer);
      twoUpActions(footer);
      paintDrawerBlocks(footer);
    } finally {
      var panel = document.querySelector(DRAWER_PANEL_SEL);
      if (drawerObserver && panel) {
        drawerObserver.observe(panel, { childList: true, subtree: true });
      }
    }
  }

  /* ── Coupon sub-drawer ─────────────────────────────────────────────────── */

  /* Appended to <body>, not inside .minicart. The wrapper is transformed while
     it slides, and a transformed ancestor makes `position: fixed` resolve
     against it instead of the viewport — the panel ended up clipped to a strip
     down one edge. At body level the geometry is the viewport's, and a z-index
     one above the drawer's 15 still puts it over the cart. The cart stays
     mounted underneath either way, which is the point: Back must not cost you
     your scroll position. */
  /* Mirrors src/modals/CouponsModal.js: a white top card carrying the header
     and the manual-code field, floating over a recessed list area of gold-stub
     tickets, split into "Best coupon" and "More offers".

     The split is the part that matters. A flat list makes a customer compare
     six coupons themselves; naming the best eligible one does the arithmetic
     for them, which is the whole reason the app's screen is laid out this way. */
  function ensureCouponSheet() {
    var sheet = document.querySelector(SHEET_SEL);
    if (sheet) return sheet;
    sheet = document.createElement("div");
    sheet.className = "cnc cnc-coupon-sheet";
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "false");
    sheet.setAttribute("aria-label", "Apply coupon");
    sheet.innerHTML =
      '<div class="cnc-coupon-top">'
      /* No close button. Back already returns you to the cart, and the X was
         the only control here that carried data-cnc-close — which shut the cart
         and left this fixed overlay covering a closed drawer. One exit, and it
         goes where people expect. */
      + '<div class="cnc-coupon-head">'
      + '<button type="button" class="cnc-coupon-back" data-cnc-coupon-back'
      + ' aria-label="Back to cart">' + ICON.arrowL + "</button>"
      + "<div class=\"cnc-coupon-headtxt\"><h3>Apply coupon</h3>"
      + '<p data-cnc-coupon-cart>Your cart</p></div>'
      + "</div>"
      + '<form class="cnc-coupon-manual" data-cnc-coupon-form>'
      + '<div class="cnc-coupon-inputwrap">'
      + '<input type="text" name="code" placeholder="Enter coupon code"'
      + ' autocomplete="off" spellcheck="false" aria-label="Coupon code">'
      + '<button type="submit" class="cnc-coupon-applybtn">APPLY</button>'
      + "</div>"
      + '<p class="cnc-coupon-msg" data-cnc-coupon-msg hidden></p>'
      + "</form></div>"
      + '<div class="cnc-coupon-listarea" data-cnc-coupon-list>'
      + '<p class="cnc-coupon-empty">Loading offers…</p>'
      + "</div>";
    document.body.appendChild(sheet);

    /* NOTHING ESCAPES THIS ELEMENT.

       The sheet is a fixed overlay on <body>, which puts it outside
       `.minicart__wrapper` in the DOM. The host theme closes its drawer on any
       press outside that panel — so to Glozin, every tap on a coupon here is a
       tap outside the cart, and applying a code shut the whole drawer. Guarding
       our own listener never helped: the theme's listener is the one firing.
       Back only survived because its capture-phase stop happened to block the
       theme too.

       pointerdown and mousedown matter as much as click, because that's what
       the theme actually binds. Note stopPropagation, not
       stopImmediatePropagation — listeners on this same node still run, which
       is why the handlers below are attached HERE rather than on document.
       Move them back to document and they stop firing entirely. */
    ["pointerdown", "mousedown", "touchstart", "click"].forEach(function (type) {
      sheet.addEventListener(type, function (e) { e.stopPropagation(); });
    });

    sheet.addEventListener("click", function (e) {
      if (e.target.closest("[data-cnc-coupon-back]")) {
        e.preventDefault();
        closeCouponSheet();
        return;
      }
      var apply = e.target.closest("[data-cnc-coupon-apply]");
      if (apply) {
        e.preventDefault();
        submitCode(apply.getAttribute("data-cnc-coupon-apply"), apply);
        return;
      }
      if (e.target.closest("[data-cnc-coupon-remove]")) {
        e.preventDefault();
        /* Clear the status line FIRST. It is still holding "WELCOME15
           applied." from the apply that put this coupon on the cart, and
           leaving it there while removing the same coupon is a message that
           actively contradicts the button being pressed. */
        sheetMessage("", "");
        applyDiscount("").then(function () {
          invalidateCart();
          /* Same shape as submitCode: announce and repaint now, sync the
             drawer afterwards. Awaiting refreshCartDrawer() here meant a full
             page fetch had to finish before the ticket changed state. */
          emitCartChange();
          paintCouponSheet();
          refreshCartDrawer().catch(function (e2) {
            console.warn("[cnc-cart] background drawer sync failed", e2);
          });
        });
      }
    });

    sheet.addEventListener("submit", function (e) {
      var form = e.target.closest("[data-cnc-coupon-form]");
      if (!form) return;
      e.preventDefault();
      var input = form.querySelector("input[name='code']");
      submitCode((input.value || "").trim().toUpperCase(), form.querySelector("button"));
    });

    return sheet;
  }

  /* The big value on the ticket's left stub. */
  function stubInfo(c) {
    if (c.freeShip) return { big: "FREE", small: "SHIPPING" };
    if (c.pct > 0) return { big: c.pct + "%", small: "OFF" };
    if (c.flat > 0) return { big: "₹" + c.flat, small: "OFF" };
    return { big: "DEAL", small: "" };
  }

  /* Same arithmetic as calcSaving() in CouponsModal — including the
     maxDiscount cap, without which a percentage coupon advertises a saving
     the checkout won't honour. */
  function calcSaving(c, subtotal) {
    if (c.freeShip) return SHIPPING_FEE;
    if (c.flat > 0) return c.flat;
    if (c.pct > 0) {
      var raw = Math.round(subtotal * (c.pct / 100));
      return c.maxDiscount > 0 ? Math.min(raw, c.maxDiscount) : raw;
    }
    return 0;
  }

  /* One ticket: gold stub, perforation, body. The stub width is shared with the
     CSS through --stub so the dashed line and the two notches that "cut" the
     edge stay registered to it — they're absolutely positioned against the same
     number, and the illusion breaks the moment they disagree. */
  function ticketHtml(d, appliedUpper) {
    var c = d.c;
    var stub = stubInfo(c);
    var mine = c.code && String(c.code).toUpperCase() === appliedUpper;
    var dim = !d.eligible && !mine;

    var action = mine
      ? '<button type="button" class="cnc-tk-act is-remove" data-cnc-coupon-remove>REMOVE</button>'
      : '<button type="button" class="cnc-tk-act" data-cnc-coupon-apply="' + esc(c.code || "")
        + '"' + (d.eligible ? "" : " disabled") + ">APPLY</button>";

    var strip;
    if (mine) {
      strip = '<p class="cnc-tk-save">' + ICON.check + " You saved <b>"
        + money(d.saving) + "</b> with this coupon!</p>";
    } else if (d.eligible && d.saving > 0) {
      strip = '<p class="cnc-tk-save">' + ICON.tag + " Save <b>"
        + money(d.saving) + "</b> instantly with this coupon!</p>";
    } else if (d.blockedBy) {
      strip = '<p class="cnc-tk-short">Can’t be combined with the <b>'
        + esc(d.blockedBy) + "</b> already on this cart.</p>";
    } else if (c.minQty && d.qtyShort > 0) {
      strip = '<p class="cnc-tk-short">Add <b>' + d.qtyShort + " more item"
        + (d.qtyShort !== 1 ? "s" : "") + "</b> to apply this coupon.</p>";
    } else if (d.orderShort > 0) {
      strip = '<p class="cnc-tk-short">Shop for <b>' + money(d.orderShort)
        + "</b> more to apply this coupon.</p>";
    } else if (d.empty) {
      /* An empty bag used to fall into the branch above and print "Shop for ₹0
         more to apply this coupon" — an instruction to spend nothing, on every
         ticket, which is what a catch-all `else` gets you when one of the
         numbers it formats is allowed to be zero. */
      strip = '<p class="cnc-tk-short">Add something to your bag to use this coupon.</p>';
    } else {
      strip = "";
    }

    return '<div class="cnc-tk' + (dim ? " is-dim" : "") + (mine ? " is-applied" : "") + '">'
      + '<div class="cnc-tk-inner">'
      + '<div class="cnc-tk-stub"><span class="cnc-tk-big">' + esc(stub.big) + "</span>"
      + (stub.small ? '<span class="cnc-tk-small">' + esc(stub.small) + "</span>" : "")
      + "</div>"
      + '<div class="cnc-tk-body">'
      + '<div class="cnc-tk-top"><h4>' + esc(c.label || couponBenefit(c)) + "</h4>"
      + action + "</div>"
      + (c.desc ? '<p class="cnc-tk-desc">' + esc(c.desc) + "</p>"
                : '<p class="cnc-tk-desc">' + esc(couponCondition(c)) + "</p>")
      + '<div class="cnc-tk-coderow">'
      + (c.code ? '<span class="cnc-tk-code">' + esc(c.code) + "</span>" : "")
      + (c.minOrder ? '<span class="cnc-tk-min">Min ' + money(c.minOrder) + "</span>" : "")
      + "</div>"
      + '<div class="cnc-tk-rule"></div>'
      + strip
      + "</div></div>"
      + '<span class="cnc-tk-perf"></span>'
      + '<span class="cnc-tk-notch cnc-tk-notch--t"></span>'
      + '<span class="cnc-tk-notch cnc-tk-notch--b"></span>'
      + "</div>";
  }

  function paintCouponSheet() {
    var sheet = ensureCouponSheet();
    var list = sheet.querySelector("[data-cnc-coupon-list]");
    Promise.all([fetchCoupons(), fetchCart()]).then(function (res) {
      var coupons = res[0], cart = res[1];
      var subtotal = cart ? cart.items_subtotal_price / 100 : 0;
      var qty = cart ? cart.item_count : 0;
      var appliedUpper = appliedCode(cart).toUpperCase();

      var cartLine = sheet.querySelector("[data-cnc-coupon-cart]");
      if (cartLine) cartLine.textContent = "Your cart: " + money(subtotal);

      // Auto-applied bundles aren't pickable, so they don't belong in a list of
      // things to pick.
      var pickable = coupons.filter(function (c) { return !c.type || c.type === "code"; });
      if (!pickable.length) {
        list.innerHTML = '<p class="cnc-coupon-empty">No offers are running right now.'
          + " If you have a code, enter it above.</p>";
        return;
      }

      /* Automatic discounts already on the cart. Most Shopify codes are
         configured not to stack with them, and a code that Shopify will refuse
         must not be offered as APPLY — showing it live and then rejecting the
         tap is the contradiction that made SAVE100 confusing. */
      var autoTitles = autoDiscountTitles(cart);

      var decorated = pickable.map(function (c) {
        var qtyShort = (c.minQty || 0) - qty;
        var orderShort = Math.ceil((c.minOrder || 0) - subtotal);
        var meetsMin = subtotal >= (c.minOrder || 0) && qty >= (c.minQty || 0);
        /* combinesWith.product is the precise answer and comes from Shopify;
           `combinable` is the older, coarser flag. Falling back to it keeps
           this correct against an older cnc-api deploy. */
        var stacks = c.combinesWith
          ? !!c.combinesWith.product
          : c.combinable !== false;
        var blocked = autoTitles.length > 0 && !stacks;
        return {
          c: c,
          eligible: meetsMin && !blocked,
          blockedBy: blocked ? autoTitles[0] : "",
          saving: calcSaving(c, subtotal),
          qtyShort: qtyShort > 0 ? qtyShort : 0,
          orderShort: orderShort > 0 ? orderShort : 0,
          empty: qty === 0
        };
      });

      var eligible = decorated.filter(function (d) { return d.eligible; })
        .sort(function (a, b) { return b.saving - a.saving; });
      var best = eligible[0] || null;
      var more = decorated.filter(function (d) { return d !== best; })
        .sort(function (a, b) {
          return (b.eligible - a.eligible) || (b.saving - a.saving);
        });

      var html = "";
      if (best) {
        html += '<p class="cnc-coupon-seclabel">Best coupon</p>'
          + ticketHtml(best, appliedUpper);
      }
      if (more.length) {
        html += '<p class="cnc-coupon-seclabel">More offers</p>'
          + more.map(function (d) { return ticketHtml(d, appliedUpper); }).join("");
      }
      list.innerHTML = html;
    });
  }

  /* The scrim is a SIBLING of the sheet, never a child: the sheet stops every
     event before it escapes (see ensureCouponSheet), so a scrim inside it
     could not report the click that is its entire job. It exists for the
     cart page, where the sheet is a 480px panel with the live page still
     visible beside it and nothing to say that page is currently inert. In
     the drawer the theme's own overlay is already there; a second one at
     this opacity is not worth a special case. */
  function ensureCouponScrim() {
    var scrim = document.querySelector(".cnc-coupon-scrim");
    if (scrim) return scrim;
    scrim = document.createElement("div");
    scrim.className = "cnc-coupon-scrim";
    /* Not a button and not focusable. It duplicates the Back arrow, which is
       the real, labelled control — a keyboard user has that and Escape. */
    scrim.setAttribute("aria-hidden", "true");
    document.body.insertBefore(scrim, document.querySelector(SHEET_SEL));
    return scrim;
  }

  function openCouponSheet() {
    var sheet = ensureCouponSheet();
    ensureCouponScrim().classList.add("is-open");

    /* THE SHEET OPENS CLEAN, EVERY TIME.

       The status line and the code field are the record of ONE attempt, and
       the sheet is not destroyed when it closes — it stays mounted so Back
       keeps its scroll position. So both survived into the next visit.

       Reported from the live store: apply WELCOME15, remove it, reopen the
       sheet — and the header reads "Your cart: ₹878" with "WELCOME15
       applied." underneath it. Two true statements about different moments,
       stacked, describing a coupon that is no longer on the cart. The ticket
       state was right the whole time; the leftover sentence was the only
       thing saying otherwise, and it is the sentence people read first.

       Cleared on open rather than on close because close has three exits
       (Back, Escape, the scrim) and only one of them would ever have been
       remembered. */
    sheetMessage("", "");
    var input = sheet.querySelector("input[name='code']");
    if (input) input.value = "";

    paintCouponSheet();
    sheet.classList.add("is-open");
    if (input && window.innerWidth >= 768) input.focus();
  }

  function closeCouponSheet() {
    var sheet = document.querySelector(SHEET_SEL);
    if (sheet) sheet.classList.remove("is-open");
    var scrim = document.querySelector(".cnc-coupon-scrim");
    if (scrim) scrim.classList.remove("is-open");
  }

  function sheetMessage(kind, text) {
    var box = document.querySelector("[data-cnc-coupon-msg]");
    if (!box) return;
    box.hidden = !text;
    box.setAttribute("data-kind", kind);
    box.textContent = text || "";
  }

  /* APPLYING A CODE MUST FEEL INSTANT.

     This used to await refreshCartDrawer() before doing anything visible, and
     that call re-fetches the ENTIRE page HTML. On the cart page — a long
     document — the sheet sat there saying "Checking…" for a second or more
     after Shopify had already accepted the code, and on a slow connection it
     read as a hang. Two round trips had to finish before a single pixel moved.

     The order is now: tell the customer, tell the page, close the sheet — then
     re-sync the drawer in the background. applyDiscount() has already returned
     the authoritative cart, so nothing shown in that first frame is a guess;
     the drawer refresh is housekeeping for a surface nobody is looking at. */
  function submitCode(code, btn) {
    if (!code) return;
    sheetMessage("ok", "Checking " + code + "…");
    if (btn) btn.disabled = true;
    applyDiscount(code)
      .then(function (res) {
        if (!res.ok) { sheetMessage("error", res.reason); return; }
        sheetMessage("ok", code + " applied.");
        /* The typed code has been consumed. Leaving it in the field means the
           next visit opens with a code already sitting there next to APPLY,
           which reads as pending rather than done. */
        var field = document.querySelector(SHEET_SEL + " input[name='code']");
        if (field) field.value = "";
        emitCartChange();                       // the page repaints now
        paintCouponSheet();
        setTimeout(closeCouponSheet, 550);
        refreshCartDrawer().catch(function (e) {
          console.warn("[cnc-cart] background drawer sync failed", e);
        });
      })
      .catch(function (e) {
        /* Shopify's own refusal is worth reading — "this code isn't valid",
           "not combinable" — so it is shown as it came. Anything else is a
           transport failure whose message is written for a developer, and a
           stack-shaped string in red under the coupon field reads as a broken
           shop rather than a bad moment. It goes to the console, where it is
           useful, and the customer gets a sentence they can act on. */
        if (e && e.userMessage) { sheetMessage("error", e.userMessage); return; }
        console.warn("[cnc-cart] applying " + code + " failed —", (e && e.message) || e);
        sheetMessage("error", "Couldn’t reach the cart just then. Please try again.");
      })
      .then(function () { if (btn) btn.disabled = false; });
  }

  /* ── The redeem card's three controls ──────────────────────────────────────

     Delegated on document, like the coupon row, because the card is rebuilt
     from scratch on every paint — in the drawer, on the cart page, and again
     after each cart change. Anything bound to a node inside it would be gone
     by the second render.

     TWO ROUND TRIPS PER CLICK, IN A FIXED ORDER: ask the server to spend the
     points, then put the code it minted on the cart. Reversed, a cart could
     carry a code for points nobody had reserved. If the second step fails the
     hold still exists, and reconcilePoints() re-attaches it on the next paint
     rather than stranding a spent balance.

     The button is disabled and relabelled for the duration. This is a money
     operation on a shared balance, and a second click firing while the first
     is in flight would mint a second code and release the first — churning
     discounts in the Shopify admin for a shopper who just clicked twice. */
  function pointsBusy(btn, label) {
    if (!btn) return;
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
    if (label) btn.textContent = label;
  }

  /* Failures speak on the card itself, not in an alert. The message replaces
     the balance line — the thing it is explaining — and clears on the next
     paint, which is the moment it stops being true. */
  function pointsError(scope, msg) {
    var sub = scope && scope.querySelector(".cnc-pts-sub");
    if (sub) { sub.textContent = msg; sub.classList.add("is-error"); }
    else if (msg) console.warn("[cnc-points] " + msg);
  }

  function initPointsControls() {
    if (!once(document.documentElement, "cncPointsCtl")) return;

    document.addEventListener("click", function (e) {
      var info = e.target.closest("[data-cnc-pts-info]");
      if (info) {
        e.preventDefault();
        var panel = info.closest(".cnc-panel");
        var note = panel && panel.querySelector("[data-cnc-pts-note]");
        if (!note) return;
        var open = note.hidden;
        note.hidden = !open;
        info.setAttribute("aria-expanded", String(open));
        return;
      }

      var use = e.target.closest("[data-cnc-pts-use]");
      if (use) {
        e.preventDefault();
        var card = use.closest(".cnc-panel");
        var want = parseInt(use.getAttribute("data-cnc-pts-use"), 10) || 0;
        pointsBusy(use, "Applying…");
        fetchCart().then(function (cart) {
          /* Priced against what the cart is charging RIGHT NOW, read fresh
             rather than taken from the figure this card was painted with —
             which may be a quantity change old. The server binds the minted
             code to this number, so a stale one produces a code Shopify
             refuses the moment it is applied. */
          var goods = cart ? cart.total_price / 100 : 0;
          return redeemPoints(want, goods).then(function (d) {
            return applyPointsCode(d.redemption.code);
          });
        }).then(function () {
          SHARED.ptsSig = "";                 // the state changed under the guard
          emitCartChange();
          return refreshCartDrawer();
        }).catch(function (err) {
          pointsError(card, (err && err.message) || "Could not apply your points.");
          use.disabled = false;
          use.removeAttribute("aria-busy");
        });
        return;
      }

      var drop = e.target.closest("[data-cnc-pts-remove]");
      if (drop) {
        e.preventDefault();
        pointsBusy(drop, "Removing…");
        /* Code off the cart FIRST, then the refund. The other order leaves a
           window where the points are back in the balance and a live code can
           still spend them — which is the one mistake here that costs money
           rather than a repaint. */
        clearPointsCode()
          .then(function () { return releasePoints(""); })
          .then(function () {
            SHARED.ptsSig = "";
            emitCartChange();
            return refreshCartDrawer();
          })
          .catch(function () {
            invalidatePoints();
            emitCartChange();
          });
      }
    });
  }

  function initCouponControls() {
    if (!once(document.documentElement, "cncCouponCtl")) return;

    /* Only the opener lives on document — it's the row in the cart drawer, not
       in the sheet. Everything inside the sheet is bound to the sheet itself in
       ensureCouponSheet(), because those events are stopped before they reach
       document and would never arrive here.

       The Remove control appears in BOTH places: on the coupon row in the cart
       drawer and on an applied ticket in the sheet. This one serves the drawer. */
    document.addEventListener("click", function (e) {
      if (e.target.closest("[data-cnc-coupon-open]")) { openCouponSheet(); return; }
      if (e.target.closest("[data-cnc-coupon-remove]")) {
        applyDiscount("").then(function () {
          invalidateCart();
          emitCartChange();               // same reasoning as submitCode
          return refreshCartDrawer();
        });
      }
    });

    /* Warm the coupon list on INTENT rather than on the click.

       fetchCoupons() caches for the page, but on a surface that never painted
       the offers block the first fetch started when the sheet opened — so the
       sheet's whole first second was "Loading offers…" against a third-party
       endpoint. pointerdown fires ~100-150ms before click on touch, and hover
       far earlier than that on a mouse; either is enough to hide the request
       entirely. Passive, idempotent, and free when the list is already in. */
    ["pointerdown", "pointerenter", "focusin"].forEach(function (type) {
      document.addEventListener(type, function (e) {
        var t = e.target;
        if (t && t.closest && t.closest("[data-cnc-coupon-open]")) fetchCoupons();
      }, { passive: true, capture: type === "pointerenter" });
    });
  }

  /* ── Boot ──────────────────────────────────────────────────────────────── */
  /* Six <script src="cnc-app.js"> tags means six boots within a few ms of each
     other. The once() guards make the widget wiring idempotent, but they run
     after the work has already been queued — so the drawer still kicked off six
     parallel repaints before any cache could fill.

     Collapsing them costs one timestamp. 50ms is far below the gap to a real
     shopify:section:load in the theme editor, so genuine re-boots still run. */
  var BOOT_COALESCE_MS = 50;

  function boot() {
    var now = Date.now();
    if (window.__cncBootAt && now - window.__cncBootAt < BOOT_COALESCE_MS) return;
    window.__cncBootAt = now;
    try {
      bootWidgets();
    } catch (err) {
      // Same reasoning as refreshCartDrawer: one broken widget must not take
      // the rest of the page's behaviour down with it.
      console.error("[cnc] boot failed", err);
    }
  }

  /* ── Product card hover state ───────────────────────────────────────────
     Two jobs, both delegated from document so cards rendered later (rail
     re-renders, theme-editor reloads, paginated grids) need no re-binding.

       1. Carousel dots swap the card's image. Only the first shot ships as an
          <img>; the rest are URLs on the dots, preloaded the first time a card
          is hovered so the swap doesn't flash. Preloading all of them up front
          would multiply image requests across a 50-card grid for shots almost
          nobody looks at.

       2. Add to bag confirms itself. The add is the host theme's — its
          <product-form> intercepts the submit and does the AJAX — so what's
          left here is the label. It flips on submit rather than on a cart
          event because the theme emits none we can trust, then reverts, so a
          failed add can't leave a button permanently claiming success.        */

  var SHOT_MS = 1400;   /* slow enough to read a shot, quick enough to see two */

  /* Double-buffered shots. The Liquid <img> (with its responsive srcset) is
     never touched — it always holds the main shot. Shots 2..n paint on a
     cloned overlay that fades in above it, so:
       · the base keeps srcset/sizes intact (mutating src alone used to fight
         srcset and strip the card's responsive images for good);
       · the swap is decode-gated — the overlay's src is only set after the
         candidate has loaded, so a cold cache can never paint a blank frame;
       · rewinding to the main shot is a fade-out of the overlay, not a cut. */
  function shotLayers(card) {
    var box = card.querySelector(".cnc-card-img");
    var base = box && box.querySelector("img");
    if (!base) return null;
    if (!card.__shotB) {
      var b = base.cloneNode(false);
      b.removeAttribute("srcset");
      b.removeAttribute("sizes");
      b.removeAttribute("loading");
      b.alt = "";
      b.setAttribute("aria-hidden", "true");
      b.className = "cnc-shot-b";
      box.insertBefore(b, base.nextSibling);
      card.__shotB = b;
    }
    return card.__shotB;
  }

  function showShot(card, i) {
    var dots = Array.prototype.slice.call(card.querySelectorAll("[data-cnc-shot]"));
    if (!dots.length) return;
    var overlay = shotLayers(card);
    if (!overlay) return;

    var n = ((i % dots.length) + dots.length) % dots.length;
    if (n === card.__shot) return;
    card.__shot = n;
    dots.forEach(function (d, k) { d.classList.toggle("is-on", k === n); });

    if (n === 0) {                       // home: reveal the untouched base
      overlay.classList.remove("is-in");
      return;
    }

    var url = dots[n].getAttribute("data-cnc-shot");
    var pre = new Image();
    pre.onload = function () {
      if (card.__shot !== n) return;     // superseded while loading
      overlay.src = url;
      overlay.classList.add("is-in");
    };
    pre.src = url;
  }

  /* ── Variant discovery on a grid card ─────────────────────────────────────
     The swatch row cnc-product-card.liquid renders, made to do something.

     Two behaviours, and which one you get depends on whether a single tap can
     honestly resolve a variant:

       one option group   the swatch SELECTS, in place. The card's hidden id,
                          its price, its Best Price pill and its photo all move
                          to that variant, and the theme's own <product-form>
                          then adds exactly what the card is showing.
       two or more        the swatch OPENS the Pick & Pack sheet, preselected
                          on the value that was tapped. Colour alone does not
                          name a variant when there is also a size, and the
                          sheet is where the rest of that question gets asked.

     The overflow chip and the "Choose options" bar both go straight to the
     sheet — same control, reached three ways.                              */

  function paintCardPrice(card, price, mrp) {
    var row = card.querySelector(".cnc-price-row");
    if (row) {
      var off = mrp && mrp > price;
      var pct = discPct(price, mrp);
      row.innerHTML = '<span class="cnc-price">' + money(price / 100) + "</span>"
        + (off ? '<span class="cnc-mrp">' + money(mrp / 100) + "</span>" : "")
        + (pct > 0 ? '<span class="cnc-disc">' + pct + "% OFF</span>" : "");
    }

    /* The Best Price pill is a function of the price, so it moves too. Its
       percentage was baked in by Liquid and does not change with the variant. */
    var pill = card.querySelector("[data-cnc-best]");
    var out = pill && pill.querySelector("[data-cnc-bestval]");
    if (!out) return;
    var bestPct = parseInt(pill.getAttribute("data-cnc-best"), 10) || 0;
    /* Floored to whole rupees, exactly as the Liquid does it. The two have to
       agree to the paise or a swatch changes the pill's FORMAT as well as its
       value, and the card starts printing prices the app never would. */
    if (bestPct > 0) {
      out.textContent = money(Math.floor((price * (100 - bestPct)) / 10000));
    }
  }

  /* A chosen colour is not a slideshow frame — it stays put. Pinning takes the
     card out of the hover carousel entirely, because otherwise moving the
     pointer away and back rewinds to shot 1 and quietly discards the choice
     the customer just made.

     Only pinned when there is actually a photo to hold: a size with no image
     of its own changes nothing on screen, so the carousel keeps working. */
  function pinShot(card, url) {
    if (!url) return;
    card.dataset.cncPinned = "1";
    clearTimeout(card.__shotIntent);
    clearInterval(card.__shotTimer);
    card.querySelectorAll("[data-cnc-shot]").forEach(function (d) {
      d.classList.remove("is-on");
    });
    card.__shot = null;
    var overlay = shotLayers(card);
    if (!overlay) return;
    // Decode-gated, like showShot: never paint a frame that hasn't loaded.
    var pre = new Image();
    pre.onload = function () {
      overlay.src = url;
      overlay.classList.add("is-in");
    };
    pre.src = url;
  }

  function openCardPicker(box, value) {
    var open = window.CNC && window.CNC.openPicker;
    if (!open) return false;
    open({
      handle: box.getAttribute("data-cnc-handle"),
      id: box.getAttribute("data-cnc-pid"),
      url: box.getAttribute("data-cnc-purl") || "",
      mode: "add",
      preselect: value || ""
    });
    return true;
  }

  function initCardOptions() {
    if (!once(document.documentElement, "cardOpts")) return;

    document.addEventListener("click", function (e) {
      /* "Choose options" — the bag bar and the round button on multi-group
         cards. Both are anchors to the product page, so if the sheet is
         unavailable for any reason the click is simply left alone and the
         browser navigates. A dead button would have no such fallback. */
      var choose = e.target.closest("[data-cnc-choose]");
      if (choose) {
        var open = window.CNC && window.CNC.openPicker;
        if (!open) return;
        e.preventDefault();
        /* Carry whatever the card is already showing into the sheet, so a
           customer who tapped a colour and then the bar does not have to pick
           that colour a second time. */
        var card = choose.closest(".cnc-card");
        var on = card && card.querySelector("[data-cnc-sw].is-on");
        open({
          handle: choose.getAttribute("data-cnc-handle"),
          id: choose.getAttribute("data-cnc-pid"),
          url: choose.getAttribute("href") || "",
          mode: "add",
          preselect: on ? on.getAttribute("data-cnc-sw") : ""
        });
        return;
      }

      var more = e.target.closest("[data-cnc-sw-more]");
      if (more) {
        var moreBox = more.closest("[data-cnc-opts]");
        if (moreBox && openCardPicker(moreBox, "")) e.preventDefault();
        return;
      }

      var sw = e.target.closest("[data-cnc-sw]");
      if (!sw) return;
      var box = sw.closest("[data-cnc-opts]");
      if (!box) return;
      e.preventDefault();

      var variant = sw.getAttribute("data-cnc-sw-variant");
      var soldOut = sw.classList.contains("is-out");

      /* Anything a single tap cannot answer goes to the sheet — including a
         sold-out value, where the sheet can show what IS available in it
         rather than the card just refusing. */
      if (box.getAttribute("data-cnc-multi") === "1" || !variant || soldOut) {
        openCardPicker(box, sw.getAttribute("data-cnc-sw"));
        return;
      }

      box.querySelectorAll("[data-cnc-sw]").forEach(function (b) {
        var on = b === sw;
        b.classList.toggle("is-on", on);
        b.setAttribute("aria-pressed", String(on));
      });

      var host = sw.closest(".cnc-card");
      if (!host) return;
      /* Every hidden id on the card: the round + button and the hover bar are
         two separate forms, and leaving one behind means the two controls add
         two different things. */
      host.querySelectorAll('input[name="id"]').forEach(function (i) {
        i.value = variant;
      });
      paintCardPrice(host,
        parseInt(sw.getAttribute("data-cnc-sw-price"), 10) || 0,
        parseInt(sw.getAttribute("data-cnc-sw-mrp"), 10) || 0);
      pinShot(host, sw.getAttribute("data-cnc-sw-img"));
    });
  }

  function initCardHover() {
    if (!once(document.documentElement, "cardHover")) return;

    /* Preload on first hover, then cycle while the pointer stays. Auto-advance
       rather than dots-only, so a card with several shots shows them without
       being poked — the dots stay clickable for anyone who wants to steer. */
    /* Target must BE the card, not merely sit inside one. pointerenter /
       pointerleave fire on every element the pointer crosses, and the capture
       listener on document sees all of them — with a closest() match, sliding
       from the image onto the title fired "leave" on the inner <a>, matched
       the card, and snapped the shot back to 0 mid-hover. That was the
       flicker on every internal boundary crossing. */
    document.addEventListener(
      "pointerenter",
      function (e) {
        var card = e.target;
        if (!card || card.nodeType !== 1 || !card.matches(".cnc-card")) return;
        /* A card showing a variant the customer chose is not a slideshow. */
        if (card.dataset.cncPinned === "1") return;

        var dots = card.querySelectorAll("[data-cnc-shot]");
        if (dots.length < 2) return;

        /* Hover intent, matching the CSS: nothing warms or cycles until the
           pointer has RESTED here. A sweep across the grid used to preload
           every card it crossed — dozens of image requests for cards nobody
           was looking at. */
        clearTimeout(card.__shotIntent);
        card.__shotIntent = setTimeout(function () {
          if (!card.__shotsWarmed) {
            card.__shotsWarmed = true;
            dots.forEach(function (d, i) {
              if (i === 0) return;                   // already on screen
              var img = new Image();
              img.src = d.getAttribute("data-cnc-shot");
            });
          }

          clearInterval(card.__shotTimer);
          card.__shotTimer = setInterval(function () {
            showShot(card, (card.__shot || 0) + 1);
          }, SHOT_MS);
        }, 150);
      },
      true                                            // pointerenter doesn't bubble
    );

    /* Stop and rewind, so a card always starts from its main shot. */
    document.addEventListener(
      "pointerleave",
      function (e) {
        var card = e.target;
        if (!card || card.nodeType !== 1 || !card.matches(".cnc-card")) return;
        if (card.dataset.cncPinned === "1") return;
        clearTimeout(card.__shotIntent);
        clearInterval(card.__shotTimer);
        if (card.__shot) showShot(card, 0);
      },
      true
    );

    document.addEventListener("click", function (e) {
      var dot = e.target.closest("[data-cnc-shot]");
      if (!dot) return;

      e.preventDefault();
      e.stopPropagation();

      var card = dot.closest(".cnc-card");
      if (!card) return;

      /* The pin is deliberately left alone here. It only suppresses the
         AUTOMATIC cycle and the rewind-on-leave; showShot below still paints
         whichever dot was clicked, so a pinned card stays fully steerable.
         Clearing the selection instead would have left the swatches showing
         nothing chosen while the hidden id still pointed at the chosen
         variant — the card saying one thing and adding another. */

      /* Steering wins over the timer — restart it so the shot you picked gets
         a full turn rather than being replaced a moment later. */
      var dots = Array.prototype.slice.call(card.querySelectorAll("[data-cnc-shot]"));
      showShot(card, dots.indexOf(dot));

      clearInterval(card.__shotTimer);
      card.__shotTimer = setInterval(function () {
        showShot(card, (card.__shot || 0) + 1);
      }, SHOT_MS);
    });

    document.addEventListener("submit", function (e) {
      var form = e.target;
      if (!form || !form.closest(".cnc-card-bagform")) return;

      var btn = form.querySelector("[data-cnc-bag]");
      var label = btn && btn.querySelector("[data-cnc-bag-label]");
      if (!btn || !label) return;

      btn.classList.add("is-added");
      label.textContent = "Added to bag";

      clearTimeout(btn.__revert);
      btn.__revert = setTimeout(function () {
        btn.classList.remove("is-added");
        label.textContent = "Add to bag";
      }, 2500);
    });
  }

  function bootWidgets() {
    document.querySelectorAll("[data-hero]").forEach(initHero);
    document.querySelectorAll("[data-carousel]").forEach(initCarousel);
    document.querySelectorAll("[data-coupons-endpoint]").forEach(initLiveCoupons);
    document.querySelectorAll("[data-gallery]").forEach(initGallery);
    document.querySelectorAll("[data-qty]").forEach(initQty);
    // Variants stay ours (they rewrite the hidden id and the price); the
    // submit belongs to the theme's <product-form>.
    document.querySelectorAll("[data-product-form]").forEach(initVariants);
    document.querySelectorAll("[data-filters]").forEach(initFilters);
    document.querySelectorAll("[data-cnc-viewed]").forEach(recordViewed);
    recordViewedFromUrl();
    initWishlist();
    initCopy();
    initDrawerClose();
    initCouponControls();
    initPointsControls();
    initTotalsToggle();
    initRailAdd();
    initRailNav();
    initRailSync();
    initToastAction();
    initThemeWishlistSync();
    initBuyNow();
    initHostQuickView();
    initZeroBadges();
    initCardOptions();
    initCardHover();
    enhanceDrawer();
    observeDrawer();
    observeUpsell();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  // Re-bind after theme-editor section reloads
  document.addEventListener("shopify:section:load", boot);
})();
